import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { adminApi, api, getAdminUser, setAdminSession, clearAdminSession, rupiah, LOGOS } from "@/lib/apiClient";
import { SeatMap } from "@/components/SeatMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2, ShieldCheck, LogOut, CheckCircle2, XCircle, Printer,
  Eye, RefreshCw, Ticket, Clock, Wallet, Users, Search, UserCheck, Download, ScanLine, MessageCircle, UploadCloud,
  Trash2, AlertTriangle, UserPlus, History,
  Store, Banknote, RotateCcw, ShieldAlert, MapPin, ChevronDown, KeyRound, Crown, Pencil, ClipboardList, Lock, Camera, Database, Armchair, HandHeart,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const max = 1400;
        let { width, height } = img;
        if (width > max || height > max) {
          const r = Math.min(max / width, max / height);
          width = Math.round(width * r); height = Math.round(height * r);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("id-ID", {
      timeZone: "Asia/Jakarta", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    }) + " WIB";
  } catch { return ""; }
};
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  try {
    const parts = new Intl.DateTimeFormat("sv-SE", {
      timeZone: "Asia/Jakarta", year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(new Date(iso));
    const g = (t) => parts.find((p) => p.type === t)?.value;
    return `${g("year")}-${g("month")}-${g("day")}T${g("hour")}:${g("minute")}`;
  } catch { return ""; }
};
const fmtWIB = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", weekday: "long", day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) + " WIB";
  } catch { return ""; }
};

const waPhone = (phone) => {
  let p = (phone || "").replace(/[^0-9]/g, "");
  if (p.startsWith("0")) p = "62" + p.slice(1);
  else if (p.startsWith("8")) p = "62" + p;
  return p;
};

const sendWA = (o) => {
  const link = `${window.location.origin}/order/${o.id}`;
  const msg =
`Namo Buddhaya, ${o.name}
Terima kasih, pembayaran Anda sudah kami *VERIFIKASI*.

No. Order: #${o.order_no}
Nama: ${o.name}
No HP: ${o.phone}

Berikut e-tiket Anda:
Film: "Y.A. MNS. Ashin Jinarakkhita: Jejak Langkah Sang Pelopor di Nusantara"
Hari/Tanggal: Minggu, 13 September 2026
Tempat: CGV Grand Batam
${o.session?.name || "Sesi"} (${o.session?.time || "-"})
Kursi: ${o.seats.join(", ")}

Lihat & simpan e-ticket:
${link}

Mohon tunjukkan e-ticket ini saat check-in di lokasi. Sampai jumpa!
— Sekretariat MBI Kepri`;
  window.open(`https://wa.me/${waPhone(o.phone)}?text=${encodeURIComponent(msg)}`, "_blank");
};

const sendReminderWA = (o) => {
  const link = `${window.location.origin}/order/${o.id}`;
  const msg =
`Namo Buddhaya, ${o.name}
Terima kasih sudah memesan tiket Film Dokumenter "Y.A. MNS. Ashin Jinarakkhita: Jejak Langkah Sang Pelopor di Nusantara".

Namun kami *BELUM menerima bukti pembayaran* Anda untuk:
No. Order: #${o.order_no}
${o.session?.name || "Sesi"} - Kursi ${o.seats.join(", ")}
Total: ${rupiah(o.total_amount)} (mohon bayar PAS termasuk kode unik)

Mohon segera lakukan pembayaran & *upload bukti transfer* melalui link berikut:
${link}

Atau Anda cukup *kirim foto bukti transfer ke chat WhatsApp ini*, nanti kami bantu upload-kan.

Jika sudah membayar, mohon abaikan pesan ini. Terima kasih.
— Sekretariat MBI Kepri`;
  window.open(`https://wa.me/${waPhone(o.phone)}?text=${encodeURIComponent(msg)}`, "_blank");
};

const sendManualWA = (o) => {
  const s = SESSIONS_STATIC.find((x) => x.id === o.session_id);
  const link = `${window.location.origin}/order/${o.id}`;
  const paidLine = o.paid ? `Dana: ${rupiah(o.total_amount)}` : "";
  const msg =
`Namo Buddhaya, ${o.name}
Terima kasih.

E-TICKET Film Dokumenter "Ashin Jinarakkhita"
No. Order: #${o.order_no}
Hari/Tanggal: Minggu, 13 September 2026
Tempat: CGV Grand Batam
${s?.name || "Sesi"} (${s?.time || "-"})
Kursi: ${(o.seats || []).join(", ")}
Jumlah: ${o.qty} tiket
${paidLine}

Lihat & simpan e-ticket di sini:
${link}

Mohon tunjukkan e-ticket ini kepada petugas di lokasi untuk menukar tiket fisik (hardcopy) asli. Sampai jumpa!
— Sekretariat MBI Kepri`;
  window.open(`https://wa.me/${waPhone(o.phone)}?text=${encodeURIComponent(msg)}`, "_blank");
};


const orderProgress = (o) => {
  if (o.status === "pending_payment") return { t: "Belum Bayar", c: "bg-[#B26A1E]/15 text-[#8A3A12]" };
  if (o.status === "waiting_verification") return { t: "⚠ Belum cek payment", c: "bg-[#B26A1E]/20 text-[#8A3A12]" };
  if (o.status === "verified") return o.wa_sent
    ? { t: "✅ Complete", c: "bg-[#2F703E]/15 text-[#255E33]" }
    : { t: "Belum kirim WhatsApp", c: "bg-[#7A241F]/15 text-[#7A241F]" };
  if (o.status === "rejected") return { t: "Ditolak", c: "bg-[#EF4444]/15 text-[#EF4444]" };
  if (o.status === "expired") return { t: "Kadaluarsa", c: "bg-[#7A6A5E]/15 text-[#7A6A5E]" };
  return { t: o.status, c: "bg-muted" };
};

const STATUS_META = {
  pending_payment: { t: "Belum Bayar", c: "bg-[#B26A1E]/15 text-[#8A3A12]" },
  waiting_verification: { t: "Perlu Verifikasi", c: "bg-[#7A241F]/15 text-[#7A241F]" },
  verified: { t: "Terverifikasi", c: "bg-[#2F703E]/15 text-[#255E33]" },
  rejected: { t: "Ditolak", c: "bg-[#EF4444]/15 text-[#EF4444]" },
  expired: { t: "Kadaluarsa", c: "bg-[#7A6A5E]/15 text-[#7A6A5E]" },
};

const FILTERS = [
  { k: "all", t: "Semua" },
  { k: "waiting_verification", t: "Perlu Verifikasi" },
  { k: "verified", t: "Terverifikasi" },
  { k: "pending_payment", t: "Belum Bayar" },
  { k: "rejected", t: "Ditolak" },
];

const channelInfo = (o) => o.vip
  ? { t: "VIP · Undangan", c: "bg-[#7A241F]/15 text-[#7A241F]" }
  : o.manual
  ? { t: "Manual", c: "bg-[#B26A1E]/10 text-[#8A3A12]" }
  : o.walkin
  ? { t: "Panitia · Lokasi", c: "bg-[#B26A1E]/15 text-[#8A3A12]" }
  : { t: "Website", c: "bg-[#7A241F]/10 text-[#7A241F]" };

function LoginView({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { username, password });
      setAdminSession(data.user);
      onLogin();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login gagal");
    }
    setLoading(false);
  };
  return (
    <div className="max-w-md mx-auto px-4 py-24">
      <motion.form initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        onSubmit={submit} className="rounded-2xl border border-border bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="h-12 w-12 rounded-full bg-[#7A241F]/10 flex items-center justify-center mb-4">
          <ShieldCheck className="h-6 w-6 text-[#7A241F]" />
        </div>
        <h1 className="font-serif-display text-3xl text-[#7A241F]">Panel Admin</h1>
        <p className="text-sm text-[#7A6A5E] mb-6">Masuk dengan akun Anda.</p>
        <Label htmlFor="uname">Username</Label>
        <Input id="uname" value={username} onChange={(e) => setUsername(e.target.value)}
          className="mt-1.5 mb-4" data-testid="admin-username" placeholder="username" autoCapitalize="none" />
        <Label htmlFor="pw">Password</Label>
        <Input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          className="mt-1.5" data-testid="admin-password" placeholder="••••••••" />
        <Button type="submit" disabled={loading} data-testid="admin-login-btn"
          className="w-full mt-5 bg-[#7A241F] hover:bg-[#5E1B17] rounded-full">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Masuk
        </Button>
      </motion.form>
    </div>
  );
}

const ROLE_BADGE = {
  superadmin: { t: "Super Admin", c: "bg-[#B26A1E]/15 text-[#8A3A12]" },
  admin: { t: "Admin", c: "bg-[#7A241F]/15 text-[#7A241F]" },
  checkin: { t: "Petugas Check-in", c: "bg-[#2F703E]/15 text-[#255E33]" },
  seller: { t: "Penjual Pre-Order", c: "bg-[#B26A1E]/15 text-[#8A3A12]" },
  loket: { t: "Loket (Jual + Check-in)", c: "bg-[#B26A1E]/15 text-[#8A3A12]" },
  checkin_web: { t: "Check-in Website", c: "bg-[#1E6F8B]/15 text-[#155A73]" },
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="rounded-xl border border-border bg-white p-4 flex items-center gap-3">
    <span className={cn("h-10 w-10 rounded-lg flex items-center justify-center", color)}><Icon className="h-5 w-5" /></span>
    <div>
      <p className="text-xs text-[#7A6A5E]">{label}</p>
      <p className="font-semibold text-lg text-[#7A241F]">{value}</p>
    </div>
  </div>
);

function SessionFunds({ stats }) {
  const ps = stats?.per_session;
  if (!ps) return null;
  return (
    <div className="mb-6 no-print" data-testid="session-funds">
      <h2 className="font-serif-display text-xl text-[#7A241F] flex items-center gap-2 mb-3">
        <Banknote className="h-5 w-5 text-[#B26A1E]" /> Dana Terkumpul per Sesi
      </h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {ps.map((s) => (
          <div key={s.id} className="rounded-2xl border border-border bg-white p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
            data-testid={`session-fund-${s.id}`}>
            <p className="text-xs font-semibold text-[#7A6A5E]">{s.name} · {s.time}</p>
            <p className="font-serif-display text-lg sm:text-2xl text-[#255E33] mt-1">{rupiah(s.revenue)}</p>
            <p className="text-xs text-[#7A6A5E] mt-0.5">{s.tickets} tiket · {s.orders} pembeli</p>
          </div>
        ))}
      </div>
    </div>
  );
}


function DailySalesChart({ stats }) {
  const daily = stats?.daily || [];
  if (daily.length === 0) return null;
  const fmtDate = (d) => {
    const [y, m, day] = d.split("-");
    const bulan = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
    return `${parseInt(day, 10)} ${bulan[parseInt(m, 10) - 1]}`;
  };
  const data = daily.map((d) => ({
    ...d,
    label: fmtDate(d.date),
    pending: Math.max(0, (d.tickets || 0) - (d.tickets_verified || 0)),
  }));
  const totalTickets = data.reduce((a, d) => a + (d.tickets || 0), 0);
  return (
    <div className="rounded-2xl border border-border bg-white p-4 sm:p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] mb-6 no-print" data-testid="daily-sales-chart">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h2 className="font-serif-display text-xl text-[#7A241F] flex items-center gap-2">
          <History className="h-5 w-5 text-[#B26A1E]" /> Pembelian Tiket per Hari
        </h2>
        <span className="text-xs text-[#7A6A5E]">{data.length} hari · total <b className="text-[#7A241F]">{totalTickets}</b> tiket</span>
      </div>
      <div className="h-56 sm:h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EDE5D8" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#7A6A5E" }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#7A6A5E" }} axisLine={false} tickLine={false} />
            <Tooltip
              cursor={{ fill: "rgba(178,106,30,0.06)" }}
              contentStyle={{ borderRadius: 12, border: "1px solid #EDE5D8", fontSize: 12 }}
              formatter={(v, nm) => [nm === "Pembeli" ? `${v} pesanan` : `${v} tiket`, nm]}
              labelFormatter={(l, p) => {
                const row = p?.[0]?.payload;
                return row ? `${l} · ${row.orders} pembeli · ${rupiah(row.revenue_verified)}` : l;
              }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="tickets_verified" name="Terverifikasi" stackId="a" fill="#2F703E" radius={[0, 0, 0, 0]} />
            <Bar dataKey="pending" name="Belum verifikasi" stackId="a" fill="#E4C57E" radius={[6, 6, 0, 0]} />
            <Line dataKey="orders" name="Pembeli" stroke="#7A241F" strokeWidth={2}
              dot={{ r: 3, fill: "#7A241F" }} type="monotone" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm min-w-[440px]" data-testid="daily-sales-table">
          <thead>
            <tr className="text-left text-xs text-[#7A6A5E] border-b border-border">
              <th className="py-2 pr-3 font-medium">Tanggal</th>
              <th className="py-2 pr-3 font-medium text-right">Pembeli</th>
              <th className="py-2 pr-3 font-medium text-right">Tiket</th>
              <th className="py-2 pr-3 font-medium text-right">Terverifikasi</th>
              <th className="py-2 font-medium text-right">Pendapatan</th>
            </tr>
          </thead>
          <tbody>
            {[...data].reverse().map((d) => (
              <tr key={d.date} className="border-b border-dashed border-border/70 last:border-0">
                <td className="py-1.5 pr-3 font-medium text-[#2C1E16]">{d.label}</td>
                <td className="py-1.5 pr-3 text-right">{d.orders}</td>
                <td className="py-1.5 pr-3 text-right font-semibold text-[#7A241F]">{d.tickets}</td>
                <td className="py-1.5 pr-3 text-right text-[#255E33]">{d.tickets_verified}</td>
                <td className="py-1.5 text-right text-[#255E33]">{rupiah(d.revenue_verified)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function SalesSummary({ stats }) {
  const b = stats?.breakdown;
  if (!b) return null;
  const w = b.walkin, on = b.online;
  const cashSessions = (stats?.cash_per_session || []).filter((s) => s.revenue > 0);
  const Row = ({ label, data, accent }) => (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className={cn("text-[#7A6A5E]", accent && "font-medium text-[#2C1E16]")}>{label}</span>
      <span className="text-right">
        <b className="text-[#7A241F]">{data.tickets}</b> <span className="text-xs text-[#7A6A5E]">tiket</span>
        <span className="mx-1 text-[#D1D5DB]">·</span>
        <b className="text-[#255E33]">{rupiah(data.revenue)}</b>
      </span>
    </div>
  );
  return (
    <div className="rounded-2xl border border-border bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] mb-6 no-print" data-testid="sales-summary">
      <h2 className="font-serif-display text-xl text-[#7A241F] flex items-center gap-2 mb-4"><Wallet className="h-5 w-5 text-[#B26A1E]" /> Ringkasan Penjualan (terverifikasi)</h2>

      {stats?.funds && (
        <div className="rounded-2xl border border-[#B26A1E]/30 bg-gradient-to-br from-[#7A241F] to-[#5E1B17] text-white p-5 sm:p-6 mb-5 grain" data-testid="total-dana-card">
          <p className="text-sm text-white/80 flex items-center gap-1.5"><HandHeart className="h-4 w-4" /> Total Dana Terkumpul (terverifikasi)</p>
          <p className="font-serif-display text-4xl sm:text-5xl font-extrabold mt-1" data-testid="total-dana-value">{rupiah(stats.funds.total.revenue)}</p>
          <p className="text-xs text-white/70 mt-0.5">{stats.funds.total.tickets} tiket · {stats.funds.total.orders} pesanan</p>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="rounded-xl bg-white/10 border border-white/15 p-3" data-testid="dana-website">
              <p className="text-xs text-white/75">Order Website (langsung)</p>
              <p className="font-serif-display text-xl sm:text-2xl font-bold mt-0.5">{rupiah(stats.funds.website.revenue)}</p>
              <p className="text-[11px] text-white/60">{stats.funds.website.tickets} tiket · {stats.funds.website.orders} pesanan</p>
            </div>
            <div className="rounded-xl bg-white/10 border border-white/15 p-3" data-testid="dana-manual">
              <p className="text-xs text-white/75">Order Manual</p>
              <p className="font-serif-display text-xl sm:text-2xl font-bold mt-0.5">{rupiah(stats.funds.manual.revenue)}</p>
              <p className="text-[11px] text-white/60">{stats.funds.manual.tickets} tiket · {stats.funds.manual.orders} pesanan</p>
            </div>
            {stats.funds.walkin.revenue > 0 && (
              <div className="rounded-xl bg-white/10 border border-white/15 p-3" data-testid="dana-walkin">
                <p className="text-xs text-white/75">Jual di Tempat (walk-in)</p>
                <p className="font-serif-display text-xl sm:text-2xl font-bold mt-0.5">{rupiah(stats.funds.walkin.revenue)}</p>
                <p className="text-[11px] text-white/60">{stats.funds.walkin.tickets} tiket · {stats.funds.walkin.orders} pesanan</p>
              </div>
            )}
            {stats.funds.vip.revenue > 0 && (
              <div className="rounded-xl bg-white/10 border border-white/15 p-3" data-testid="dana-vip">
                <p className="text-xs text-white/75">VIP</p>
                <p className="font-serif-display text-xl sm:text-2xl font-bold mt-0.5">{rupiah(stats.funds.vip.revenue)}</p>
                <p className="text-[11px] text-white/60">{stats.funds.vip.tickets} tiket · {stats.funds.vip.orders} pesanan</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-5">
        <div className="rounded-xl border border-border p-4">
          <p className="text-sm font-semibold text-[#7A241F] mb-1">Online (web)</p>
          <Row label="Total" data={on} accent />
        </div>
        <div className="rounded-xl border border-border p-4">
          <p className="text-sm font-semibold text-[#7A241F] mb-1">Jual di Tempat (walk-in)</p>
          <Row label="Total" data={w} accent />
          <div className="mt-1 border-t border-dashed border-border pt-1">
            <Row label="• Cash" data={w.cash} />
            <Row label="• QRIS" data={w.qris} />
            <Row label="• Transfer" data={w.transfer} />
          </div>
        </div>
      </div>
      <div className="mt-4 rounded-xl bg-[#2F703E]/[0.07] border border-[#2F703E]/20 p-4" data-testid="cash-recap">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-[#255E33] flex items-center gap-1.5"><Banknote className="h-4 w-4" /> Rekap Kas Cash (jualan panitia di lokasi)</p>
            <p className="text-xs text-[#7A6A5E]">Total uang tunai yang harus ada di tangan bendahara</p>
          </div>
          <span className="font-serif-display text-3xl text-[#255E33]" data-testid="cash-total">{rupiah(stats.cash_total || 0)}</span>
        </div>
        {cashSessions.length > 0 && (
          <div className="mt-3 border-t border-dashed border-[#2F703E]/25 pt-2 space-y-1" data-testid="cash-per-session">
            <p className="text-[11px] font-semibold text-[#255E33] mb-1">Rincian kas cash per sesi:</p>
            {cashSessions.map((s) => (
              <div key={s.id} className="flex items-center justify-between text-sm" data-testid={`cash-session-${s.id}`}>
                <span className="text-[#5B4636]">{s.name} · {s.time} <span className="text-xs text-[#7A6A5E]">({s.tickets} tiket · {s.orders} pembeli)</span></span>
                <b className="text-[#255E33]">{rupiah(s.revenue)}</b>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function CheckinPanel({ orders, query, setQuery, onCheckin, busyId }) {
  const q = query.trim().toLowerCase();
  const nq = q.replace(/\s/g, "");
  const results = q.length === 0 ? [] : orders.filter(
    (o) => o.status === "verified" &&
      (o.name.toLowerCase().includes(q) || o.phone.replace(/\s/g, "").includes(nq))
  );
  return (
    <div className="rounded-2xl border border-border bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] no-print">
      <h2 className="font-serif-display text-2xl text-[#7A241F] mb-1">Check-in Peserta</h2>
      <p className="text-sm text-[#7A6A5E] mb-4">Cari peserta dengan nama atau nomor HP, tandai kehadiran, lalu serahkan tiket kursinya.</p>
      <a href="/checkin" target="_blank" rel="noreferrer" data-testid="open-mobile-checkin"
        className="inline-flex items-center gap-1.5 text-sm text-[#B26A1E] hover:underline font-medium mb-4">
        <ScanLine className="h-4 w-4" /> Buka halaman Check-in khusus HP (mobile) → /checkin
      </a>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A6A5E]" />
        <Input data-testid="checkin-search" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Ketik nama atau nomor HP peserta..." className="pl-9" />
      </div>
      <div className="mt-5 space-y-3">
        {q.length > 0 && results.length === 0 && (
          <p className="text-sm text-[#7A6A5E]" data-testid="checkin-empty">Tidak ada peserta terverifikasi yang cocok. (Hanya pesanan Terverifikasi yang tampil)</p>
        )}
        {results.map((o) => (
          <div key={o.id} data-testid={`checkin-result-${o.id.slice(0, 8)}`}
            className="rounded-xl border border-border p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-[#2C1E16]">{o.name}</p>
              <p className="text-xs text-[#7A6A5E]">{o.phone} · {o.session?.name} · {o.session?.time}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {o.seats.map((s) => (
                  <span key={s} className="px-2.5 py-1 rounded-md bg-[#B26A1E]/10 text-[#8A3A12] text-sm font-semibold">{s}</span>
                ))}
              </div>
            </div>
            <div className="text-right">
              {o.checked_in ? (
                <div className="text-right">
                  <span className="inline-flex items-center gap-1.5 text-sm text-[#2F703E] font-medium"><CheckCircle2 className="h-4 w-4" /> Sudah Hadir</span>
                  {o.checked_in_at && <p className="text-[11px] text-[#7A6A5E] mt-0.5">Check-in: {fmtTime(o.checked_in_at)}</p>}
                  {o.checked_in_by && <p className="text-[11px] text-[#7A6A5E]">oleh <b className="text-[#255E33]">{o.checked_in_by}</b></p>}
                </div>
              ) : (
                <Button onClick={() => onCheckin(o)} disabled={busyId === o.id} data-testid={`checkin-btn-${o.id.slice(0, 8)}`}
                  className="bg-[#7A241F] hover:bg-[#5E1B17]">
                  {busyId === o.id ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <UserCheck className="h-4 w-4 mr-1.5" />}
                  Tandai Sudah Datang
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const ACTION_META = {
  delete: { label: "Hapus", c: "bg-[#EF4444]/15 text-[#EF4444]" },
  verify: { label: "Verifikasi", c: "bg-[#2F703E]/15 text-[#255E33]" },
  reject: { label: "Tolak", c: "bg-[#B26A1E]/15 text-[#8A3A12]" },
  bulk_verify: { label: "Verifikasi Massal", c: "bg-[#2F703E]/15 text-[#255E33]" },
  bulk_reject: { label: "Tolak Massal", c: "bg-[#B26A1E]/15 text-[#8A3A12]" },
  checkin: { label: "Check-in", c: "bg-[#7A241F]/15 text-[#7A241F]" },
  user_create: { label: "Buat User", c: "bg-[#7A241F]/15 text-[#7A241F]" },
  user_delete: { label: "Hapus User", c: "bg-[#EF4444]/15 text-[#EF4444]" },
  walkin: { label: "Jual di Tempat", c: "bg-[#B26A1E]/15 text-[#8A3A12]" },
  coming_soon: { label: "Coming Soon", c: "bg-[#7A6A5E]/15 text-[#7A6A5E]" },
  session_toggle: { label: "Buka/Tutup Sesi", c: "bg-[#2F703E]/15 text-[#255E33]" },
  restore: { label: "Pulihkan Order", c: "bg-[#2F703E]/15 text-[#255E33]" },
  user_permission: { label: "Izin Hapus", c: "bg-[#B26A1E]/15 text-[#8A3A12]" },
  seat_change: { label: "Ubah Kursi", c: "bg-[#B26A1E]/20 text-[#8A3A12] ring-1 ring-[#B26A1E]/40" },
};

function LogsPanel() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await adminApi.get("/admin/logs"); setLogs(data); }
    catch { toast.error("Gagal memuat log"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  const exportLogs = async () => {
    setExporting(true);
    try {
      const res = await adminApi.get("/admin/logs/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "log_aktivitas.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Log berhasil diunduh");
    } catch { toast.error("Gagal mengunduh log"); }
    setExporting(false);
  };
  return (
    <div className="rounded-2xl border border-border bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] no-print">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="font-serif-display text-2xl text-[#7A241F] flex items-center gap-2"><History className="h-5 w-5 text-[#B26A1E]" /> Log Aktivitas</h2>
          <p className="text-sm text-[#7A6A5E]">Catatan siapa melakukan aksi apa & kapan.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportLogs} disabled={exporting || logs.length === 0} data-testid="logs-export">
            {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Export Excel
          </Button>
          <Button variant="outline" onClick={load} data-testid="logs-refresh"><RefreshCw className="h-4 w-4 mr-1.5" /> Muat Ulang</Button>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#B26A1E]" /></div>
      ) : logs.length === 0 ? (
        <p className="text-center text-sm text-[#7A6A5E] py-12" data-testid="logs-empty">Belum ada aktivitas tercatat.</p>
      ) : (
        <div className="space-y-2" data-testid="logs-list">
          {logs.map((l) => {
            const m = ACTION_META[l.action] || { label: l.action, c: "bg-muted text-[#7A6A5E]" };
            return (
              <div key={l.id} className="flex items-start gap-3 rounded-xl border border-border p-3">
                <span className={cn("shrink-0 text-[11px] px-2 py-0.5 rounded-full font-medium", m.c)}>{m.label}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-[#2C1E16]">{l.detail}</p>
                  <p className="text-xs text-[#7A6A5E] mt-0.5">
                    oleh <b>{l.actor_name || l.actor_username}</b> (@{l.actor_username}) · {fmtTime(l.created_at)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function WaitlistPanel() {
  const [data, setData] = useState({ entries: [], count: 0, total_tickets: 0, target: 188, session_at: "" });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sessionAt, setSessionAt] = useState("");
  const [targetInput, setTargetInput] = useState(188);
  const [sess5Name, setSess5Name] = useState("Sesi Cadangan");
  const [savingCfg, setSavingCfg] = useState(false);
  // manual add
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", phone: "", qty: 1 });
  const [addBusy, setAddBusy] = useState(false);
  // edit
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", phone: "", qty: 1 });
  const [editBusy, setEditBusy] = useState(false);
  // seat picker (Sesi Cadangan)
  const [seatRow, setSeatRow] = useState(null);
  const [seatRows, setSeatRows] = useState([]);
  const [seatSelected, setSeatSelected] = useState([]);
  const [seatCouples, setSeatCouples] = useState({});
  const [seatLoading, setSeatLoading] = useState(false);
  const [seatBusy, setSeatBusy] = useState(false);
  // berdana / upload bukti (untuk tiket yg sudah dibuat)
  const [donateRow, setDonateRow] = useState(null);
  const [donateForm, setDonateForm] = useState({ amount: "", method: "transfer", proof: null });
  const [donateBusy, setDonateBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/waitlist");
      setData(data);
      setSessionAt(isoToLocalInput(data.session_at));
      setTargetInput(data.target || 188);
      setSess5Name(data.session5_name || "Sesi Cadangan");
    } catch { toast.error("Gagal memuat daftar tunggu"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      const iso = sessionAt ? new Date(`${sessionAt.length === 16 ? sessionAt + ":00" : sessionAt}+07:00`).toISOString() : "";
      await adminApi.post("/admin/waitlist-config", { session_at: iso, target: Number(targetInput) || 188, session5_name: sess5Name.trim() || "Sesi Cadangan" });
      toast.success("Jadwal sesi cadangan disimpan");
      await load();
    } catch { toast.error("Gagal menyimpan pengaturan"); }
    setSavingCfg(false);
  };

  const [togglingClosed, setTogglingClosed] = useState(false);
  const toggleClosed = async () => {
    const next = !data.closed;
    if (!window.confirm(next
      ? "Tutup pendaftaran Daftar Tunggu? Form di halaman publik akan disembunyikan dan pengunjung tidak bisa mendaftar lagi."
      : "Buka kembali pendaftaran Daftar Tunggu? Form akan tampil lagi di halaman publik.")) return;
    setTogglingClosed(true);
    try {
      await adminApi.post("/admin/waitlist-config", { closed: next });
      toast.success(next ? "Daftar tunggu ditutup" : "Daftar tunggu dibuka");
      await load();
    } catch { toast.error("Gagal mengubah status daftar tunggu"); }
    setTogglingClosed(false);
  };

  const addManual = async () => {
    if (!addForm.name.trim() || !addForm.phone.trim()) { toast.error("Isi Nama & No HP"); return; }
    setAddBusy(true);
    try {
      await adminApi.post("/admin/waitlist-add", { name: addForm.name.trim(), phone: addForm.phone.trim(), qty: Number(addForm.qty) || 1 });
      toast.success("Pendaftar ditambahkan");
      setAddOpen(false); setAddForm({ name: "", phone: "", qty: 1 });
      await load();
    } catch { toast.error("Gagal menambahkan"); }
    setAddBusy(false);
  };

  const openEdit = (e) => { setEditRow(e); setEditForm({ name: e.name, phone: e.phone, qty: e.qty }); };
  const saveEdit = async () => {
    if (!editForm.name.trim()) { toast.error("Nama wajib diisi"); return; }
    setEditBusy(true);
    try {
      await adminApi.put(`/admin/waitlist/${editRow.id}`, { name: editForm.name.trim(), phone: editForm.phone.trim(), qty: Number(editForm.qty) || 1 });
      toast.success("Data pendaftar diperbarui");
      setEditRow(null); await load();
    } catch { toast.error("Gagal menyimpan"); }
    setEditBusy(false);
  };

  const openSeatPicker = async (e) => {
    setSeatRow(e); setSeatSelected([]); setSeatLoading(true);
    try {
      const { data } = await api.get(`/sessions/5/seats`);
      setSeatRows(data.rows || []);
      setSeatCouples(data.couples || {});
    } catch { toast.error("Gagal memuat denah kursi"); }
    setSeatLoading(false);
  };
  const toggleSeat = (label, partner) => {
    const set = new Set(seatSelected);
    const removing = set.has(label);
    const limit = seatRow?.qty || 1;
    if (!removing) {
      const addCount = 1 + (partner && !set.has(partner) ? 1 : 0);
      if (seatSelected.length + addCount > limit) {
        toast.error(`Maksimal ${limit} kursi sesuai rencana tiket. Ubah 'rencana tiket' dulu bila ingin lebih.`);
        return;
      }
    }
    const flip = (l) => { if (set.has(l)) set.delete(l); else set.add(l); };
    flip(label); if (partner) flip(partner);
    setSeatSelected(Array.from(set));
  };
  const createTicket = async () => {
    if (seatSelected.length === 0) { toast.error("Pilih minimal 1 kursi"); return; }
    setSeatBusy(true);
    try {
      const { data: order } = await adminApi.post(`/admin/waitlist/${seatRow.id}/create-ticket`, { seats: seatSelected });
      toast.success(`Tiket Sesi Cadangan dibuat: #${order.order_no} (${order.seats.join(", ")})`);
      setSeatRow(null); await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal membuat tiket"); }
    setSeatBusy(false);
  };

  const openDonate = (e) => { setDonateRow(e); setDonateForm({ amount: "", method: "transfer", proof: null }); };
  const saveDonate = async () => {
    const amt = Number(donateForm.amount) || 0;
    if (amt <= 0) { toast.error("Isi nominal dana"); return; }
    if (donateForm.method !== "cash" && !donateForm.proof) { toast.error("Upload bukti transfer/QRIS dulu"); return; }
    setDonateBusy(true);
    try {
      await adminApi.put(`/admin/manual/${donateRow.order_id}`, {
        paid: true, amount: amt, transfer_amount: amt,
        payment_method: donateForm.method,
        transfer_date: new Date().toISOString(),
        proof_image: donateForm.proof || null,
      });
      toast.success(`Dana paramita #${donateRow.order_no} tercatat`);
      setDonateRow(null); await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal menyimpan dana"); }
    setDonateBusy(false);
  };

  const sendWhatsApp = async (e) => {
    const jadwal = data.session_at ? fmtWIB(data.session_at) : "(akan diinformasikan)";
    let msg;
    if (e.order_id) {
      const link = `${window.location.origin}/order/${e.order_id}`;
      msg =
`Namo Buddhaya, ${e.name} 🙏
Kabar gembira! Sesi TAMBAHAN (Cadangan) Film Dokumenter *ASHIN JINARAKKHITA* telah dibuka untuk Anda.

Jadwal: ${jadwal}
Lokasi: CGV Grand Batam Mall
Kursi Anda: ${e.seats}
No. Tiket: #${e.order_no}

E-TICKET Anda (simpan & tunjukkan saat check-in):
${link}

Bila berkenan menyalurkan *Dana Paramita*, silakan buka tautan e-ticket di atas, pilih QRIS/Transfer, isi nominal & unggah bukti. Sifatnya sukarela 🙏
- Sekretariat MBI Kepri`;
    } else {
      msg =
`Namo Buddhaya, ${e.name} 🙏
Terima kasih telah masuk daftar tunggu Film Dokumenter *ASHIN JINARAKKHITA*.

${data.session_at ? `Rencana sesi tambahan: ${jadwal}\n` : ""}Kami akan segera menginformasikan kursi & e-ticket Anda. Mohon nomor ini tetap aktif.
- Sekretariat MBI Kepri`;
    }
    window.open(`https://wa.me/${waPhone(e.phone)}?text=${encodeURIComponent(msg)}`, "_blank");
    try { await adminApi.put(`/admin/waitlist/${e.id}`, { notified: true }); await load(); } catch {}
  };

  const toggleNotified = async (e) => {
    try { await adminApi.put(`/admin/waitlist/${e.id}`, { notified: !e.notified }); await load(); }
    catch { toast.error("Gagal memperbarui status"); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Hapus ${e.name} dari daftar tunggu?`)) return;
    try { await adminApi.delete(`/admin/waitlist/${e.id}`); toast.success("Dihapus"); await load(); }
    catch { toast.error("Gagal menghapus"); }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await adminApi.get("/admin/waitlist/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "daftar_tunggu.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Daftar tunggu diunduh");
    } catch { toast.error("Gagal mengunduh"); }
    setExporting(false);
  };

  const pct = data.target ? Math.min(100, Math.round((data.total_tickets / data.target) * 100)) : 0;
  const reached = data.total_tickets >= data.target;

  return (
    <div className="rounded-2xl border border-border bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] no-print" data-testid="waitlist-panel">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="font-serif-display text-2xl text-[#7A241F] flex items-center gap-2"><HandHeart className="h-5 w-5 text-[#B26A1E]" /> Daftar Tunggu</h2>
          <p className="text-sm text-[#7A6A5E]">Calon penonton bila sesi cadangan dibuka.</p>
        </div>
        <div className="flex gap-2 flex-wrap w-full sm:w-auto">
          <Button onClick={() => setAddOpen(true)} data-testid="waitlist-add-manual" className="flex-1 sm:flex-none bg-[#B26A1E] hover:bg-[#8A3A12] text-white">
            <UserPlus className="h-4 w-4 mr-1.5" /> Tambah Manual
          </Button>
          <Button variant="outline" onClick={exportExcel} disabled={exporting || data.count === 0} data-testid="waitlist-export" className="flex-1 sm:flex-none">
            {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Export Excel
          </Button>
          <Button variant="outline" onClick={load} data-testid="waitlist-refresh" className="flex-1 sm:flex-none"><RefreshCw className="h-4 w-4 mr-1.5" /> Muat Ulang</Button>
        </div>
      </div>

      <div className={cn("rounded-2xl border p-4 mb-4", reached ? "border-[#2F703E] bg-[#2F703E]/[0.06]" : "border-[#B26A1E]/30 bg-[#F3E9DD]/40")} data-testid="waitlist-progress">
        <div className="flex items-center justify-between text-sm mb-1.5">
          <span className="font-semibold text-[#2C1E16]">{data.total_tickets} / {data.target} tiket direncanakan</span>
          <span className="text-[#7A6A5E]">{data.count} pendaftar</span>
        </div>
        <div className="h-3 w-full rounded-full bg-[#7A6A5E]/15 overflow-hidden">
          <div className={cn("h-full rounded-full transition-all", reached ? "bg-[#2F703E]" : "bg-[#B26A1E]")} style={{ width: `${pct}%` }} />
        </div>
        {reached && <p className="text-xs font-semibold text-[#255E33] mt-2">✓ Kuota tercapai! Saatnya membuka sesi cadangan.</p>}
      </div>

      <div className={cn("rounded-2xl border p-4 mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3",
        data.closed ? "border-[#7A241F]/40 bg-[#7A241F]/[0.06]" : "border-[#2F703E]/30 bg-[#2F703E]/[0.05]")}
        data-testid="waitlist-toggle-card">
        <div className="min-w-0">
          <p className="font-semibold text-[#2C1E16] flex items-center gap-2">
            {data.closed
              ? <><span className="inline-block h-2.5 w-2.5 rounded-full bg-[#7A241F]" /> Pendaftaran DITUTUP</>
              : <><span className="inline-block h-2.5 w-2.5 rounded-full bg-[#2F703E]" /> Pendaftaran DIBUKA</>}
          </p>
          <p className="text-xs text-[#7A6A5E] mt-0.5">
            {data.closed
              ? "Form daftar tunggu disembunyikan di halaman publik."
              : "Pengunjung bisa mendaftar daftar tunggu di halaman publik."}
          </p>
        </div>
        <Button onClick={toggleClosed} disabled={togglingClosed} data-testid="waitlist-toggle-closed"
          className={cn("h-11 w-full sm:w-auto text-white shrink-0", data.closed ? "bg-[#2F703E] hover:bg-[#255E33]" : "bg-[#7A241F] hover:bg-[#5E1B17]")}>
          {togglingClosed ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
          {data.closed ? "Buka Daftar Tunggu" : "Tutup Daftar Tunggu"}
        </Button>
      </div>

      <div className="rounded-2xl border border-border p-4 mb-5 grid sm:grid-cols-[1fr_auto_auto] gap-3 items-end" data-testid="waitlist-config">
        <div>
          <Label htmlFor="wl-sess5-name">Judul Sesi Cadangan (bisa diganti)</Label>
          <Input id="wl-sess5-name" data-testid="waitlist-sess5-name" value={sess5Name}
            onChange={(e) => setSess5Name(e.target.value)} placeholder="Sesi Cadangan" className="mt-1.5 h-11" />
          <p className="text-[11px] text-[#7A6A5E] mt-1">Judul ini otomatis dipakai di semua tempat (denah, masterlist, check-in, e-ticket).</p>
        </div>
        <div>
          <Label htmlFor="wl-session-at">Jadwal (tanggal & jam WIB)</Label>
          <Input id="wl-session-at" type="datetime-local" data-testid="waitlist-session-at" value={sessionAt}
            onChange={(e) => setSessionAt(e.target.value)} className="mt-1.5 h-11" />
          {data.session_at && <p className="text-[11px] text-[#255E33] font-medium mt-1" data-testid="waitlist-session-preview">Terjadwal: {fmtWIB(data.session_at)}</p>}
        </div>
        <Button onClick={saveConfig} disabled={savingCfg} data-testid="waitlist-config-save" className="h-11 bg-[#2F703E] hover:bg-[#255E33]">
          {savingCfg ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Simpan
        </Button>
      </div>
      <div className="rounded-2xl border border-border p-4 mb-5 flex items-end gap-3" data-testid="waitlist-config-kuota">
        <div>
          <Label htmlFor="wl-target">Kuota (tiket) untuk buka sesi cadangan</Label>
          <Input id="wl-target" data-testid="waitlist-target" type="number" min={1} value={targetInput}
            onChange={(e) => setTargetInput(e.target.value)} className="mt-1.5 h-11 w-28" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#B26A1E]" /></div>
      ) : data.entries.length === 0 ? (
        <p className="text-center text-sm text-[#7A6A5E] py-12" data-testid="waitlist-empty">Belum ada pendaftar daftar tunggu.</p>
      ) : (
        <div className="space-y-2" data-testid="waitlist-list">
          {data.entries.map((e, i) => (
            <div key={e.id} data-testid={`waitlist-row-${e.id.slice(0, 8)}`}
              className={cn("rounded-xl border p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3", e.order_id ? "border-[#2F703E]/40 bg-[#2F703E]/[0.04]" : e.notified ? "border-[#B26A1E]/40 bg-[#F3E9DD]/40" : "border-border")}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-[#2C1E16]">
                  <span className="text-[#7A6A5E] mr-1">{i + 1}.</span>{e.name}
                  {e.order_id && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-[#2F703E]/15 text-[#255E33] font-medium">Tiket #{e.order_no} · {e.seats}</span>}
                  {!e.order_id && e.notified && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-[#B26A1E]/15 text-[#8A3A12] font-medium">Sudah dihubungi</span>}
                </p>
                <p className="text-xs text-[#7A6A5E] mt-0.5">{e.phone} · Rencana <b>{e.qty}</b> tiket · {fmtTime(e.created_at)}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {e.order_id ? (
                  <>
                    <span className="text-xs text-[#255E33] font-medium inline-flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Tiket dibuat</span>
                    <Button size="sm" onClick={() => openDonate(e)} data-testid={`waitlist-donate-${e.id.slice(0, 8)}`}
                      className="h-9 bg-[#B26A1E] hover:bg-[#8A3A12] text-white"><UploadCloud className="h-4 w-4 mr-1" /> Berdana / Bukti</Button>
                  </>
                ) : (
                  <Button size="sm" onClick={() => openSeatPicker(e)} data-testid={`waitlist-seat-${e.id.slice(0, 8)}`}
                    className="h-9 bg-[#7A241F] hover:bg-[#5E1B17] text-white">
                    <Armchair className="h-4 w-4 mr-1" /> Pilih Kursi & Buat Tiket
                  </Button>
                )}
                <Button size="sm" onClick={() => sendWhatsApp(e)} data-testid={`waitlist-wa-${e.id.slice(0, 8)}`}
                  className="h-9 bg-[#25D366] hover:bg-[#1EA952] text-white">
                  <MessageCircle className="h-4 w-4 mr-1" /> WhatsApp
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEdit(e)} data-testid={`waitlist-edit-${e.id.slice(0, 8)}`}
                  className="h-9 text-xs"><Pencil className="h-3.5 w-3.5 mr-1" /> Ubah</Button>
                <Button size="icon" variant="outline" onClick={() => remove(e)} data-testid={`waitlist-del-${e.id.slice(0, 8)}`}
                  className="h-9 w-9 border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tambah Manual */}
      <Dialog open={addOpen} onOpenChange={(o) => !addBusy && setAddOpen(o)}>
        <DialogContent data-testid="waitlist-add-dialog" className="max-w-md rounded-2xl">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-[#7A241F]">Tambah Pendaftar Manual</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama</Label><Input data-testid="waitlist-add-name" value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} className="mt-1.5 h-11" placeholder="Nama pendaftar" /></div>
            <div><Label>No. HP (WhatsApp)</Label><Input data-testid="waitlist-add-phone" value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} className="mt-1.5 h-11" placeholder="08xxxxxxxxxx" /></div>
            <div><Label>Rencana Jumlah Tiket</Label><Input data-testid="waitlist-add-qty" type="number" min={1} max={20} value={addForm.qty} onChange={(e) => setAddForm({ ...addForm, qty: e.target.value })} className="mt-1.5 h-11 w-32" /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={addBusy} className="flex-1">Batal</Button>
            <Button onClick={addManual} disabled={addBusy} data-testid="waitlist-add-save" className="flex-1 bg-[#B26A1E] hover:bg-[#8A3A12]">
              {addBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <UserPlus className="h-4 w-4 mr-1.5" />} Tambah
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit pendaftar */}
      <Dialog open={!!editRow} onOpenChange={() => !editBusy && setEditRow(null)}>
        <DialogContent data-testid="waitlist-edit-dialog" className="max-w-md rounded-2xl">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-[#7A241F]">Ubah Pendaftar</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama</Label><Input data-testid="waitlist-edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1.5 h-11" /></div>
            <div><Label>No. HP (WhatsApp)</Label><Input data-testid="waitlist-edit-phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="mt-1.5 h-11" /></div>
            <div><Label>Rencana Jumlah Tiket</Label><Input data-testid="waitlist-edit-qty" type="number" min={1} max={20} value={editForm.qty} onChange={(e) => setEditForm({ ...editForm, qty: e.target.value })} className="mt-1.5 h-11 w-32" /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditRow(null)} disabled={editBusy} className="flex-1">Batal</Button>
            <Button onClick={saveEdit} disabled={editBusy} data-testid="waitlist-edit-save" className="flex-1 bg-[#2F703E] hover:bg-[#255E33]">
              {editBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pilih Kursi & Buat Tiket (Sesi Cadangan) */}
      <Dialog open={!!seatRow} onOpenChange={() => !seatBusy && setSeatRow(null)}>
        <DialogContent data-testid="waitlist-seat-dialog" className="max-w-3xl rounded-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#7A241F] flex items-center gap-2"><Armchair className="h-5 w-5 text-[#B26A1E]" /> Pilih Kursi — {data.session5_name || "Sesi Cadangan"}</DialogTitle>
            {seatRow && <p className="text-sm text-[#7A6A5E]"><b className="text-[#2C1E16]">{seatRow.name}</b> · {seatRow.phone} · rencana {seatRow.qty} tiket</p>}
          </DialogHeader>
          {seatLoading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#B26A1E]" /></div>
          ) : (
            <>
              <div className="rounded-xl border border-[#B26A1E]/30 bg-[#F3E9DD]/40 p-3 mb-3 text-sm">
                <p className="text-[#8A3A12] font-medium">
                  Kursi dipilih {seatSelected.length} dari {seatRow?.qty || 1}
                  {seatSelected.length < (seatRow?.qty || 1)
                    ? <span className="text-[#7A241F] font-bold" data-testid="waitlist-seat-shortage"> — kurang {(seatRow?.qty || 1) - seatSelected.length} kursi lagi</span>
                    : seatSelected.length > (seatRow?.qty || 1)
                      ? <span className="text-[#7A241F] font-bold"> — lebih {seatSelected.length - (seatRow?.qty || 1)} dari rencana</span>
                      : <span className="text-[#255E33] font-bold"> ✓ pas</span>}
                </p>
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="waitlist-seat-selected">
                  {seatSelected.length === 0 ? <span className="text-xs text-[#9CA3AF]">Belum ada kursi dipilih</span>
                    : seatSelected.map((s) => <span key={s} className="px-2.5 py-1 rounded-md bg-[#B26A1E]/15 text-[#8A3A12] text-sm font-bold">{s}</span>)}
                </div>
                <p className="text-[11px] text-[#7A6A5E] mt-2">Denah sama seperti sesi lain. Tiket langsung masuk Masterlist & bisa Check-in.</p>
              </div>
              {seatRows.length > 0 && <SeatMap rows={seatRows} selected={seatSelected} onToggle={toggleSeat} couples={seatCouples} allowDisability={true} />}
            </>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSeatRow(null)} disabled={seatBusy} className="flex-1" data-testid="waitlist-seat-cancel">Batal</Button>
            <Button onClick={createTicket} disabled={seatBusy || seatLoading || seatSelected.length < (seatRow?.qty || 1)} data-testid="waitlist-seat-save"
              className="flex-1 bg-[#7A241F] hover:bg-[#5E1B17]">
              {seatBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Ticket className="h-4 w-4 mr-1.5" />}
              {seatSelected.length < (seatRow?.qty || 1) ? `Kurang ${(seatRow?.qty || 1) - seatSelected.length} kursi lagi` : "Buat Tiket Sesi Cadangan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Berdana / Upload Bukti Transfer */}
      <Dialog open={!!donateRow} onOpenChange={() => !donateBusy && setDonateRow(null)}>
        <DialogContent data-testid="waitlist-donate-dialog" className="max-w-md rounded-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#7A241F]">Catat Dana Paramita</DialogTitle>
            {donateRow && <p className="text-sm text-[#7A6A5E]"><b className="text-[#2C1E16]">{donateRow.name}</b> · Tiket #{donateRow.order_no} · {donateRow.seats}</p>}
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Metode</Label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                {[{ k: "transfer", t: "Transfer" }, { k: "qris", t: "QRIS" }, { k: "cash", t: "Cash" }].map((m) => (
                  <button type="button" key={m.k} data-testid={`waitlist-donate-method-${m.k}`}
                    onClick={() => setDonateForm({ ...donateForm, method: m.k })}
                    className={cn("rounded-xl border p-2.5 text-sm font-medium transition-colors",
                      donateForm.method === m.k ? "border-[#B26A1E] bg-[#B26A1E]/5 ring-2 ring-[#B26A1E]/30 text-[#8A3A12]" : "border-border text-[#7A6A5E]")}>{m.t}</button>
                ))}
              </div>
            </div>
            <div>
              <Label>Nominal Dana (Rp)</Label>
              <Input data-testid="waitlist-donate-amount" inputMode="numeric" value={donateForm.amount}
                onChange={(ev) => setDonateForm({ ...donateForm, amount: ev.target.value.replace(/[^0-9]/g, "") })}
                placeholder="0" className="mt-1.5 h-11" />
            </div>
            {donateForm.method !== "cash" && (
              <div>
                <Label>Bukti Transfer / QRIS</Label>
                <label className="mt-1.5 flex items-center justify-center gap-2 h-24 rounded-xl border-2 border-dashed border-[#B26A1E]/40 cursor-pointer hover:bg-[#F3E9DD]/40" data-testid="waitlist-donate-upload">
                  {donateForm.proof ? <img src={donateForm.proof} alt="bukti" className="h-20 rounded" /> : <span className="text-sm text-[#7A6A5E] inline-flex items-center gap-1.5"><UploadCloud className="h-5 w-5" /> Pilih / foto bukti</span>}
                  <input type="file" accept="image/*" className="hidden" data-testid="waitlist-donate-file"
                    onChange={(ev) => { const f = ev.target.files?.[0]; if (f) compressImage(f).then((p) => setDonateForm((d) => ({ ...d, proof: p }))).catch(() => toast.error("Gagal memproses gambar")); }} />
                </label>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDonateRow(null)} disabled={donateBusy} className="flex-1">Batal</Button>
            <Button onClick={saveDonate} disabled={donateBusy} data-testid="waitlist-donate-save" className="flex-1 bg-[#2F703E] hover:bg-[#255E33]">
              {donateBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Simpan Dana
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function DatabasePanel({ onImported }) {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [pending, setPending] = useState(null); // File awaiting confirmation
  const fileRef = useRef(null);

  const exportDb = async () => {
    setExporting(true);
    try {
      const res = await adminApi.get("/admin/db/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "");
      a.href = url; a.download = `db-backup-${stamp}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Database berhasil diunduh");
    } catch {
      toast.error("Gagal mengunduh database");
    } finally { setExporting(false); }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) setPending(f);
    e.target.value = "";
  };

  const doImport = async () => {
    if (!pending) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", pending);
      const { data } = await adminApi.post("/admin/db/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Restore berhasil — ${data.total} dokumen dipulihkan`);
      setPending(null);
      onImported?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengimpor database");
    } finally { setImporting(false); }
  };

  return (
    <div className="space-y-5" data-testid="database-panel">
      <div className="rounded-2xl border border-border bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h2 className="font-serif-display text-2xl text-[#255E33] flex items-center gap-2">
          <Database className="h-5 w-5" /> Backup & Restore Database
        </h2>
        <p className="text-sm text-[#7A6A5E] mt-1.5">
          Unduh <b>seluruh database</b> (pesanan, kursi, user, dll) menjadi satu file, atau pulihkan dari file backup.
          Berguna untuk memindahkan data antar environment (mis. dari <b>preview</b> ke <b>production</b>).
        </p>

        {/* Download */}
        <div className="mt-5 rounded-xl border border-[#2F703E]/25 bg-[#2F703E]/[0.05] p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="font-semibold text-[#255E33] flex items-center gap-1.5"><Download className="h-4 w-4" /> Download Database</p>
              <p className="text-xs text-[#7A6A5E] mt-0.5">Simpan salinan lengkap database saat ini sebagai file .json</p>
            </div>
            <Button onClick={exportDb} disabled={exporting} data-testid="db-export-btn"
              className="bg-[#2F703E] hover:bg-[#255E33]">
              {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
              Download Database
            </Button>
          </div>
        </div>

        {/* Upload */}
        <div className="mt-4 rounded-xl border border-[#B26A1E]/30 bg-[#B26A1E]/[0.05] p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="font-semibold text-[#8A3A12] flex items-center gap-1.5"><UploadCloud className="h-4 w-4" /> Upload / Restore Database</p>
              <p className="text-xs text-[#7A6A5E] mt-0.5">Pilih file backup .json — akan <b>mengganti</b> data yang ada.</p>
            </div>
            <input ref={fileRef} type="file" accept=".json,application/json" onChange={onPickFile}
              className="hidden" data-testid="db-import-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={importing}
              variant="outline" className="border-[#B26A1E]/50 text-[#8A3A12]" data-testid="db-import-pick">
              <UploadCloud className="h-4 w-4 mr-1.5" /> Pilih File Backup
            </Button>
          </div>
        </div>

        <div className="mt-4 flex items-start gap-2 text-xs text-[#8A3A12] bg-[#7A241F]/[0.06] border border-[#7A241F]/20 rounded-lg p-3">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span><b>Hati-hati:</b> Restore akan <b>menimpa (replace)</b> seluruh data pada koleksi yang ada di file backup. Pastikan file benar sebelum melanjutkan.</span>
        </div>
      </div>

      {/* Confirm restore dialog */}
      <Dialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
        <DialogContent data-testid="db-import-confirm">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#7A241F]">Konfirmasi Restore Database</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#2C1E16] space-y-2">
            <p>File: <b className="break-all">{pending?.name}</b> ({pending ? (pending.size / 1024 / 1024).toFixed(2) : 0} MB)</p>
            <p className="text-[#8A3A12]">Semua data pada koleksi di dalam file ini akan <b>diganti</b>. Tindakan ini tidak bisa dibatalkan.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)} disabled={importing} data-testid="db-import-cancel">Batal</Button>
            <Button onClick={doImport} disabled={importing} className="bg-[#7A241F] hover:bg-[#5E1B17]" data-testid="db-import-confirm-btn">
              {importing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-1.5" />}
              Ya, Restore Sekarang
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function UsersPanel({ currentUser }) {
  const isSuper = currentUser?.role === "superadmin";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ username: "", name: "", password: "", role: isSuper ? "checkin" : "loket" });
  const [busy, setBusy] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await adminApi.get("/admin/users"); setUsers(data); }
    catch { toast.error("Gagal memuat user"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminApi.post("/admin/users", form);
      toast.success(`User "${form.username}" dibuat`);
      setForm({ username: "", name: "", password: "", role: isSuper ? "checkin" : "loket" });
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal membuat user"); }
    setBusy(false);
  };
  const doDelete = async () => {
    if (!delTarget) return;
    setBusy(true);
    try {
      await adminApi.delete(`/admin/users/${delTarget.id}`);
      toast.success(`User "${delTarget.username}" dihapus`);
      setDelTarget(null);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal menghapus user"); }
    setBusy(false);
  };
  const togglePermission = async (u, value) => {
    setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, can_delete: value } : x));
    try {
      await adminApi.post(`/admin/users/${u.id}/permission`, { can_delete: value });
      toast.success(value ? `${u.name} kini boleh menghapus data` : `Izin hapus ${u.name} dicabut`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengubah izin");
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, can_delete: !value } : x));
    }
  };
  const [pwTarget, setPwTarget] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const doResetPw = async () => {
    if (newPw.trim().length < 6) { toast.error("Password minimal 6 karakter"); return; }
    setBusy(true);
    try {
      await adminApi.post(`/admin/users/${pwTarget.id}/reset-password`, { new_password: newPw.trim() });
      toast.success(`Password "${pwTarget.username}" berhasil direset`);
      setPwTarget(null); setNewPw(""); setShowPw(false);
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal reset password"); }
    setBusy(false);
  };

  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState("");
  const [editRole, setEditRole] = useState("checkin");
  const openEdit = (u) => { setEditTarget(u); setEditName(u.name || ""); setEditRole(u.role); };
  const doEdit = async () => {
    if (editName.trim().length < 2) { toast.error("Nama minimal 2 karakter"); return; }
    setBusy(true);
    try {
      const { data } = await adminApi.put(`/admin/users/${editTarget.id}`, { name: editName.trim(), role: editRole });
      setUsers((prev) => prev.map((x) => x.id === editTarget.id ? { ...x, ...data } : x));
      toast.success(`User "${editTarget.username}" diperbarui`);
      setEditTarget(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal memperbarui user"); }
    setBusy(false);
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 no-print">
      {/* Create form */}
      <div className="rounded-2xl border border-border bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] h-fit">
        <h2 className="font-serif-display text-2xl text-[#7A241F] flex items-center gap-2 mb-1"><UserPlus className="h-5 w-5 text-[#B26A1E]" /> Tambah User</h2>
        <p className="text-sm text-[#7A6A5E] mb-4">Buat akun login baru untuk panitia.</p>
        <form onSubmit={create} className="space-y-3">
          <div>
            <Label htmlFor="nu">Username</Label>
            <Input id="nu" data-testid="user-username" value={form.username} autoCapitalize="none"
              onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="mis. panitia1" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="nn">Nama Lengkap</Label>
            <Input id="nn" data-testid="user-name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="mis. Budi" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="np">Password</Label>
            <Input id="np" data-testid="user-password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min. 4 karakter" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="nr">Peran</Label>
            <select id="nr" data-testid="user-role" value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="mt-1.5 w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
              <option value="checkin_web">Check-in Website (check-in tiket website; manual diarahkan)</option>
              <option value="loket">Loket (jual tiket + check-in SEMUA termasuk manual)</option>
              {isSuper && <>
                <option value="checkin">Petugas Check-in (hanya check-in semua)</option>
                <option value="seller">Penjual Pre-Order (jual tiket saja, TANPA check-in)</option>
                <option value="admin">Admin (verifikasi + hapus + check-in)</option>
                <option value="superadmin">Super Admin (semua + kelola user)</option>
              </>}
            </select>
          </div>
          <Button type="submit" disabled={busy} data-testid="user-create-btn" className="w-full bg-[#7A241F] hover:bg-[#5E1B17]">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <UserPlus className="h-4 w-4 mr-1.5" />} Buat User
          </Button>
        </form>
      </div>

      {/* List */}
      <div className="rounded-2xl border border-border bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif-display text-2xl text-[#7A241F] flex items-center gap-2"><Users className="h-5 w-5 text-[#B26A1E]" /> Daftar User</h2>
          <Button variant="outline" size="sm" onClick={load} data-testid="users-refresh"><RefreshCw className="h-4 w-4" /></Button>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#B26A1E]" /></div>
        ) : (
          <div className="space-y-2" data-testid="users-list">
            {users.map((u) => {
              const rb = ROLE_BADGE[u.role] || { t: u.role, c: "bg-muted" };
              const isSuperUser = u.role === "superadmin";
              return (
                <div key={u.id} data-testid={`user-row-${u.username}`} className="rounded-xl border border-border p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-[#2C1E16] truncate">{u.name} <span className="text-xs text-[#7A6A5E] font-normal">@{u.username}</span></p>
                      <span className={cn("inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full font-medium", rb.c)}>{rb.t}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {isSuper ? (<>
                      <button onClick={() => openEdit(u)} data-testid={`user-edit-${u.username}`}
                        title="Edit nama & peran" className="inline-flex items-center gap-1 h-8 px-2 rounded-lg text-[#2F703E] hover:bg-[#2F703E]/10 text-xs font-medium">
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </button>
                      <button onClick={() => { setPwTarget(u); setNewPw(""); setShowPw(false); }} data-testid={`user-resetpw-${u.username}`}
                        title="Reset password" className="inline-flex items-center gap-1 h-8 px-2 rounded-lg text-[#B26A1E] hover:bg-[#B26A1E]/10 text-xs font-medium">
                        <KeyRound className="h-3.5 w-3.5" /> Reset PW
                      </button>
                      {u.id === currentUser?.id ? (
                        <span className="text-[11px] text-[#7A6A5E]">Anda</span>
                      ) : (
                        <button onClick={() => setDelTarget(u)} data-testid={`user-delete-${u.username}`}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-[#EF4444] hover:bg-[#EF4444]/10">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      </>) : (
                        <span className="text-[11px] text-[#7A6A5E]">{u.id === currentUser?.id ? "Anda" : "Hanya Super Admin yang dapat mengelola"}</span>
                      )}
                    </div>
                  </div>
                  {isSuper && !isSuperUser && (
                    <div className="mt-2.5 flex items-center justify-between gap-2 rounded-lg bg-[#FDFBF7] border border-border px-2.5 py-2">
                      <span className="text-[11px] font-medium text-[#5B4636] flex items-center gap-1.5">
                        <Trash2 className="h-3.5 w-3.5 text-[#EF4444]" /> Boleh hapus data pesanan
                      </span>
                      <Switch checked={!!u.can_delete} onCheckedChange={(v) => togglePermission(u, v)}
                        data-testid={`user-candelete-${u.username}`} />
                    </div>
                  )}
                  {isSuperUser && (
                    <p className="mt-2 text-[11px] text-[#7A6A5E]">Super Admin selalu bisa menghapus & memulihkan data.</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={!!delTarget} onOpenChange={() => !busy && setDelTarget(null)}>
        <DialogContent data-testid="user-delete-dialog" className="max-w-sm rounded-2xl">
          <DialogHeader>
            <div className="h-11 w-11 rounded-full bg-[#EF4444]/15 flex items-center justify-center mb-2"><AlertTriangle className="h-5 w-5 text-[#EF4444]" /></div>
            <DialogTitle className="font-serif-display text-2xl text-[#7A241F]">Hapus user ini?</DialogTitle>
          </DialogHeader>
          {delTarget && (
            <p className="text-sm text-[#7A6A5E]">User <b className="text-[#2C1E16]">{delTarget.name} (@{delTarget.username})</b> tidak akan bisa login lagi. Tindakan ini tidak bisa dibatalkan.</p>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDelTarget(null)} disabled={busy} className="flex-1" data-testid="user-delete-cancel">Batal</Button>
            <Button onClick={doDelete} disabled={busy} className="flex-1 bg-[#EF4444] hover:bg-[#DC2626]" data-testid="user-delete-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Trash2 className="h-4 w-4 mr-1.5" />} Hapus
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwTarget} onOpenChange={() => !busy && (setPwTarget(null), setNewPw(""))}>
        <DialogContent data-testid="user-resetpw-dialog" className="max-w-sm rounded-2xl">
          <DialogHeader>
            <div className="h-11 w-11 rounded-full bg-[#B26A1E]/15 flex items-center justify-center mb-2"><KeyRound className="h-5 w-5 text-[#B26A1E]" /></div>
            <DialogTitle className="font-serif-display text-2xl text-[#7A241F]">Reset Password</DialogTitle>
          </DialogHeader>
          {pwTarget && (
            <div className="space-y-3">
              <p className="text-sm text-[#7A6A5E]">Buat password baru untuk <b className="text-[#2C1E16]">{pwTarget.name} (@{pwTarget.username})</b>. Password lama tidak berlaku lagi.</p>
              <div>
                <Label htmlFor="newpw">Password Baru</Label>
                <div className="relative mt-1.5">
                  <Input id="newpw" data-testid="user-resetpw-input" type={showPw ? "text" : "password"}
                    value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Minimal 6 karakter" className="pr-16" autoComplete="new-password" />
                  <button type="button" onClick={() => setShowPw((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] font-medium text-[#B26A1E]">{showPw ? "Sembunyi" : "Lihat"}</button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => { setPwTarget(null); setNewPw(""); }} disabled={busy} className="flex-1" data-testid="user-resetpw-cancel">Batal</Button>
            <Button onClick={doResetPw} disabled={busy || newPw.trim().length < 6} className="flex-1 bg-[#B26A1E] hover:bg-[#9A5716]" data-testid="user-resetpw-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <KeyRound className="h-4 w-4 mr-1.5" />} Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={() => !busy && setEditTarget(null)}>
        <DialogContent data-testid="user-edit-dialog" className="max-w-sm rounded-2xl">
          <DialogHeader>
            <div className="h-11 w-11 rounded-full bg-[#2F703E]/15 flex items-center justify-center mb-2"><Pencil className="h-5 w-5 text-[#2F703E]" /></div>
            <DialogTitle className="font-serif-display text-2xl text-[#7A241F]">Edit User</DialogTitle>
          </DialogHeader>
          {editTarget && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="editname">Nama Lengkap</Label>
                <Input id="editname" data-testid="user-edit-name" value={editName}
                  onChange={(e) => setEditName(e.target.value)} placeholder="Nama user" className="mt-1.5" />
                <p className="text-[11px] text-[#7A6A5E] mt-1">Username <b>@{editTarget.username}</b> tidak bisa diubah.</p>
              </div>
              <div>
                <Label className="mb-1.5 block">Peran / Akses</Label>
                <div className="space-y-2">
                  {[
                    { v: "checkin_web", t: "Check-in Website", d: "Check-in tiket Website; order manual hanya diarahkan (tidak bisa tandai hadir)" },
                    { v: "loket", t: "Loket", d: "Jual tiket di tempat + check-in SEMUA (termasuk Order Manual)" },
                    { v: "checkin", t: "Petugas Check-in", d: "Check-in semua peserta" },
                    { v: "seller", t: "Penjual Pre-Order", d: "Jual tiket pre-order saja (tanpa check-in; peserta check-in di hari-H)" },
                    { v: "admin", t: "Admin", d: "Verifikasi + hapus + check-in + jual" },
                    { v: "superadmin", t: "Super Admin", d: "Semua akses + kelola user" },
                  ].map((opt) => {
                    const active = editRole === opt.v;
                    const selfDemote = editTarget.id === currentUser?.id && opt.v !== "superadmin";
                    return (
                      <button key={opt.v} type="button" disabled={selfDemote}
                        onClick={() => setEditRole(opt.v)} data-testid={`user-edit-role-${opt.v}`}
                        className={cn("w-full text-left flex items-start gap-2.5 rounded-xl border px-3 py-2.5 transition-colors disabled:opacity-40",
                          active ? "border-[#2F703E] bg-[#2F703E]/[0.06]" : "border-border bg-white hover:border-[#2F703E]/40")}>
                        <span className={cn("mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0",
                          active ? "border-[#2F703E]" : "border-[#9CA3AF]")}>
                          {active && <span className="h-2 w-2 rounded-full bg-[#2F703E]" />}
                        </span>
                        <span>
                          <span className="block text-sm font-semibold text-[#2C1E16]">{opt.t}</span>
                          <span className="block text-[11px] text-[#7A6A5E]">{opt.d}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                {editTarget.id === currentUser?.id && (
                  <p className="text-[11px] text-[#B26A1E] mt-1.5">Anda tidak bisa menurunkan peran akun sendiri.</p>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setEditTarget(null)} disabled={busy} className="flex-1" data-testid="user-edit-cancel">Batal</Button>
            <Button onClick={doEdit} disabled={busy || editName.trim().length < 2} className="flex-1 bg-[#2F703E] hover:bg-[#255E33]" data-testid="user-edit-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Pencil className="h-4 w-4 mr-1.5" />} Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BendaharaPanel({ isSuper, onGoSetoran }) {
  const [resp, setResp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [exporting, setExporting] = useState(false);
  const [channel, setChannel] = useState("all");
  const [seller, setSeller] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("time_desc");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [proofView, setProofView] = useState(null);
  const [proofLoading, setProofLoading] = useState(false);

  const viewProof = async (o) => {
    setProofView({ name: o.name, order_no: o.order_no, image: null });
    setProofLoading(true);
    try {
      const { data } = await adminApi.get(`/admin/orders/${o.id}/proof-image`);
      setProofView({ name: o.name, order_no: o.order_no, image: data.proof_image });
    } catch { toast.error("Gagal memuat bukti"); setProofView(null); }
    setProofLoading(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      const { data } = await adminApi.get("/admin/bendahara", { params });
      setResp(data);
    } catch { toast.error("Gagal memuat rekap bendahara"); }
    setLoading(false);
  }, [dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const params = { responseType: "blob" };
      if (dateFrom || dateTo) params.params = { ...(dateFrom && { from: dateFrom }), ...(dateTo && { to: dateTo }) };
      const res = await adminApi.get("/admin/bendahara/export", params);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "rekap_bendahara.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal export Excel"); }
    setExporting(false);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#2F703E]" /></div>;

  const g = resp?.grand_total || {};
  const _paidRows = (resp?.orders || []).map((o) => ({ ...o, paid: true }));
  const _unpaidRows = (resp?.unpaid_manual || []).map((o) => ({ ...o, paid: false }));
  const _allRows = [..._paidRows, ..._unpaidRows];
  const sellers = Array.from(new Set(_allRows.map((o) => o.seller))).sort();
  let orders = _allRows.filter((o) =>
    (channel === "all" || o.channel === channel) &&
    (seller === "all" || o.seller === seller) &&
    (!search || o.name?.toLowerCase().includes(search.toLowerCase()) || String(o.order_no).includes(search))
  );
  orders = [...orders].sort((a, b) => {
    if (sort === "time_desc") return (b.created_at || "").localeCompare(a.created_at || "");
    if (sort === "time_asc") return (a.created_at || "").localeCompare(b.created_at || "");
    if (sort === "amount_desc") return b.amount - a.amount;
    if (sort === "amount_asc") return a.amount - b.amount;
    if (sort === "seller") return (a.seller || "").localeCompare(b.seller || "");
    if (sort === "name") return (a.name || "").localeCompare(b.name || "");
    return 0;
  });

  const methodChip = (m) => ({ cash: { t: "Cash", c: "bg-[#2F703E]/15 text-[#255E33]" },
    qris: { t: "QRIS", c: "bg-[#7A241F]/10 text-[#7A241F]" },
    transfer: { t: "Transfer", c: "bg-[#B26A1E]/15 text-[#8A3A12]" } }[m] || { t: m, c: "bg-muted" });

  const Stat = ({ label, val, big, color }) => (
    <div className="rounded-xl border border-border bg-white p-3">
      <p className="text-[11px] text-[#7A6A5E]">{label}</p>
      <p className={cn(big ? "font-serif-display text-2xl" : "text-lg font-bold", color)}>{rupiah(val || 0)}</p>
    </div>
  );

  return (
    <div className="no-print" data-testid="bendahara-panel">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="font-serif-display text-2xl text-[#255E33] flex items-center gap-2"><Banknote className="h-5 w-5" /> Rekap Bendahara — Laporan Keuangan</h2>
          <p className="text-sm text-[#7A6A5E]">Semua pemasukan terverifikasi: Order Website (langsung) + Order Manual (+ Cash/Walk-in bila ada). Bisa disortir & di-export.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="bendahara-refresh"><RefreshCw className="h-4 w-4 mr-1.5" /> Muat Ulang</Button>
          <Button onClick={exportExcel} disabled={exporting} data-testid="bendahara-export" className="bg-[#2F703E] hover:bg-[#255E33]">
            {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Export Excel
          </Button>
        </div>
      </div>

      {isSuper && (
        <div className="rounded-lg border border-[#2F703E]/30 bg-[#2F703E]/[0.06] px-3 py-2 mb-4 flex items-center gap-2 text-sm text-[#255E33]" data-testid="bendahara-to-setoran-note">
          <Wallet className="h-4 w-4 flex-shrink-0" />
          <span>Untuk mencatat <b>setoran uang tunai</b> dari petugas ke Bendahara (closing cash), buka tab</span>
          <button onClick={onGoSetoran} data-testid="bendahara-to-setoran-btn"
            className="underline font-semibold hover:text-[#2F703E]">Setoran Kas →</button>
        </div>
      )}

      {/* Date range filter */}
      <div className="rounded-xl border border-border bg-white p-3 mb-4 flex flex-wrap items-end gap-3" data-testid="bendahara-daterange">
        <div>
          <label className="text-[11px] text-[#7A6A5E] block mb-1">Dari tanggal</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            data-testid="bendahara-date-from" className="text-sm border border-border rounded-lg px-2.5 py-1.5 bg-white" />
        </div>
        <div>
          <label className="text-[11px] text-[#7A6A5E] block mb-1">Sampai tanggal</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            data-testid="bendahara-date-to" className="text-sm border border-border rounded-lg px-2.5 py-1.5 bg-white" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => { const t = new Date().toISOString().slice(0, 10); setDateFrom(t); setDateTo(t); }}
            data-testid="bendahara-preset-today" className="text-xs rounded-full px-3 py-1.5 border border-border bg-white text-[#7A6A5E] hover:border-[#2F703E]/50">Hari ini</button>
          <button onClick={() => { setDateFrom("2026-09-13"); setDateTo("2026-09-13"); }}
            data-testid="bendahara-preset-event" className="text-xs rounded-full px-3 py-1.5 border border-border bg-white text-[#7A6A5E] hover:border-[#2F703E]/50">Hari Acara (13 Sep)</button>
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(""); setDateTo(""); }}
              data-testid="bendahara-preset-all" className="text-xs rounded-full px-3 py-1.5 border border-[#B26A1E]/40 bg-white text-[#7A241F] hover:border-[#B26A1E]">Semua tanggal ✕</button>
          )}
        </div>
        {(dateFrom || dateTo) && (
          <span className="text-[11px] text-[#255E33] font-medium ml-auto" data-testid="bendahara-daterange-info">
            Menampilkan {dateFrom || "awal"} s/d {dateTo || "akhir"}
          </span>
        )}
      </div>

      {/* Grand total */}
      <div className="rounded-2xl border border-[#2F703E]/25 bg-[#2F703E]/[0.05] p-4 mb-5" data-testid="bendahara-grand">
        <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
          <div>
            <p className="text-xs text-[#7A6A5E]">Total Dana Terkumpul (semua)</p>
            <p className="font-serif-display text-4xl text-[#255E33]" data-testid="bendahara-grand-amount">{rupiah(g.amount)}</p>
            <p className="text-xs text-[#7A6A5E] mt-0.5">{g.orders || 0} transaksi · {g.tickets || 0} tiket</p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            {(g.cash || 0) > 0 && <span className="rounded-md bg-[#2F703E]/10 text-[#255E33] px-2.5 py-1.5 font-semibold">Cash {rupiah(g.cash)}</span>}
            {(g.qris || 0) > 0 && <span className="rounded-md bg-[#7A241F]/10 text-[#7A241F] px-2.5 py-1.5 font-semibold">QRIS {rupiah(g.qris)}</span>}
            {(g.transfer || 0) > 0 && <span className="rounded-md bg-[#B26A1E]/10 text-[#8A3A12] px-2.5 py-1.5 font-semibold">Transfer {rupiah(g.transfer)}</span>}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Stat label="Order Website (langsung)" val={g.website_amount} big color="text-[#7A241F]" />
          <Stat label="Order Manual" val={g.manual_amount} big color="text-[#8A3A12]" />
          {(g.cash || 0) > 0 && <Stat label="Cash (di lokasi)" val={g.cash} big color="text-[#255E33]" />}
          {(g.panitia_amount || 0) > 0 && <Stat label="Jual di Tempat (Walk-in)" val={g.panitia_amount} big color="text-[#B26A1E]" />}
          {(g.vip_amount || 0) > 0 && <Stat label="VIP (Undangan)" val={g.vip_amount} big color="text-[#7A6A5E]" />}
        </div>
      </div>

      {/* Filter + sort bar */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="bendahara-filters">
        <div className="flex gap-1">
          {[["all", "Semua"], ["umum", "Umum"], ["panitia", "Panitia"]].map(([k, t]) => (
            <button key={k} onClick={() => setChannel(k)} data-testid={`bendahara-ch-${k}`}
              className={cn("px-3 py-1.5 rounded-full text-xs font-medium border",
                channel === k ? "bg-[#2F703E] text-white border-[#2F703E]" : "bg-white text-[#7A6A5E] border-border")}>{t}</button>
          ))}
        </div>
        <select value={seller} onChange={(e) => setSeller(e.target.value)} data-testid="bendahara-seller-filter"
          className="text-xs border border-border rounded-lg px-2 py-1.5 bg-white">
          <option value="all">Semua petugas/sumber</option>
          {sellers.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="bendahara-sort"
          className="text-xs border border-border rounded-lg px-2 py-1.5 bg-white">
          <option value="time_desc">Terbaru</option>
          <option value="time_asc">Terlama</option>
          <option value="amount_desc">Nominal terbesar</option>
          <option value="amount_asc">Nominal terkecil</option>
          <option value="seller">Nama petugas (A-Z)</option>
          <option value="name">Nama pembeli (A-Z)</option>
        </select>
        <div className="relative flex-1 min-w-[140px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9CA3AF]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama / no order"
            data-testid="bendahara-search" className="w-full text-xs border border-border rounded-lg pl-8 pr-2 py-1.5 bg-white" />
        </div>
      </div>

      {/* Mobile card view — nominal, status/lunas & bukti terlihat tanpa scroll */}
      <div className="md:hidden space-y-2 mb-6" data-testid="bendahara-txn-cards">
        {orders.length === 0 ? (
          <p className="text-center text-sm text-[#7A6A5E] py-8">Belum ada transaksi.</p>
        ) : orders.map((o, i) => {
          const mc = methodChip(o.method);
          return (
            <div key={o.order_no ?? i} className="rounded-xl border border-border bg-white p-3" data-testid={`bendahara-card-${i}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-[#2C1E16] truncate">{o.name} <span className="font-mono text-[10px] text-[#7A6A5E]">#{o.order_no}</span></p>
                  <p className="text-[11px] text-[#7A6A5E]">{o.session_name} · {o.seats.join(", ")} ({o.tickets} tkt)</p>
                  <p className="text-[11px] text-[#7A6A5E]">{o.channel === "panitia" ? "Panitia" : o.channel === "manual" ? "Manual" : "Website"} · {o.seller}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-serif-display text-lg text-[#7A241F]">{o.paid ? rupiah(o.amount) : "—"}</p>
                  {o.method ? <span className={cn("text-[10px] px-2 py-0.5 rounded-full font-medium", mc.c)}>{mc.t}</span> : null}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-dashed border-border">
                <div>
                  {o.paid
                    ? <div><span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#2F703E]/15 text-[#255E33]">Lunas</span><span className="block text-[10px] text-[#7A6A5E] mt-0.5">{o.paid_at_label || `${o.date} ${o.time}`}</span></div>
                    : <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#EF4444]/10 text-[#B91C1C]">Belum Lunas</span>}
                </div>
                {o.has_proof
                  ? <button onClick={() => viewProof(o)} data-testid={`bendahara-card-proof-${i}`}
                      className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg border border-[#B26A1E]/40 text-[#8A3A12] hover:bg-[#B26A1E]/10">
                      <Eye className="h-3.5 w-3.5" /> Lihat Bukti
                    </button>
                  : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* Sortable transactions table (desktop) */}
      <div className="rounded-2xl border border-border bg-white overflow-x-auto mb-6 hidden md:block" data-testid="bendahara-txn-table">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[#7A6A5E] text-xs">
            <tr>
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Tgl / Jam</th>
              <th className="px-3 py-2 text-left font-medium">Pembeli</th>
              <th className="px-3 py-2 text-right font-medium">Nominal Dana</th>
              <th className="px-3 py-2 text-left font-medium">Status Berdana</th>
              <th className="px-3 py-2 text-center font-medium">Bukti Transfer</th>
              <th className="px-3 py-2 text-center font-medium">Metode</th>
              <th className="px-3 py-2 text-left font-medium">Kanal</th>
              <th className="px-3 py-2 text-left font-medium">Petugas/Sumber</th>
              <th className="px-3 py-2 text-left font-medium">Lokasi</th>
              <th className="px-3 py-2 text-left font-medium">Sesi / Kursi</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr><td colSpan={10} className="px-3 py-8 text-center text-[#7A6A5E]" data-testid="bendahara-empty">Belum ada transaksi.</td></tr>
            ) : orders.map((o, i) => {
              const mc = methodChip(o.method);
              return (
                <tr key={o.order_no ?? i} className="border-t border-border" data-testid={`bendahara-txn-${i}`}>
                  <td className="px-3 py-2 text-[#7A6A5E] whitespace-nowrap">{o.date}<span className="block text-[10px]">{o.time}</span></td>
                  <td className="px-3 py-2 font-medium text-[#2C1E16]">{o.name}<span className="block font-mono text-[10px] text-[#7A6A5E]">#{o.order_no}</span></td>
                  <td className="px-3 py-2 text-right font-semibold text-[#7A241F]">{o.paid ? rupiah(o.amount) : <span className="text-[#7A6A5E] font-normal">—</span>}</td>
                  <td className="px-3 py-2" data-testid={`bendahara-status-${i}`}>
                    {o.paid
                      ? <span><span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#2F703E]/15 text-[#255E33]">Lunas</span><span className="block text-[10px] text-[#7A6A5E] mt-0.5">{o.paid_at_label || `${o.date} ${o.time}`}</span></span>
                      : <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#EF4444]/10 text-[#B91C1C]">Belum Lunas</span>}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {o.has_proof
                      ? <button onClick={() => viewProof(o)} data-testid={`bendahara-proof-${i}`}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg border border-[#B26A1E]/40 text-[#8A3A12] hover:bg-[#B26A1E]/10">
                          <Eye className="h-3.5 w-3.5" /> Lihat
                        </button>
                      : <span className="text-[10px] text-[#7A6A5E]">—</span>}
                  </td>
                  <td className="px-3 py-2 text-center">{o.method ? <span className={cn("text-[10px] px-2 py-0.5 rounded-full font-medium", mc.c)}>{mc.t}</span> : <span className="text-[10px] text-[#7A6A5E]">—</span>}</td>
                  <td className="px-3 py-2"><span className={cn("text-[10px] px-1.5 py-0.5 rounded-full font-medium", o.channel === "panitia" ? "bg-[#B26A1E]/15 text-[#8A3A12]" : o.channel === "manual" ? "bg-[#7A241F]/10 text-[#7A241F]" : "bg-[#7A241F]/10 text-[#7A241F]")}>{o.channel === "panitia" ? "Panitia" : o.channel === "manual" ? "Manual" : "Website"}</span></td>
                  <td className="px-3 py-2 text-[#5B4636]">{o.seller}</td>
                  <td className="px-3 py-2 text-[#5B4636]">{o.location}</td>
                  <td className="px-3 py-2 text-[#5B4636] whitespace-nowrap">{o.session_name} · {o.seats.join(", ")} <span className="text-[10px] text-[#7A6A5E]">({o.tickets} tkt)</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Per tanggal cards */}
      <h3 className="font-serif-display text-xl text-[#7A241F] mb-3">Ringkasan per Tanggal</h3>
      <div className="space-y-3">
        {(resp?.by_date || []).map((day) => {
          const isOpen = open[day.date] ?? false;
          return (
            <div key={day.date} className="rounded-2xl border border-border bg-white p-4" data-testid={`bendahara-day-${day.date}`}>
              <button onClick={() => setOpen((p) => ({ ...p, [day.date]: !isOpen }))} data-testid={`bendahara-toggle-${day.date}`}
                className="w-full flex items-center justify-between gap-3 text-left">
                <div>
                  <p className="font-serif-display text-lg text-[#7A241F]">{day.date_label}</p>
                  <p className="text-xs text-[#7A6A5E]">{day.total.orders} transaksi · {day.total.tickets} tiket · Umum {rupiah(day.total.umum_amount)} · Panitia {rupiah(day.total.panitia_amount)}</p>
                </div>
                <span className="font-serif-display text-xl text-[#255E33]" data-testid={`bendahara-total-${day.date}`}>{rupiah(day.total.amount)}</span>
              </button>
              {isOpen && (
                <div className="grid md:grid-cols-2 gap-4 mt-3">
                  <div className="rounded-xl border border-border p-3">
                    <p className="text-xs font-semibold text-[#7A241F] mb-2">Per Petugas/Sumber</p>
                    <div className="space-y-1.5">
                      {day.by_seller.map((s) => (
                        <div key={s.seller} className="flex items-center justify-between text-xs" data-testid={`bendahara-seller-${day.date}`}>
                          <span className="text-[#5B4636]">{s.seller} <span className="text-[10px] text-[#7A6A5E]">({s.orders} trx · {s.tickets} tkt)</span></span>
                          <b className="text-[#255E33]">{rupiah(s.amount)}</b>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-xl border border-border p-3">
                    <p className="text-xs font-semibold text-[#7A241F] mb-2">Per Lokasi</p>
                    <div className="space-y-1.5">
                      {day.by_location.map((l) => (
                        <div key={l.location} className="flex items-center justify-between text-xs" data-testid={`bendahara-loc-${day.date}`}>
                          <span className="text-[#5B4636] flex items-center gap-1"><MapPin className="h-3 w-3 text-[#B26A1E]" /> {l.location} <span className="text-[10px] text-[#7A6A5E]">({l.orders} trx)</span></span>
                          <b className="text-[#255E33]">{rupiah(l.amount)}</b>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!proofView} onOpenChange={() => setProofView(null)}>
        <DialogContent data-testid="bendahara-proof-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#7A241F]">Bukti Transfer</DialogTitle>
          </DialogHeader>
          {proofView && (
            <div className="space-y-3">
              <p className="text-sm"><b>{proofView.name}</b> <span className="font-mono text-xs text-[#7A6A5E]">#{proofView.order_no}</span></p>
              {proofLoading || !proofView.image
                ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#B26A1E]" /></div>
                : <img src={proofView.image} alt="Bukti transfer" className="w-full rounded-lg border border-border" data-testid="bendahara-proof-image" />}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WalkinPanel() {
  return (
    <div className="max-w-xl mx-auto no-print">
      <div className="rounded-2xl border border-border bg-white p-8 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="h-14 w-14 rounded-full bg-[#B26A1E]/10 flex items-center justify-center mb-4 mx-auto">
          <Store className="h-7 w-7 text-[#B26A1E]" />
        </div>
        <h2 className="font-serif-display text-2xl text-[#7A241F]">Jual Tiket di Tempat</h2>
        <p className="text-sm text-[#7A6A5E] mt-1 mb-5 max-w-md mx-auto">
          Halaman khusus layar monitor: pembeli melihat peta kursi, petugas memilih kursi, langsung lunas & check-in. Ketersediaan kursi mengikuti sistem secara real-time.
        </p>
        <a href="/walkin" target="_blank" rel="noreferrer" data-testid="open-walkin"
          className="inline-flex items-center gap-2 bg-[#7A241F] hover:bg-[#5E1B17] text-white rounded-full px-6 py-3 text-sm font-medium">
          <Store className="h-4 w-4" /> Buka Halaman Jual di Tempat &rarr; /walkin
        </a>
      </div>
    </div>
  );
}


function TrashPanel({ orders, loading, onRefresh, onRestore, restoreBusyId }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] no-print" data-testid="trash-panel">
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <div>
          <h2 className="font-serif-display text-2xl text-[#7A241F] flex items-center gap-2"><Trash2 className="h-5 w-5 text-[#EF4444]" /> Kotak Sampah</h2>
          <p className="text-sm text-[#7A6A5E]">Pesanan yang dihapus tersimpan di sini. Super Admin bisa memulihkannya.</p>
        </div>
        <Button variant="outline" onClick={onRefresh} data-testid="trash-refresh"><RefreshCw className="h-4 w-4 mr-1.5" /> Muat Ulang</Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#B26A1E]" /></div>
      ) : orders.length === 0 ? (
        <p className="text-center text-sm text-[#7A6A5E] py-12" data-testid="trash-empty">Kotak sampah kosong.</p>
      ) : (
        <div className="space-y-2" data-testid="trash-list">
          {orders.map((o) => (
            <div key={o.id} data-testid={`trash-row-${o.id.slice(0, 8)}`}
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border p-3">
              <div className="min-w-0">
                <p className="font-semibold text-[#2C1E16]">{o.name} <span className="font-mono text-[10px] text-[#7A6A5E]">#{o.order_no}</span></p>
                <p className="text-xs text-[#7A6A5E]">{o.phone} · {o.session?.name} · {o.seats?.join(", ")} · {rupiah(o.total_amount)}</p>
                {o.deleted_by && <p className="text-[11px] text-[#EF4444]">Dihapus oleh {o.deleted_by} · {fmtTime(o.deleted_at)}</p>}
              </div>
              <Button size="sm" onClick={() => onRestore(o)} disabled={restoreBusyId === o.id}
                data-testid={`trash-restore-${o.id.slice(0, 8)}`} className="bg-[#2F703E] hover:bg-[#255E33]">
                {restoreBusyId === o.id ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RotateCcw className="h-4 w-4 mr-1.5" />} Pulihkan
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function markEditableSeats(mapData, ownSeats) {
  if (!mapData) return mapData;
  const own = new Set(ownSeats || []);
  return {
    ...mapData,
    rows: mapData.rows.map((r) => ({
      ...r,
      blocks: r.blocks.map((b) => b.map((s) => (own.has(s.label) ? { ...s, status: "available" } : s))),
    })),
  };
}

function VIPPanel() {
  const [sessionId, setSessionId] = useState(1);
  const [mapData, setMapData] = useState(null);
  const [loadingMap, setLoadingMap] = useState(true);
  const [selected, setSelected] = useState([]);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [editSeats, setEditSeats] = useState([]);
  const [editMap, setEditMap] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const pollRef = useRef(null);

  const loadMap = useCallback(async (sid, showLoad) => {
    if (showLoad) setLoadingMap(true);
    try { const { data } = await adminApi.get(`/sessions/${sid}/seats`); setMapData(data); }
    catch (e) { console.error("Gagal memuat peta kursi VIP:", e); }
    setLoadingMap(false);
  }, []);

  useEffect(() => {
    setSelected([]);
    loadMap(sessionId, true);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => loadMap(sessionId, false), 6000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [sessionId, loadMap]);

  const couples = mapData?.couples || {};
  const toggle = (seat) => {
    setSelected((prev) => {
      const partner = couples[seat];
      if (prev.includes(seat)) return prev.filter((s) => s !== seat && s !== partner);
      const add = partner ? [seat, partner] : [seat];
      return [...new Set([...prev, ...add])];
    });
  };

  const submit = async () => {
    if (!name.trim()) { toast.error("Isi nama tamu VIP"); return; }
    if (selected.length === 0) { toast.error("Pilih minimal 1 kursi"); return; }
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/vip", { name, session_id: sessionId, seats: selected, note });
      setResult(data);
      setName(""); setNote(""); setSelected([]);
      loadMap(sessionId, false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat tiket VIP"); loadMap(sessionId, false); }
    setBusy(false);
  };

  const loadList = useCallback(async () => {
    try { const { data } = await adminApi.get("/admin/vip"); setList(data || []); }
    catch (e) { console.error("Gagal memuat daftar VIP:", e); }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const openEdit = async (o) => {
    setEdit(o); setEditSeats(o.seats || []); setEditMap(null); setEditLoading(true);
    try {
      const { data } = await adminApi.get(`/sessions/${o.session_id}/seats`);
      setEditMap(markEditableSeats(data, o.seats || []));
    } catch (e) { toast.error("Gagal memuat peta kursi"); }
    setEditLoading(false);
  };
  const editCouples = editMap?.couples || {};
  const toggleEdit = (seat) => {
    setEditSeats((prev) => {
      const partner = editCouples[seat];
      if (prev.includes(seat)) return prev.filter((s) => s !== seat && s !== partner);
      const add = partner ? [seat, partner] : [seat];
      return [...new Set([...prev, ...add])];
    });
  };
  const saveEdit = async () => {
    if (!edit) return;
    if (!edit.name || !edit.name.trim()) { toast.error("Isi nama tamu VIP"); return; }
    if (editSeats.length === 0) { toast.error("Pilih minimal 1 kursi"); return; }
    setBusy(true);
    try {
      await adminApi.put(`/admin/vip/${edit.id}`, { name: edit.name, note: edit.note || "", seats: editSeats });
      toast.success("Tiket VIP diperbarui");
      setEdit(null); setEditMap(null); loadList(); loadMap(sessionId, false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    setBusy(false);
  };
  const delItem = async (o) => {
    if (!window.confirm(`Hapus tiket VIP "${o.name}" (${o.seats?.join(", ")})? Kursi akan dilepas.`)) return;
    try {
      await adminApi.delete(`/admin/vip/${o.id}`);
      toast.success("Tiket VIP dihapus");
      loadList(); loadMap(sessionId, false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus"); }
  };

  return (
    <div className="no-print" data-testid="vip-panel">
      <div className="rounded-2xl border border-[#7A241F]/20 bg-gradient-to-br from-[#7A241F]/[0.06] to-transparent p-4 mb-4">
        <h2 className="font-serif-display text-2xl text-[#7A241F] flex items-center gap-2"><Crown className="h-5 w-5 text-[#B26A1E]" /> Pesan Tiket VIP (Tamu Undangan)</h2>
        <p className="text-sm text-[#7A6A5E]">Pilih kursi untuk tamu VIP di sesi mana pun (gratis, kursi langsung dikunci). Tamu tetap check-in di lokasi saat datang.</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-4">
        <div className="rounded-2xl border border-border bg-white p-4">
          <div className="flex flex-wrap gap-2 mb-3">
            {SESSIONS_STATIC.map((s) => (
              <button key={s.id} data-testid={`vip-session-${s.id}`} onClick={() => setSessionId(s.id)}
                className={cn("px-3 py-1.5 rounded-full text-sm font-medium border transition-colors",
                  sessionId === s.id ? "bg-[#7A241F] text-white border-[#7A241F]" : "bg-white text-[#7A6A5E] border-border hover:border-[#7A241F]/50")}>
                {s.name} · {s.time}
              </button>
            ))}
          </div>
          {mapData && (
            <p className="text-sm mb-3" data-testid="vip-remaining">
              Sisa <b className="text-[#255E33]">{mapData.capacity - mapData.booked}</b> kursi · {mapData.booked}/{mapData.capacity} terisi
            </p>
          )}
          {loadingMap ? (
            <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#B26A1E]" /></div>
          ) : mapData ? (
            <SeatMap rows={mapData.rows} selected={selected} onToggle={toggle} couples={couples} allowDisability
              aisleRow={mapData.aisle_row} doorRow={mapData.door_row} reverse={mapData.reverse} />
          ) : (
            <p className="text-center text-sm text-[#7A6A5E] py-16">Gagal memuat peta kursi.</p>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-white p-4 h-fit lg:sticky lg:top-4">
          <Label htmlFor="vipname">Nama Tamu VIP <span className="text-[#EF4444]">*</span></Label>
          <Input id="vipname" data-testid="vip-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="mis. Bapak Budi (Donatur)" className="mt-1.5 mb-3" />
          <Label htmlFor="vipnote">Catatan <span className="text-[#9CA3AF]">(opsional)</span></Label>
          <Input id="vipnote" data-testid="vip-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="mis. tamu undangan panitia" className="mt-1.5 mb-3" />
          <div className="rounded-lg bg-muted/40 p-2.5 mb-3">
            <p className="text-xs text-[#7A6A5E]">Kursi dipilih:</p>
            <p className="text-sm font-semibold text-[#7A241F]" data-testid="vip-selected">{selected.length ? selected.join(", ") : "— belum ada —"}</p>
          </div>
          <Button onClick={submit} disabled={busy || selected.length === 0 || !name.trim()} data-testid="vip-submit"
            className="w-full h-12 bg-[#7A241F] hover:bg-[#5E1B17] text-base">
            {busy ? <Loader2 className="h-5 w-5 animate-spin mr-1.5" /> : <Crown className="h-5 w-5 mr-1.5" />} Kunci Kursi VIP
          </Button>
        </div>
      </div>

      <Dialog open={!!result} onOpenChange={() => setResult(null)}>
        <DialogContent data-testid="vip-result" className="max-w-sm rounded-2xl">
          <DialogHeader>
            <div className="h-12 w-12 rounded-full bg-[#B26A1E]/15 flex items-center justify-center mb-2"><Crown className="h-6 w-6 text-[#B26A1E]" /></div>
            <DialogTitle className="font-serif-display text-2xl text-[#7A241F]">Tiket VIP Dibuat!</DialogTitle>
          </DialogHeader>
          {result && (
            <div className="space-y-1.5 text-sm">
              <p><span className="text-[#7A6A5E]">Nama:</span> <b>{result.name}</b></p>
              <p><span className="text-[#7A6A5E]">No. Order:</span> <b className="font-mono">#{result.order_no}</b></p>
              <p><span className="text-[#7A6A5E]">Sesi:</span> <b>{SESSIONS_STATIC.find((s) => s.id === result.session_id)?.name}</b></p>
              <p><span className="text-[#7A6A5E]">Kursi:</span> <b>{result.seats?.join(", ")}</b></p>
              <p className="text-xs text-[#7A6A5E] pt-1">Kursi sudah dikunci. Tamu check-in di lokasi saat datang.</p>
            </div>
          )}
          <Button onClick={() => setResult(null)} className="w-full bg-[#7A241F] hover:bg-[#5E1B17] mt-2" data-testid="vip-result-close">Selesai</Button>
        </DialogContent>
      </Dialog>

      <div className="rounded-2xl border border-border bg-white overflow-hidden mt-5" data-testid="vip-list">
        <div className="px-4 py-3 bg-[#7A241F]/10 flex items-center justify-between">
          <h3 className="font-serif-display text-xl text-[#7A241F]">Daftar Tiket VIP</h3>
          <span className="text-sm font-semibold text-[#7A6A5E]">{list.length} tiket</span>
        </div>
        {/* Mobile: kartu */}
        <div className="md:hidden divide-y divide-border">
          {list.length === 0 ? (
            <p className="px-4 py-8 text-center text-[#7A6A5E] text-sm">Belum ada tiket VIP.</p>
          ) : list.map((o) => (
            <div key={o.id} className="p-3.5 space-y-1.5" data-testid={`vip-card-${o.order_no}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-[#2C1E16] truncate">{o.name}</p>
                  <p className="font-mono text-[10px] text-[#7A6A5E]">#{o.order_no}</p>
                </div>
                <span className="shrink-0 rounded-full bg-[#7A241F]/10 px-2.5 py-1 text-xs font-bold text-[#7A241F]">{o.qty} kursi</span>
              </div>
              <p className="text-sm text-[#5B4636]"><span className="font-medium">{SESSIONS_STATIC.find((s) => s.id === o.session_id)?.name}</span> · <span className="break-words">{o.seats?.join(", ")}</span></p>
              {o.note ? <p className="text-xs text-[#7A6A5E]">Catatan: {o.note}</p> : null}
              <div className="flex gap-2 pt-1">
                <button onClick={() => openEdit({ ...o })} className="flex-1 inline-flex items-center justify-center gap-1 h-9 rounded-lg bg-[#2F703E]/10 text-[#2F703E] text-sm font-medium"><Pencil className="h-4 w-4" /> Edit Kursi</button>
                <button onClick={() => delItem(o)} className="inline-flex items-center justify-center h-9 px-4 rounded-lg bg-[#EF4444]/10 text-[#EF4444] text-sm font-medium"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
        {/* Desktop: tabel */}
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[#7A6A5E] text-xs">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Nama</th>
                <th className="px-3 py-2 text-left font-medium">Sesi / Kursi</th>
                <th className="px-3 py-2 text-left font-medium">Catatan</th>
                <th className="px-3 py-2 text-right font-medium">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr><td colSpan={4} className="px-3 py-8 text-center text-[#7A6A5E]">Belum ada tiket VIP.</td></tr>
              ) : list.map((o) => (
                <tr key={o.id} className="border-t border-border" data-testid={`vip-row-${o.order_no}`}>
                  <td className="px-3 py-2 font-medium text-[#2C1E16]">{o.name}<span className="block font-mono text-[10px] text-[#7A6A5E]">#{o.order_no}</span></td>
                  <td className="px-3 py-2 text-[#5B4636] align-top"><span className="block font-medium">{SESSIONS_STATIC.find((s) => s.id === o.session_id)?.name}</span><span className="text-[13px] leading-snug break-words">{o.seats?.join(", ")} <span className="text-[10px] text-[#7A6A5E]">({o.qty} kursi)</span></span></td>
                  <td className="px-3 py-2 text-[#7A6A5E]">{o.note || "—"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => openEdit({ ...o })} data-testid={`vip-edit-${o.order_no}`} className="inline-flex items-center gap-1 h-8 px-2 rounded-lg text-[#2F703E] hover:bg-[#2F703E]/10 text-xs font-medium"><Pencil className="h-3.5 w-3.5" /> Edit Kursi</button>
                    <button onClick={() => delItem(o)} data-testid={`vip-del-${o.order_no}`} className="inline-flex items-center gap-1 h-8 px-2 rounded-lg text-[#EF4444] hover:bg-[#EF4444]/10 text-xs font-medium"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!edit} onOpenChange={() => { if (!busy) { setEdit(null); setEditMap(null); } }}>
        <DialogContent data-testid="vip-edit-dialog" className="w-[calc(100vw-1rem)] max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#7A241F]">Edit Tiket VIP</DialogTitle>
          </DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div>
                <Label>Nama Tamu VIP</Label>
                <Input data-testid="vip-edit-name" value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label>Catatan <span className="text-[#9CA3AF]">(opsional)</span></Label>
                <Input data-testid="vip-edit-note" value={edit.note || ""} onChange={(e) => setEdit({ ...edit, note: e.target.value })} className="mt-1.5" />
              </div>
              <div className="rounded-lg bg-muted/40 p-2.5">
                <p className="text-xs text-[#7A6A5E]">Sesi: <b>{SESSIONS_STATIC.find((s) => s.id === edit.session_id)?.name}</b></p>
                <p className="text-xs text-[#7A6A5E] mt-1">Kursi terpilih ({editSeats.length}):</p>
                <p className="text-sm font-semibold text-[#7A241F] break-words" data-testid="vip-edit-selected">{editSeats.length ? editSeats.join(", ") : "— belum ada —"}</p>
              </div>
              <p className="text-xs text-[#7A6A5E]">Klik kursi untuk mengganti: hijau/pilih kursi baru, klik kursi terpilih untuk melepasnya.</p>
              {editLoading ? (
                <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#B26A1E]" /></div>
              ) : editMap ? (
                <div className="w-full overflow-x-auto -mx-1 px-1">
                  <SeatMap rows={editMap.rows} selected={editSeats} onToggle={toggleEdit} couples={editCouples} allowDisability
                    aisleRow={editMap.aisle_row} doorRow={editMap.door_row} reverse={editMap.reverse} />
                </div>
              ) : (
                <p className="text-center text-sm text-[#7A6A5E] py-10">Gagal memuat peta kursi.</p>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => { setEdit(null); setEditMap(null); }} disabled={busy} className="flex-1">Batal</Button>
            <Button onClick={saveEdit} disabled={busy || editLoading} className="flex-1 bg-[#7A241F] hover:bg-[#5E1B17]" data-testid="vip-edit-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SetoranPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);
  const load = async () => {
    try {
      const params = {};
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      const r = await adminApi.get("/admin/cash-settlement", { params });
      setData(r.data);
    } catch { toast.error("Gagal memuat data setoran"); }
  };
  useEffect(() => { load(); }, [dateFrom, dateTo]);
  const receive = async (seller) => {
    if (!window.confirm(`Catat: sudah TERIMA semua setoran cash dari ${seller}? Aksi ini menandai kas petugas sudah disetor ke Bendahara.`)) return;
    setBusy(seller);
    try {
      const body = { seller, ...(dateFrom && { from: dateFrom }), ...(dateTo && { to: dateTo }) };
      const r = await adminApi.post("/admin/cash-settlement/receive", body);
      toast.success(`Setoran ${seller} ${rupiah(r.data.amount)} tercatat`); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mencatat setoran"); }
    setBusy("");
  };
  const cancelSettlement = async (st) => {
    if (!window.confirm(`Batalkan setoran ${st.seller} sebesar ${rupiah(st.amount)}? ${st.count} order akan kembali berstatus BELUM disetor.`)) return;
    setBusy("cancel-" + st.id);
    try {
      await adminApi.delete(`/admin/cash-settlement/${st.id}`);
      toast.success(`Setoran ${st.seller} dibatalkan`); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membatalkan setoran"); }
    setBusy("");
  };
  const exportExcel = async () => {
    setExporting(true);
    try {
      const params = { responseType: "blob" };
      if (dateFrom || dateTo) params.params = { ...(dateFrom && { from: dateFrom }), ...(dateTo && { to: dateTo }) };
      const res = await adminApi.get("/admin/cash-settlement/export", params);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "setoran_kas.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal export Excel"); }
    setExporting(false);
  };
  if (!data) return <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#B26A1E]" /></div>;
  return (
    <div className="no-print" data-testid="setoran-panel">
      <div className="rounded-2xl border border-[#2F703E]/25 bg-gradient-to-br from-[#2F703E]/[0.06] to-transparent p-4 mb-4">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <h2 className="font-serif-display text-2xl text-[#255E33] flex items-center gap-2"><Wallet className="h-5 w-5 text-[#2F703E]" /> Setoran Kas ke Bendahara</h2>
            <p className="text-sm text-[#7A6A5E]">Hanya CASH (Pre-Order + Walk-in + Order Manual). QRIS/Transfer langsung ke rekening. Klik "Terima Setoran" saat petugas sudah menyerahkan uang cash ke Chelyn.</p>
          </div>
          <Button size="sm" variant="outline" onClick={exportExcel} disabled={exporting}
            data-testid="setoran-export-btn" className="border-[#2F703E]/40 text-[#255E33] hover:bg-[#2F703E]/10">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} Export Excel
          </Button>
        </div>
      </div>
      {data.grand_total && (
        <div className="rounded-2xl border-2 border-[#2F703E]/40 bg-white p-4 mb-4" data-testid="setoran-grand-total">
          <p className="text-[11px] uppercase font-bold text-[#255E33] tracking-wide mb-2">Ringkasan Kas Seluruh Acara</p>
          <div className="grid grid-cols-3 gap-3">
            <div><p className="text-[11px] text-[#7A6A5E]">Total Kas Cash</p><p className="text-xl font-bold text-[#2C1E16]" data-testid="grand-collected">{rupiah(data.grand_total.collected)}</p></div>
            <div><p className="text-[11px] text-[#255E33]">Sudah Disetor</p><p className="text-xl font-bold text-[#2F703E]" data-testid="grand-deposited">{rupiah(data.grand_total.deposited)}</p></div>
            <div><p className="text-[11px] text-[#7A241F]">Belum Disetor</p><p className="text-xl font-bold text-[#7A241F]" data-testid="grand-outstanding">{rupiah(data.grand_total.outstanding)}</p></div>
          </div>
        </div>
      )}
      <div className="rounded-xl border border-border bg-white p-3 mb-4 flex flex-wrap items-end gap-3" data-testid="setoran-daterange">
        <div>
          <label className="block text-[11px] text-[#7A6A5E] mb-1">Dari tanggal</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            data-testid="setoran-date-from" className="text-sm rounded-lg border border-border px-2 py-1.5" />
        </div>
        <div>
          <label className="block text-[11px] text-[#7A6A5E] mb-1">Sampai tanggal</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            data-testid="setoran-date-to" className="text-sm rounded-lg border border-border px-2 py-1.5" />
        </div>
        <button onClick={() => { const t = new Date().toISOString().slice(0, 10); setDateFrom(t); setDateTo(t); }}
          data-testid="setoran-preset-today" className="text-xs rounded-full px-3 py-1.5 border border-border bg-white text-[#7A6A5E] hover:border-[#2F703E]/50">Hari ini</button>
        <button onClick={() => { setDateFrom("2026-09-13"); setDateTo("2026-09-13"); }}
          data-testid="setoran-preset-event" className="text-xs rounded-full px-3 py-1.5 border border-border bg-white text-[#7A6A5E] hover:border-[#2F703E]/50">Hari Acara (13 Sep)</button>
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(""); setDateTo(""); }}
            data-testid="setoran-preset-all" className="text-xs rounded-full px-3 py-1.5 border border-[#B26A1E]/40 bg-white text-[#7A241F] hover:border-[#B26A1E]">Semua tanggal ✕</button>
        )}
        {(dateFrom || dateTo) && (
          <span className="text-[11px] text-[#255E33] font-medium ml-auto" data-testid="setoran-daterange-info">
            Menampilkan {dateFrom || "awal"} s/d {dateTo || "akhir"}
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="rounded-xl border border-border bg-white p-3"><p className="text-[11px] text-[#7A6A5E] uppercase font-semibold">Total Cash</p><p className="text-lg font-bold text-[#2C1E16]">{rupiah(data.total_collected)}</p></div>
        <div className="rounded-xl border border-[#2F703E]/30 bg-white p-3"><p className="text-[11px] text-[#255E33] uppercase font-semibold">Sudah Disetor</p><p className="text-lg font-bold text-[#2F703E]">{rupiah(data.total_deposited)}</p></div>
        <div className="rounded-xl border border-[#7A241F]/30 bg-white p-3"><p className="text-[11px] text-[#7A241F] uppercase font-semibold">Belum Disetor</p><p className="text-lg font-bold text-[#7A241F]">{rupiah(data.total_outstanding)}</p></div>
      </div>
      {data.sellers.length === 0 && <p className="text-sm text-[#7A6A5E]">Belum ada penjualan cash.</p>}
      {data.sellers.map((s) => (
        <div key={s.seller} className="rounded-xl border border-border bg-white p-4 mb-3" data-testid="setoran-seller">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="font-semibold text-[#2C1E16]">{s.seller}</p>
              <p className="text-xs text-[#7A6A5E]">{s.tickets} tiket · {s.count} order cash · sudah disetor {rupiah(s.deposited)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-[#7A6A5E]">Belum disetor</p>
              <p className="text-lg font-bold text-[#7A241F]">{rupiah(s.outstanding)}</p>
            </div>
            <Button size="sm" disabled={s.outstanding <= 0 || busy === s.seller} onClick={() => receive(s.seller)}
              data-testid="setoran-receive-btn" className="bg-[#2F703E] hover:bg-[#255E33]">
              {busy === s.seller ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} {s.outstanding > 0 ? "Terima Setoran" : "Lunas Disetor"}
            </Button>
          </div>
          <div className="mt-2 border-t border-border pt-2">
            <p className="text-[11px] text-[#7A6A5E] mb-1">Sumber kas:</p>
            <div className="flex flex-wrap gap-2 mb-2" data-testid="setoran-sources">
              {(s.sources || []).map((src) => (
                <span key={src.source} className={cn("text-xs rounded-md px-2 py-1 border",
                  src.source === "Pre-Order" ? "bg-[#2F703E]/10 border-[#2F703E]/30 text-[#255E33]" :
                  src.source === "Walk-in" ? "bg-[#B26A1E]/10 border-[#B26A1E]/30 text-[#7A241F]" :
                  src.source === "Order Manual" ? "bg-[#7A241F]/10 border-[#7A241F]/30 text-[#7A241F]" :
                  "bg-[#F3E9DD]/60 border-border text-[#5B4636]")}>
                  {src.source}: <b>{rupiah(src.cash)}</b> · {src.tickets} tiket
                </span>
              ))}
            </div>
            <p className="text-[11px] text-[#7A6A5E] mb-1">Rincian per tanggal:</p>
            <div className="flex flex-wrap gap-2">
              {s.days.map((d) => (
                <span key={d.date} className="text-xs rounded-md bg-[#F3E9DD]/60 px-2 py-1 text-[#5B4636]">{d.date}: <b>{d.tickets} tiket</b> · {rupiah(d.cash)}</span>
              ))}
            </div>
          </div>
        </div>
      ))}
      {data.settlements.length > 0 && (
        <div className="mt-6">
          <h3 className="font-serif-display text-lg text-[#7A241F] mb-2">Riwayat Setoran (Closing)</h3>
          <div className="space-y-1.5">
            {data.settlements.map((st) => (
              <div key={st.id} className="flex justify-between items-center gap-2 text-sm border border-border rounded-lg px-3 py-2 bg-white" data-testid="setoran-history-row">
                <span><b>{st.seller}</b> · {(st.created_at || "").slice(0, 16).replace("T", " ")} WIB</span>
                <div className="flex items-center gap-2">
                  <span className="text-[#2F703E] font-semibold">{rupiah(st.amount)} <span className="text-[#7A6A5E] font-normal">({st.count} order · diterima {st.received_by})</span></span>
                  <Button size="sm" variant="ghost" disabled={busy === "cancel-" + st.id} onClick={() => cancelSettlement(st)}
                    data-testid="setoran-cancel-btn" className="text-[#7A241F] hover:bg-[#7A241F]/10 h-7 px-2">
                    {busy === "cancel-" + st.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><XCircle className="h-3.5 w-3.5 mr-1" />Batalkan</>}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ManualPanel() {
  const [sessionId, setSessionId] = useState(1);
  const [mapData, setMapData] = useState(null);
  const [loadingMap, setLoadingMap] = useState(true);
  const [selected, setSelected] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState("");
  const [paid, setPaid] = useState(false);
  const [tgl, setTgl] = useState("");
  const [tfAmount, setTfAmount] = useState("");
  const [proof, setProof] = useState(null);
  const [note, setNote] = useState("");
  const [method, setMethod] = useState("cash");
  const [ucode, setUcode] = useState(0);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [editProof, setEditProof] = useState(null);
  const [editSeats, setEditSeats] = useState([]);
  const [editMap, setEditMap] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [waitlist, setWaitlist] = useState([]);
  const [fromWaitlistId, setFromWaitlistId] = useState(null);
  const pollRef = useRef(null);

  const loadMap = useCallback(async (sid, showLoad) => {
    if (showLoad) setLoadingMap(true);
    try { const { data } = await adminApi.get(`/sessions/${sid}/seats`); setMapData(data); }
    catch (e) { console.error("Gagal memuat peta kursi manual:", e); }
    setLoadingMap(false);
  }, []);
  const loadList = useCallback(async () => {
    try { const { data } = await adminApi.get("/admin/manual"); setList(data || []); }
    catch (e) { console.error("Gagal memuat order manual:", e); }
  }, []);
  const loadWaitlist = useCallback(async () => {
    try { const { data } = await adminApi.get("/admin/waitlist"); setWaitlist(data?.entries || []); }
    catch (e) { console.error("Gagal memuat daftar tunggu:", e); }
  }, []);

  useEffect(() => {
    setSelected([]);
    loadMap(sessionId, true);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => loadMap(sessionId, false), 8000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [sessionId, loadMap]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadWaitlist(); }, [loadWaitlist]);

  const pickFromWaitlist = (w) => {
    setName(w.name || "");
    setPhone(w.phone || "");
    setNote(`Dari Daftar Tunggu — minta ${w.qty || 1} tiket`);
    setFromWaitlistId(w.id);
    setSelected([]);
    toast.success(`Data "${w.name}" dimuat. Silakan pilih kursi & buat order.`);
    setTimeout(() => document.querySelector('[data-testid="manual-name"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  };

  const couples = mapData?.couples || {};
  const toggle = (seat) => {
    setSelected((prev) => {
      const partner = couples[seat];
      if (prev.includes(seat)) return prev.filter((s) => s !== seat && s !== partner);
      const add = partner ? [seat, partner] : [seat];
      return [...new Set([...prev, ...add])];
    });
  };
  const onProof = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setProof(null); return; }
    compressImage(f).then(setProof).catch(() => toast.error("Gagal memproses gambar"));
  };

  const submit = async () => {
    if (!name.trim()) { toast.error("Isi nama pembeli"); return; }
    if (selected.length === 0) { toast.error("Pilih minimal 1 kursi"); return; }
    if (paid && (!amount || parseInt(amount) <= 0)) { toast.error("Isi nominal dana"); return; }
    if (paid && method !== "cash" && !proof) { toast.error("Foto / upload bukti struk dulu"); return; }
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/manual", {
        name, phone, session_id: sessionId, seats: selected,
        amount: paid ? parseInt(amount) : 0, paid,
        payment_method: method, unique_code: (paid && method !== "cash") ? ucode : 0,
        proof_image: (paid && method !== "cash") ? (proof || null) : null,
        note,
      });
      setResult(data);
      if (fromWaitlistId) {
        try { await adminApi.delete(`/admin/waitlist/${fromWaitlistId}`); } catch (e) { console.error("hapus daftar tunggu:", e); }
        setFromWaitlistId(null); loadWaitlist();
      }
      setName(""); setPhone(""); setAmount(""); setPaid(false); setMethod("cash"); setUcode(0); setTgl(""); setTfAmount(""); setProof(null); setNote(""); setSelected([]);
      loadMap(sessionId, false); loadList();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat order manual"); loadMap(sessionId, false); }
    setBusy(false);
  };

  const saveEdit = async () => {
    if (!edit) return;
    if (edit.paid && !edit.transfer_date) { toast.error("Isi tanggal transfer"); return; }
    if (editSeats.length === 0) { toast.error("Pilih minimal 1 kursi (atau hapus order)"); return; }
    setBusy(true);
    try {
      await adminApi.put(`/admin/manual/${edit.id}`, {
        name: edit.name,
        phone: edit.phone || "",
        amount: parseInt(edit.order_amount || edit.total_amount || 0, 10),
        paid: edit.paid,
        payment_method: edit.payment_method || edit.method || "transfer",
        transfer_date: edit.paid ? edit.transfer_date : null,
        transfer_amount: edit.paid && edit.transfer_amount ? parseInt(edit.transfer_amount, 10) : null,
        seats: editSeats,
        ...(editProof ? { proof_image: editProof } : {}),
      });
      toast.success("Order manual diperbarui");
      setEdit(null); setEditProof(null); setEditMap(null); loadList(); loadMap(sessionId, false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    setBusy(false);
  };
  const openEdit = async (o) => {
    setEdit({ ...o }); setEditProof(null); setEditSeats(o.seats || []); setEditMap(null); setEditLoading(true);
    try {
      const { data } = await adminApi.get(`/sessions/${o.session_id}/seats`);
      setEditMap(markEditableSeats(data, o.seats || []));
    } catch (e) { toast.error("Gagal memuat peta kursi"); }
    setEditLoading(false);
  };
  const editCouples = editMap?.couples || {};
  const toggleEdit = (seat) => {
    setEditSeats((prev) => {
      const partner = editCouples[seat];
      if (prev.includes(seat)) return prev.filter((s) => s !== seat && s !== partner);
      const add = partner ? [seat, partner] : [seat];
      return [...new Set([...prev, ...add])];
    });
  };
  const removeOrder = async (o) => {
    if (!window.confirm(`Hapus order manual "${o.name}" (${o.seats?.join(", ")})?`)) return;
    try {
      await adminApi.delete(`/admin/manual/${o.id}`);
      toast.success("Order manual dihapus");
      loadList(); loadMap(sessionId, false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus"); }
  };

  const fmtDateTime = (iso) => {
    try { return new Date(iso).toLocaleString("id-ID", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta" }) + " WIB"; }
    catch { return "-"; }
  };
  const saveTicket = async (o) => {
    const session = SESSIONS_STATIC.find((s) => s.id === o.session_id);
    const paid = !!o.paid;
    const by = o.created_by || o.seller || o.sold_by || "-";
    const W = 820;
    // Wrap nomor kursi (bisa banyak) + tinggi kanvas dinamis
    const seats = o.seats || [];
    const _m = document.createElement("canvas").getContext("2d");
    _m.font = "bold 25px Arial";
    const seatMaxW = 470;
    const seatLines = [];
    let _cur = "";
    seats.forEach((s) => {
      const t = _cur ? _cur + ", " + s : s;
      if (_m.measureText(t).width > seatMaxW && _cur) { seatLines.push(_cur); _cur = s; } else _cur = t;
    });
    if (_cur) seatLines.push(_cur);
    if (seatLines.length === 0) seatLines.push("-");
    // Wrap Nama panjang (rombongan) juga
    const wrapVal = (text, maxW) => {
      const words = String(text || "-").split(" ");
      const lines = []; let ln = "";
      words.forEach((w) => {
        const t = ln ? ln + " " + w : w;
        if (_m.measureText(t).width > maxW && ln) { lines.push(ln); ln = w; } else ln = t;
      });
      if (ln) lines.push(ln);
      return lines.length ? lines : ["-"];
    };
    const nameLines = wrapVal(o.name, 470);
    const H = 1180 + (seatLines.length - 1) * 38 + (nameLines.length - 1) * 38;
    const draw = (logoImg) => {
      const c = document.createElement("canvas"); c.width = W; c.height = H;
      const x = c.getContext("2d");
      x.fillStyle = "#7A241F"; x.fillRect(0, 0, W, H);
      x.fillStyle = "#FDFBF7"; x.fillRect(30, 30, W - 60, H - 60);
      x.fillStyle = "#B26A1E"; x.fillRect(30, 30, W - 60, 12);
      const cx = W / 2; x.textAlign = "center";
      let top = 130;
      if (logoImg) {
        try {
          const maxH = 92, maxW = 300;
          let lw = logoImg.naturalWidth || logoImg.width || 1, lh = logoImg.naturalHeight || logoImg.height || 1;
          const r = Math.min(maxW / lw, maxH / lh);
          lw = lw * r; lh = lh * r;
          x.drawImage(logoImg, cx - lw / 2, 52, lw, lh);
          top = 52 + lh + 40;
        } catch (e) { /* skip logo */ }
      }
      x.fillStyle = "#B26A1E"; x.font = "bold 26px Georgia"; x.fillText("E-TICKET · FILM DOKUMENTER", cx, top);
      x.fillStyle = "#7A241F"; x.font = "bold 38px Georgia"; x.fillText("ASHIN JINARAKKHITA", cx, top + 48);
      const wrap = (text, maxW) => {
        const words = text.split(" "); const lines = []; let line = "";
        words.forEach((w) => {
          const t = line ? line + " " + w : w;
          if (x.measureText(t).width > maxW && line) { lines.push(line); line = w; } else line = t;
        });
        if (line) lines.push(line);
        return lines;
      };
      x.fillStyle = "#8A6A3E"; x.font = "italic 17px Georgia";
      let sy = top + 80;
      wrap("Jejak Langkah Sang Pelopor Membangkitkan Kembali Dharma di Nusantara.", W - 200).forEach((ln) => { x.fillText(ln, cx, sy); sy += 24; });
      x.fillStyle = "#5B4636"; x.font = "18px Arial";
      x.fillText("Minggu, 13 September 2026", cx, sy + 10);
      x.fillText("CGV Grand Batam Mall", cx, sy + 36);
      let y0 = sy + 68;
      x.strokeStyle = "#B26A1E"; x.setLineDash([8, 6]); x.beginPath(); x.moveTo(70, y0); x.lineTo(W - 70, y0); x.stroke(); x.setLineDash([]);
      const rows = [
        ["No. Order", `#${o.order_no}`],
        ["Nama", null],
        ["Sesi", `${session?.name || "-"} · ${session?.time || ""}`],
        ["Nomor Kursi", null],
        ["Jumlah", `${o.qty} tiket`],
        ["Tgl & Jam Pesan", fmtDateTime(o.created_at)],
        ["Diinput oleh", by],
      ];
      if (paid) rows.push(["Status Pembayaran", rupiah(o.total_amount)]);
      let y = y0 + 54; x.textAlign = "left";
      rows.forEach(([k, v]) => {
        x.fillStyle = "#7A6A5E"; x.font = "20px Arial"; x.fillText(k, 80, y);
        if (k === "Nomor Kursi" || k === "Nama") {
          const lines = k === "Nama" ? nameLines : seatLines;
          x.fillStyle = "#2C1E16"; x.font = "bold 25px Arial"; x.textAlign = "right";
          lines.forEach((ln, i) => { x.fillText(ln, W - 80, y + i * 38); });
          x.textAlign = "left"; y += 60 + (lines.length - 1) * 38;
        } else {
          x.fillStyle = k === "Status Pembayaran" ? (paid ? "#255E33" : "#8A3A12") : "#2C1E16";
          x.font = "bold 25px Arial"; x.textAlign = "right"; x.fillText(String(v).slice(0, 40), W - 80, y);
          x.textAlign = "left"; y += 60;
        }
      });
      x.textAlign = "center";
      x.fillStyle = "#7A241F"; x.font = "bold 30px Georgia"; x.fillText(`#${o.order_no}`, cx, y + 28);
      x.fillStyle = "#8A3A12"; x.font = "bold 21px Arial";
      x.fillText("Tunjukkan e-ticket ini kepada petugas di lokasi", cx, H - 108);
      x.fillText("untuk menukar tiket fisik (hardcopy) asli.", cx, H - 80);
      x.fillStyle = "#B26A1E"; x.font = "16px Arial";
      x.fillText("Keluarga Buddhayana Indonesia Prov. Kepulauan Riau", cx, H - 48);
      return c;
    };
    try {
      let logo = null;
      await new Promise((res) => { const img = new Image(); img.crossOrigin = "anonymous"; img.onload = () => { logo = img; res(); }; img.onerror = () => res(); img.src = LOGOS.kbi; });
      let canvas;
      try { canvas = draw(logo); } catch (e) { canvas = draw(null); }
      const fileName = `e-ticket-${o.order_no}.png`;
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const isIOS = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      const isMobile = isIOS || /Android/i.test(navigator.userAgent);
      // 1) Web Share API HANYA di HP (iPhone/Android) -> muncul "Simpan ke Foto". Desktop dilewati.
      if (isMobile && blob && navigator.canShare) {
        try {
          const file = new File([blob], fileName, { type: "image/png" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: `E-Ticket #${o.order_no}` });
            toast.success("E-ticket siap dibagikan/disimpan");
            return;
          }
        } catch (err) { if (err?.name === "AbortError") return; /* user batal */ }
      }
      const url = blob ? URL.createObjectURL(blob) : canvas.toDataURL("image/png");
      // 2) iOS: buka gambar di tab baru -> tekan lama -> "Simpan ke Foto"
      if (isIOS) {
        window.open(url, "_blank");
        toast.info("Tekan lama gambar lalu pilih \u201CSimpan ke Foto\u201D", { duration: 6000 });
        return;
      }
      // 3) Desktop / Android: download langsung
      const a = document.createElement("a"); a.href = url; a.download = fileName;
      document.body.appendChild(a); a.click(); a.remove();
      if (blob) setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("E-ticket tersimpan sebagai gambar");
    } catch (e) { console.error("save ticket:", e); toast.error("Gagal membuat e-ticket"); }
  };


  const preorders = list.filter((o) => o.preorder);
  const listSess = list.filter((o) => o.session_id === sessionId);
  const sessName = SESSIONS_STATIC.find((s) => s.id === sessionId)?.name || `Sesi ${sessionId}`;
  const poSudah = preorders.filter((o) => o.paid || o.status === "verified");
  const poMenunggu = preorders.filter((o) => !o.paid && o.status === "waiting_verification");
  const poBelum = preorders.filter((o) => !o.paid && o.status !== "waiting_verification" && o.status !== "verified");
  const poDanaTotal = poSudah.reduce((a, o) => a + (o.total_amount || o.order_amount || 0), 0);
  const poTiket = preorders.reduce((a, o) => a + (o.qty || (o.seats || []).length || 0), 0);
  const moSudah = list.filter((o) => o.paid || o.status === "verified");
  const moBelum = list.filter((o) => !(o.paid || o.status === "verified"));
  const moDanaTotal = moSudah.reduce((a, o) => a + (o.total_amount || o.order_amount || 0), 0);
  const moTiket = list.reduce((a, o) => a + (o.qty || (o.seats || []).length || 0), 0);

  return (
    <div className="no-print" data-testid="manual-panel">
      <div className="rounded-2xl border border-[#B26A1E]/25 bg-gradient-to-br from-[#B26A1E]/[0.07] to-transparent p-4 mb-4">
        <h2 className="font-serif-display text-2xl text-[#7A241F] flex items-center gap-2"><ClipboardList className="h-5 w-5 text-[#B26A1E]" /> Order Manual (Rombongan)</h2>
        <p className="text-sm text-[#7A6A5E]">Buat pesanan manual: pilih kursi (bebas), input nominal & status bayar. Kalau sudah bayar, isi tanggal & nominal transfer — otomatis masuk laporan Bendahara.</p>
        <button onClick={() => window.open("/denah", "_blank")} data-testid="admin-denah-btn" className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#B26A1E]/40 bg-white px-4 py-2 text-sm font-medium text-[#8A3A12] hover:bg-[#B26A1E]/5 transition-colors">
          <Printer className="h-4 w-4" /> Cetak Denah Kursi (PDF) — pilih per sesi
        </button>
      </div>

      {list.length > 0 && (
        <div className="rounded-2xl border border-[#B26A1E]/30 bg-gradient-to-br from-[#B26A1E]/[0.08] to-transparent p-4 mb-4" data-testid="manual-rekap">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif-display text-xl text-[#8A3A12] flex items-center gap-2"><Banknote className="h-4 w-4 text-[#B26A1E]" /> Rekap Dana Order Manual (termasuk Pre-Order)</h3>
            <span className="text-xs text-[#7A6A5E]">{list.length} order · {moTiket} tiket</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-[#2F703E]/30 bg-white p-3 col-span-2 sm:col-span-1" data-testid="manual-rekap-dana">
              <p className="text-[11px] font-semibold text-[#255E33] uppercase tracking-wide">Total Dana Terkumpul</p>
              <p className="font-serif-display text-2xl text-[#2F703E] mt-0.5 leading-tight">{rupiah(moDanaTotal)}</p>
              <p className="text-xs text-[#7A6A5E]">dari {moSudah.length} order sudah berdana</p>
            </div>
            <div className="rounded-xl border border-[#2F703E]/30 bg-white p-3" data-testid="manual-rekap-sudah">
              <p className="text-[11px] font-semibold text-[#255E33] uppercase tracking-wide">Sudah Berdana</p>
              <p className="text-2xl font-bold text-[#2F703E] mt-0.5">{moSudah.length}</p>
              <p className="text-xs text-[#7A6A5E]">order</p>
            </div>
            <div className="rounded-xl border border-[#7A241F]/25 bg-white p-3" data-testid="manual-rekap-belum">
              <p className="text-[11px] font-semibold text-[#7A241F] uppercase tracking-wide">Belum Berdana</p>
              <p className="text-2xl font-bold text-[#7A241F] mt-0.5">{moBelum.length}</p>
              <p className="text-xs text-[#7A6A5E]">order</p>
            </div>
          </div>
        </div>
      )}

      {preorders.length > 0 && (
        <div className="hidden" data-testid="preorder-rekap" />
      )}

      {waitlist.length > 0 && (
        <div className="rounded-2xl border border-[#7A241F]/30 bg-gradient-to-br from-[#7A241F]/[0.06] to-transparent p-4 mb-4" data-testid="manual-waitlist">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif-display text-xl text-[#7A241F] flex items-center gap-2"><Clock className="h-4 w-4 text-[#B26A1E]" /> Dari Daftar Tunggu</h3>
            <span className="text-xs text-[#7A6A5E]">{waitlist.length} pendaftar</span>
          </div>
          <p className="text-xs text-[#7A6A5E] mb-3">Klik "Pilih Kursi & Proses" untuk memuat data pendaftar ke form, lalu pilih kursi & buat ordernya. Setelah order dibuat, pendaftar otomatis hilang dari daftar ini.</p>
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {waitlist.map((w) => (
              <div key={w.id} className={cn("flex items-center justify-between gap-3 rounded-xl border bg-white p-3",
                fromWaitlistId === w.id ? "border-[#B26A1E] ring-2 ring-[#B26A1E]/30" : "border-border")}
                data-testid={`waitlist-entry-${w.id}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-[#2C1E16] truncate">{w.name}</p>
                  <p className="text-xs text-[#7A6A5E]">{w.phone || "—"} · minta <b>{w.qty || 1}</b> tiket</p>
                </div>
                <Button size="sm" onClick={() => pickFromWaitlist(w)} data-testid={`waitlist-pick-${w.id}`}
                  className="bg-[#B26A1E] hover:bg-[#8A3A12] text-white shrink-0">
                  <Armchair className="h-4 w-4 mr-1.5" /> Pilih Kursi & Proses
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_340px] gap-4">
        <div className="rounded-2xl border border-border bg-white p-4">
          <div className="flex flex-wrap gap-2 mb-3">
            {SESSIONS_STATIC.map((s) => (
              <button key={s.id} data-testid={`manual-session-${s.id}`} onClick={() => setSessionId(s.id)}
                className={cn("px-3 py-1.5 rounded-full text-sm font-medium border transition-colors",
                  sessionId === s.id ? "bg-[#B26A1E] text-white border-[#B26A1E]" : "bg-white text-[#7A6A5E] border-border hover:border-[#B26A1E]/50")}>
                {s.name} · {s.time}
              </button>
            ))}
          </div>
          {mapData && (
            <p className="text-sm mb-3" data-testid="manual-remaining">
              Sisa <b className="text-[#255E33]">{mapData.capacity - mapData.booked}</b> kursi · {mapData.booked}/{mapData.capacity} terisi
            </p>
          )}
          {loadingMap ? (
            <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#B26A1E]" /></div>
          ) : mapData ? (
            <SeatMap rows={mapData.rows} selected={selected} onToggle={toggle} couples={couples} allowDisability
              aisleRow={mapData.aisle_row} doorRow={mapData.door_row} reverse={mapData.reverse} />
          ) : (
            <p className="text-center text-sm text-[#7A6A5E] py-16">Gagal memuat peta kursi.</p>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-white p-4 h-fit lg:sticky lg:top-4 space-y-3">
          <div>
            <Label htmlFor="mname">Nama Pembeli <span className="text-[#EF4444]">*</span></Label>
            <Input id="mname" data-testid="manual-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="mis. Rombongan Vihara A" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="mphone">No HP <span className="text-[#9CA3AF]">(opsional)</span></Label>
            <Input id="mphone" data-testid="manual-phone" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ""))} placeholder="08xxx" className="mt-1.5" />
          </div>
          <div className="rounded-lg bg-muted/40 p-2.5">
            <p className="text-xs text-[#7A6A5E]">Kursi dipilih ({selected.length}):</p>
            <p className="text-sm font-semibold text-[#7A241F] break-words" data-testid="manual-selected">{selected.length ? selected.join(", ") : "— belum ada —"}</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" data-testid="manual-belum" onClick={() => setPaid(false)}
              className={cn("rounded-xl border px-3 py-2.5 text-sm font-semibold", !paid ? "border-[#B26A1E] bg-[#B26A1E]/10 text-[#8A3A12] ring-2 ring-[#B26A1E]/30" : "border-border bg-white text-[#7A6A5E]")}>Belum Bayar</button>
            <button type="button" data-testid="manual-sudah" onClick={() => setPaid(true)}
              className={cn("rounded-xl border px-3 py-2.5 text-sm font-semibold", paid ? "border-[#2F703E] bg-[#2F703E]/10 text-[#255E33] ring-2 ring-[#2F703E]/30" : "border-border bg-white text-[#7A6A5E]")}>Sudah Bayar</button>
          </div>

          {paid ? (
            <>
              <div>
                <Label>Nominal Dana</Label>
                <div className="relative mt-1.5">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#7A6A5E]">Rp</span>
                  <Input data-testid="manual-amount" inputMode="numeric" value={amount ? parseInt(amount).toLocaleString("id-ID") : ""} onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ""))} placeholder="0" className="pl-9" />
                </div>
              </div>
              <div>
                <Label>Metode Pembayaran</Label>
                <div className="grid grid-cols-3 gap-2 mt-1.5">
                  {["cash", "qris", "transfer"].map((k) => (
                    <button key={k} type="button" data-testid={`manual-pay-${k}`} onClick={() => { setMethod(k); setUcode(k === "cash" ? 0 : Math.floor(100 + Math.random() * 900)); }}
                      className={cn("rounded-xl border p-2 text-center text-xs font-medium capitalize", method === k ? "border-[#B26A1E] bg-[#B26A1E]/5 ring-2 ring-[#B26A1E]/30 text-[#8A3A12]" : "border-border text-[#7A6A5E]")}>{k}</button>
                  ))}
                </div>
              </div>
              {method !== "cash" && (
                <div className="rounded-lg border border-[#B26A1E]/30 bg-white p-3">
                  {amount > 0 && (
                    <div className="mb-3 rounded-lg bg-[#7A241F]/10 border border-[#7A241F]/30 p-2.5" data-testid="manual-total-unik">
                      <p className="text-[11px] text-[#7A6A5E]">Total transfer (termasuk kode unik <b>{ucode}</b>):</p>
                      <p className="text-lg font-bold text-[#7A241F]">{rupiah(parseInt(amount) + ucode)}</p>
                      <p className="text-[10px] text-[#7A6A5E]">Minta pembeli transfer PAS sejumlah ini.</p>
                    </div>
                  )}
                  {method === "qris" ? (
                    <div className="text-center">
                      <p className="text-xs font-semibold text-[#7A241F] mb-2">Scan QRIS</p>
                      <img src={LOGOS.qris} alt="QRIS" className="mx-auto w-full max-w-[180px] rounded-lg border border-border" />
                    </div>
                  ) : (
                    <div className="rounded-lg bg-[#F3E9DD]/50 p-3 text-center">
                      <p className="text-xs text-[#7A6A5E]">BCA</p>
                      <p className="text-lg font-bold text-[#7A241F]">061 518 3381</p>
                      <p className="text-xs text-[#7A6A5E]">a.n. PD MBI Kepri</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <label data-testid="manual-photo" className="flex items-center justify-center gap-1.5 rounded-lg border border-[#B26A1E]/50 bg-white px-2 py-2 text-xs font-medium text-[#8A3A12] cursor-pointer hover:bg-[#F3E9DD]/40">
                      <Camera className="h-4 w-4" /> Ambil Foto
                      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onProof} />
                    </label>
                    <label data-testid="manual-upload" className="flex items-center justify-center gap-1.5 rounded-lg border border-[#B26A1E]/50 bg-white px-2 py-2 text-xs font-medium text-[#8A3A12] cursor-pointer hover:bg-[#F3E9DD]/40">
                      <UploadCloud className="h-4 w-4" /> Upload Struk
                      <input type="file" accept="image/*" className="hidden" onChange={onProof} />
                    </label>
                  </div>
                  {proof && <img src={proof} alt="bukti" className="rounded-lg border border-border max-h-28 object-contain mt-2 mx-auto" data-testid="manual-proof-preview" />}
                </div>
              )}
            </>
          ) : (
            <p className="text-xs text-[#7A6A5E] rounded-lg bg-[#B26A1E]/[0.07] border border-[#B26A1E]/20 p-2.5">
              Order dibuat status <b>Belum Bayar</b>. Nominal & bukti transfer bisa diisi nanti lewat tombol <b>Edit</b> saat pembeli sudah berdana.
            </p>
          )}
          <Button onClick={submit} disabled={busy || selected.length === 0 || !name.trim() || (paid && (!amount || parseInt(amount) <= 0)) || (paid && method !== "cash" && !proof)} data-testid="manual-submit"
            className="w-full h-12 bg-[#B26A1E] hover:bg-[#8A3A12] text-base">
            {busy ? <Loader2 className="h-5 w-5 animate-spin mr-1.5" /> : <ClipboardList className="h-5 w-5 mr-1.5" />} {paid ? (method !== "cash" && !proof ? "Foto / Upload Bukti Dulu" : "Buat Tiket & Tampilkan E-Ticket") : "Buat Order Manual (Belum Bayar)"}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-white overflow-hidden mt-5" data-testid="manual-list">
        <div className="px-4 py-3 bg-[#B26A1E]/10 flex items-center justify-between">
          <h3 className="font-serif-display text-xl text-[#7A241F]">Daftar Order Manual — {sessName}</h3>
          <span className="text-sm font-semibold text-[#7A6A5E]">{listSess.length} order</span>
        </div>
        {/* Mobile: kartu */}
        <div className="md:hidden divide-y divide-border">
          {listSess.length === 0 ? (
            <p className="px-4 py-8 text-center text-[#7A6A5E] text-sm">Belum ada order manual untuk {sessName}.</p>
          ) : listSess.map((o) => (
            <div key={o.id} className="p-3.5 space-y-1.5" data-testid={`manual-card-${o.order_no}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <button onClick={() => saveTicket(o)} title="Klik untuk unduh E-Ticket" data-testid={`manual-name-ticket-${o.order_no}`} className="text-left font-semibold text-[#7A241F] underline decoration-dotted underline-offset-2 truncate hover:text-[#B26A1E]">{o.name}</button>
                  <p className="font-mono text-[10px] text-[#7A6A5E]">#{o.order_no}</p>
                </div>
                <span className="shrink-0 rounded-full bg-[#7A241F]/10 px-2.5 py-1 text-xs font-bold text-[#7A241F]">{o.qty} tiket</span>
              </div>
              <p className="text-sm text-[#5B4636]"><span className="font-medium">{SESSIONS_STATIC.find((s) => s.id === o.session_id)?.name}</span> · <span className="break-words">{o.seats?.join(", ")}</span></p>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className={cn("text-[10px] px-2 py-0.5 rounded-full font-medium", o.paid ? "bg-[#2F703E]/15 text-[#255E33]" : "bg-[#EF4444]/15 text-[#B91C1C]")}>{o.paid ? "Sudah Bayar" : "Belum Bayar"}</span>
                <span className="font-semibold text-[#7A241F]">{rupiah(o.paid ? o.total_amount : (o.order_amount || o.total_amount))}</span>
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={() => saveTicket(o)} className="inline-flex items-center justify-center gap-1 h-9 px-3 rounded-lg bg-[#B26A1E]/10 text-[#B26A1E] text-sm font-medium"><Ticket className="h-4 w-4" /> E-Ticket</button>
                {o.phone ? <button onClick={() => sendManualWA(o)} data-testid={`manual-wa-${o.order_no}`} className="inline-flex items-center justify-center gap-1 h-9 px-3 rounded-lg bg-[#25D366]/15 text-[#1E8A4C] text-sm font-medium"><MessageCircle className="h-4 w-4" /> Kirim WA</button> : null}
                <button onClick={() => openEdit(o)} className="flex-1 inline-flex items-center justify-center gap-1 h-9 rounded-lg bg-[#2F703E]/10 text-[#2F703E] text-sm font-medium"><Pencil className="h-4 w-4" /> Edit</button>
                <button onClick={() => removeOrder(o)} className="inline-flex items-center justify-center h-9 px-4 rounded-lg bg-[#EF4444]/10 text-[#EF4444] text-sm font-medium"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
        {/* Desktop: tabel */}
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[#7A6A5E] text-xs">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Nama</th>
                <th className="px-3 py-2 text-center font-medium">Tiket</th>
                <th className="px-3 py-2 text-left font-medium">Sesi / Kursi</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-left font-medium">Tgl Order / Transfer</th>
                <th className="px-3 py-2 text-right font-medium">Nominal</th>
                <th className="px-3 py-2 text-right font-medium">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {listSess.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-[#7A6A5E]">Belum ada order manual untuk {sessName}.</td></tr>
              ) : listSess.map((o) => (
                <tr key={o.id} className="border-t border-border" data-testid={`manual-row-${o.order_no}`}>
                  <td className="px-3 py-2 font-medium text-[#2C1E16]"><button onClick={() => saveTicket(o)} title="Klik untuk unduh E-Ticket" data-testid={`manual-nameclick-${o.order_no}`} className="text-left text-[#7A241F] underline decoration-dotted underline-offset-2 hover:text-[#B26A1E]">{o.name}</button><span className="block font-mono text-[10px] text-[#7A6A5E]">#{o.order_no}</span></td>
                  <td className="px-3 py-2 text-center font-bold text-[#7A241F] whitespace-nowrap">{o.qty}</td>
                  <td className="px-3 py-2 text-[#5B4636] align-top"><span className="block font-medium">{SESSIONS_STATIC.find((s) => s.id === o.session_id)?.name}</span><span className="text-[13px] leading-snug break-words">{o.seats?.join(", ")}</span></td>
                  <td className="px-3 py-2">
                    <span className={cn("text-[10px] px-2 py-0.5 rounded-full font-medium", o.paid ? "bg-[#2F703E]/15 text-[#255E33]" : "bg-[#EF4444]/15 text-[#B91C1C]")}>
                      {o.paid ? "Sudah Bayar" : "Belum Bayar"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[#5B4636] whitespace-nowrap"><span className="block text-[10px] text-[#7A6A5E]">order: {o.created_at ? new Date(o.created_at).toLocaleDateString("id-ID") : "-"}</span>transfer: {o.transfer_date || "-"}</td>
                  <td className="px-3 py-2 text-right font-semibold text-[#7A241F]">{rupiah(o.paid ? o.total_amount : (o.order_amount || o.total_amount))}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => saveTicket(o)} data-testid={`manual-ticket-${o.order_no}`} className="inline-flex items-center gap-1 h-8 px-2 rounded-lg text-[#B26A1E] hover:bg-[#B26A1E]/10 text-xs font-medium"><Ticket className="h-3.5 w-3.5" /> E-Ticket</button>
                    {o.phone ? <button onClick={() => sendManualWA(o)} data-testid={`manual-wa-${o.order_no}`} className="inline-flex items-center gap-1 h-8 px-2 rounded-lg text-[#1E8A4C] hover:bg-[#25D366]/15 text-xs font-medium"><MessageCircle className="h-3.5 w-3.5" /> WA</button> : null}
                    <button onClick={() => openEdit(o)} data-testid={`manual-edit-${o.order_no}`} className="inline-flex items-center gap-1 h-8 px-2 rounded-lg text-[#2F703E] hover:bg-[#2F703E]/10 text-xs font-medium"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                    <button onClick={() => removeOrder(o)} data-testid={`manual-del-${o.order_no}`} className="inline-flex items-center gap-1 h-8 px-2 rounded-lg text-[#EF4444] hover:bg-[#EF4444]/10 text-xs font-medium"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!result} onOpenChange={(o) => !o && setResult(null)}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="manual-result-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#2F703E]">{result?.paid ? "SUDAH BAYAR ✅" : "ORDER TERSIMPAN"}</DialogTitle>
          </DialogHeader>
          {result && (
            <div className="space-y-2.5">
              <p className="text-sm"><b className="text-[#2C1E16]">{result.name}</b> · #{result.order_no}</p>
              <p className="text-xs text-[#7A6A5E]">{(result.seats || []).join(", ")} · {result.paid ? rupiah(result.total_amount) : "Belum Bayar"}</p>
              <Button onClick={() => saveTicket(result)} className="w-full bg-[#B26A1E] hover:bg-[#8A3A12]" data-testid="manual-result-download"><Download className="h-4 w-4 mr-1.5" /> Download E-Ticket</Button>
              {result.phone ? <Button onClick={() => sendManualWA(result)} className="w-full bg-[#25D366] hover:bg-[#1EA952] text-white" data-testid="manual-result-wa"><MessageCircle className="h-4 w-4 mr-1.5" /> Kirim WhatsApp</Button> : null}
              <Button variant="outline" onClick={() => setResult(null)} className="w-full" data-testid="manual-result-ok">Selesai</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={() => { if (!busy) { setEdit(null); setEditProof(null); setEditMap(null); } }}>
        <DialogContent data-testid="manual-edit-dialog" className="w-[calc(100vw-1rem)] max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-xl sm:text-2xl text-[#7A241F]">Edit Order Manual</DialogTitle>
          </DialogHeader>
          {edit && (
            <div className="space-y-3 min-w-0">
              <div>
                <Label>Nama Pembeli</Label>
                <Input data-testid="manual-edit-name" value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label>No HP <span className="text-[#9CA3AF]">(opsional)</span></Label>
                <Input data-testid="manual-edit-phone" value={edit.phone || ""} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} className="mt-1.5" placeholder="08xxxxxxxxxx" />
              </div>
              <div>
                <Label>Nominal Order (Rp)</Label>
                <Input data-testid="manual-edit-amount" inputMode="numeric"
                  value={edit.order_amount ? Number(edit.order_amount).toLocaleString("id-ID") : (edit.total_amount ? Number(edit.total_amount).toLocaleString("id-ID") : "")}
                  onChange={(e) => setEdit({ ...edit, order_amount: e.target.value.replace(/[^0-9]/g, "") })} className="mt-1.5" />
              </div>
              <div>
                <Label className="mb-1.5 block">Status</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" data-testid="manual-edit-unpaid" onClick={() => setEdit({ ...edit, paid: false })}
                    className={cn("rounded-lg border px-3 py-2 text-sm font-medium", !edit.paid ? "border-[#EF4444] bg-[#EF4444]/10 text-[#B91C1C]" : "border-border bg-white text-[#7A6A5E]")}>Belum Bayar</button>
                  <button type="button" data-testid="manual-edit-paid" onClick={() => setEdit({ ...edit, paid: true })}
                    className={cn("rounded-lg border px-3 py-2 text-sm font-medium", edit.paid ? "border-[#2F703E] bg-[#2F703E]/10 text-[#255E33]" : "border-border bg-white text-[#7A6A5E]")}>Sudah Bayar</button>
                </div>
              </div>
              {edit.paid && (
                <div className="space-y-3 rounded-lg bg-[#2F703E]/5 border border-[#2F703E]/20 p-3">
                  <div>
                    <Label className="mb-1.5 block">Metode Pembayaran</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {["cash", "transfer", "qris"].map((m) => (
                        <button key={m} type="button" data-testid={`manual-edit-method-${m}`}
                          onClick={() => setEdit({ ...edit, payment_method: m })}
                          className={cn("rounded-lg border px-2 py-2 text-sm font-medium",
                            (edit.payment_method || edit.method || "transfer") === m
                              ? "border-[#2F703E] bg-[#2F703E]/10 text-[#255E33]"
                              : "border-border bg-white text-[#7A6A5E]")}>
                          {m === "cash" ? "Cash" : m === "qris" ? "QRIS" : "Transfer"}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-[#7A6A5E] mt-1">Pilih sesuai bukti yang diunggah (cash / transfer / QRIS).</p>
                  </div>
                  <div>
                    <Label>Tanggal Transfer</Label>
                    <Input type="date" data-testid="manual-edit-date" value={edit.transfer_date || ""} onChange={(e) => setEdit({ ...edit, transfer_date: e.target.value })} className="mt-1.5" />
                  </div>
                  <div>
                    <Label>Nominal Transfer (Rp)</Label>
                    <Input data-testid="manual-edit-tf" inputMode="numeric"
                      value={edit.transfer_amount ? Number(edit.transfer_amount).toLocaleString("id-ID") : ""}
                      onChange={(e) => setEdit({ ...edit, transfer_amount: e.target.value.replace(/[^0-9]/g, "") })} placeholder="kosong = pakai nominal order" className="mt-1.5" />
                  </div>
                  <div>
                    <Label className="block mb-1.5">Foto Bukti Transfer <span className="text-[#9CA3AF]">(opsional)</span></Label>
                    <input type="file" accept="image/*" data-testid="manual-edit-proof" className="text-xs"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) { setEditProof(null); return; }
                        compressImage(f).then(setEditProof).catch(() => toast.error("Gagal memproses gambar"));
                      }} />
                    {editProof
                      ? <p className="text-[11px] text-[#255E33] mt-1">Foto baru siap diunggah ✓</p>
                      : <p className="text-[11px] text-[#7A6A5E] mt-1">Kosongkan bila tidak ingin mengubah bukti yang sudah ada.</p>}
                  </div>
                </div>
              )}
              <div className="rounded-lg bg-muted/40 p-2.5">
                <p className="text-xs text-[#7A6A5E]">Sesi: <b>{SESSIONS_STATIC.find((s) => s.id === edit.session_id)?.name}</b></p>
                <p className="text-xs text-[#7A6A5E] mt-1">Kursi terpilih ({editSeats.length}):</p>
                <p className="text-sm font-semibold text-[#7A241F] break-words" data-testid="manual-edit-selected">{editSeats.length ? editSeats.join(", ") : "— belum ada —"}</p>
              </div>
              <p className="text-xs text-[#7A6A5E]">Klik kursi terpilih untuk melepasnya, atau pilih kursi lain untuk menambah.</p>
              {editLoading ? (
                <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#B26A1E]" /></div>
              ) : editMap ? (
                <div className="w-full overflow-x-auto -mx-1 px-1">
                  <SeatMap rows={editMap.rows} selected={editSeats} onToggle={toggleEdit} couples={editCouples} allowDisability
                    aisleRow={editMap.aisle_row} doorRow={editMap.door_row} reverse={editMap.reverse} />
                </div>
              ) : (
                <p className="text-center text-sm text-[#7A6A5E] py-10">Gagal memuat peta kursi.</p>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => { setEdit(null); setEditProof(null); setEditMap(null); }} disabled={busy} className="flex-1">Batal</Button>
            <Button onClick={saveEdit} disabled={busy || editLoading} className="flex-1 bg-[#B26A1E] hover:bg-[#8A3A12]" data-testid="manual-edit-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function MasterlistPanel() {
  const [orders, setOrders] = useState([]);
  const [unpaidManual, setUnpaidManual] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [exportingType, setExportingType] = useState(null);
  const [exportingAll, setExportingAll] = useState(false);
  const [sessFilter, setSessFilter] = useState("all");
  const exportAll = async () => {
    setExportingAll(true);
    try {
      const res = await adminApi.get(`/admin/masterlist/export-all?proofs=1`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `masterlist-semua.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel semua tabel (dengan bukti transfer) berhasil diunduh");
    } catch (e) { console.error("masterlist export-all:", e); toast.error("Gagal export Excel gabungan"); }
    setExportingAll(false);
  };
  const exportTable = async (type) => {
    setExportingType(type);
    try {
      const res = await adminApi.get(`/admin/masterlist/export?type=${type}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `masterlist-${type}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("File Excel berhasil diunduh");
    } catch (e) { console.error("masterlist export:", e); toast.error("Gagal export Excel"); }
    setExportingType(null);
  };
  useEffect(() => {
    (async () => {
      try { const { data } = await adminApi.get("/admin/bendahara"); setOrders(data.orders || []); setUnpaidManual(data.unpaid_manual || []); }
      catch (e) { console.error("masterlist load:", e); toast.error("Gagal memuat masterlist"); }
      setLoading(false);
    })();
  }, []);
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#7A241F]" /></div>;
  const matchSess = (o) => sessFilter === "all" || o.session_name === sessFilter;
  const match = (o) => matchSess(o) && (!q || o.name?.toLowerCase().includes(q.toLowerCase()) || String(o.order_no).includes(q) || (o.phone || "").includes(q));
  const sessionNames = Array.from(new Set([...orders, ...unpaidManual].map((o) => o.session_name).filter(Boolean)))
    .sort((a, b) => String(a).localeCompare(String(b), "id", { numeric: true }));
  const umum = orders.filter((o) => o.channel !== "vip" && o.channel !== "manual" && match(o));
  const vip = orders.filter((o) => o.channel === "vip" && match(o));
  const manual = [...orders.filter((o) => o.channel === "manual"), ...unpaidManual].filter(match);
  const Table = ({ title, rows, kind }) => {
    const isVip = kind === "vip";
    const isManual = kind === "manual";
    const headBg = isVip ? "bg-[#7A241F]/10" : isManual ? "bg-[#B26A1E]/10" : "bg-[#2F703E]/10";
    const btnBg = isVip ? "bg-[#7A241F] hover:bg-[#5E1B17]" : isManual ? "bg-[#B26A1E] hover:bg-[#8A3A12]" : "bg-[#2F703E] hover:bg-[#255E33]";
    const Icon = isVip ? Crown : isManual ? ClipboardList : Users;
    const iconColor = isVip ? "text-[#B26A1E]" : isManual ? "text-[#B26A1E]" : "text-[#2F703E]";
    const nCols = isVip ? 4 : isManual ? 7 : 6;
    return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid={`masterlist-${kind}`}>
      <div className={cn("px-4 py-3 flex items-center justify-between gap-3", headBg)}>
        <h3 className="font-serif-display text-xl text-[#7A241F] flex items-center gap-2"><Icon className={cn("h-4 w-4", iconColor)} /> {title}</h3>
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-[#7A6A5E] whitespace-nowrap">{rows.length} pembeli</span>
          <button onClick={() => exportTable(kind)} disabled={exportingType !== null}
            data-testid={`masterlist-export-${kind}`}
            className={cn("inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold text-white transition-colors disabled:opacity-60", btnBg)}>
            {exportingType === kind ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Export Excel
          </button>
        </div>
      </div>
      {/* Mobile: kartu bertumpuk */}
      <div className="md:hidden divide-y divide-border">
        {rows.length === 0 ? (
          <p className="px-4 py-8 text-center text-[#7A6A5E] text-sm">Belum ada.</p>
        ) : rows.map((o, i) => (
          <div key={o.order_no ?? i} className="p-3.5 space-y-1.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-[#2C1E16] truncate">{o.name}</p>
                <p className="font-mono text-[10px] text-[#7A6A5E]">#{o.order_no}{isManual && o.seller ? ` · input: ${o.seller}` : ""}</p>
              </div>
              <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[#7A241F]/10 px-2.5 py-1 text-xs font-bold text-[#7A241F]">{o.tickets} tiket</span>
            </div>
            <p className="text-sm text-[#5B4636]"><span className="font-medium">{o.session_name}</span> · <span className="break-words">{o.seats.join(", ")}</span></p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#7A6A5E]">
              {o.phone && o.phone !== "-" ? <span>📱 {o.phone}</span> : null}
              {!isVip && !isManual ? <span className="rounded-full bg-[#7A241F]/10 text-[#7A241F] px-1.5 py-0.5 font-medium">{o.channel === "panitia" ? "Panitia" : "Website"}</span> : null}
              {isManual ? (o.paid === false
                ? <span className="rounded-full bg-[#B26A1E]/15 text-[#8A3A12] px-1.5 py-0.5 font-semibold">Belum Berdana</span>
                : <span className="rounded-full bg-[#2F703E]/15 text-[#255E33] px-1.5 py-0.5 font-semibold">Sudah Berdana</span>) : null}
              {isManual ? <span>order: {o.date}{o.transfer_date ? ` · transfer: ${o.transfer_date}` : ""}</span> : null}
            </div>
            {!isVip && <p className="text-right font-semibold text-[#7A241F]">{rupiah(o.amount)}</p>}
          </div>
        ))}
      </div>
      {/* Desktop: tabel */}
      <div className="overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[#7A6A5E] text-xs">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Nama</th>
              <th className="px-3 py-2 text-center font-medium">Tiket</th>
              <th className="px-3 py-2 text-left font-medium">No HP</th>
              <th className="px-3 py-2 text-left font-medium">Sesi / Kursi</th>
              {isManual && <th className="px-3 py-2 text-left font-medium">Status</th>}
              {isManual && <th className="px-3 py-2 text-left font-medium">Tgl Order / Transfer</th>}
              {!isVip && !isManual && <th className="px-3 py-2 text-left font-medium">Kanal</th>}
              {!isVip && <th className="px-3 py-2 text-right font-medium">Nominal</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={nCols} className="px-3 py-8 text-center text-[#7A6A5E]">Belum ada.</td></tr>
            ) : rows.map((o, i) => (
              <tr key={o.order_no ?? i} className="border-t border-border">
                <td className="px-3 py-2 font-medium text-[#2C1E16]">{o.name}<span className="block font-mono text-[10px] text-[#7A6A5E]">#{o.order_no}{isManual && o.seller ? ` · input: ${o.seller}` : ""}</span></td>
                <td className="px-3 py-2 text-center font-bold text-[#7A241F] whitespace-nowrap">{o.tickets}</td>
                <td className="px-3 py-2 text-[#5B4636]">{o.phone || "-"}</td>
                <td className="px-3 py-2 text-[#5B4636] align-top"><span className="block font-medium">{o.session_name}</span><span className="text-[13px] leading-snug break-words">{o.seats.join(", ")}</span></td>
                {isManual && <td className="px-3 py-2 whitespace-nowrap">{o.paid === false
                  ? <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-[#B26A1E]/15 text-[#8A3A12]">Belum Berdana</span>
                  : <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-[#2F703E]/15 text-[#255E33]">Sudah Berdana</span>}</td>}
                {isManual && <td className="px-3 py-2 text-[#5B4636] whitespace-nowrap"><span className="block text-[10px] text-[#7A6A5E]">order: {o.date}</span>transfer: {o.transfer_date || "-"}</td>}
                {!isVip && !isManual && <td className="px-3 py-2"><span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-[#7A241F]/10 text-[#7A241F]">{o.channel === "panitia" ? "Panitia" : "Website"}</span></td>}
                {!isVip && <td className="px-3 py-2 text-right font-semibold text-[#7A241F]">{o.paid === false ? <span className="text-[#8A3A12]">{rupiah(o.amount)}</span> : rupiah(o.amount)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    );
  };
  return (
    <div className="no-print space-y-5" data-testid="masterlist-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-serif-display text-2xl text-[#7A241F]">Masterlist Pembelian</h2>
          <p className="text-sm text-[#7A6A5E]">Daftar semua yang sudah beli — tabel VIP, Order Manual, & Website (online + panitia) terpisah, masing-masing bisa di-export Excel.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={exportAll} disabled={exportingAll} data-testid="masterlist-export-all"
            className="bg-[#255E33] hover:bg-[#1E4A29] h-9">
            {exportingAll ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
            Export Semua (1 File + Bukti)
          </Button>
          <select value={sessFilter} onChange={(e) => setSessFilter(e.target.value)}
            data-testid="masterlist-session-filter"
            className="text-sm border border-border rounded-lg px-2.5 py-2 bg-white text-[#2C1E16]">
            <option value="all">Semua Sesi</option>
            {sessionNames.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="relative min-w-[200px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama / HP / no order"
              data-testid="masterlist-search" className="w-full text-sm border border-border rounded-lg pl-8 pr-2 py-2 bg-white" />
          </div>
        </div>
      </div>
      <Table title="Tamu VIP" rows={vip} kind="vip" />
      <Table title="Order Manual" rows={manual} kind="manual" />
      <Table title="Pembeli Umum" rows={umum} kind="umum" />
    </div>
  );
}

const SESSIONS_STATIC = [
  { id: 1, name: "Sesi 1", time: "09.30–11.30" },
  { id: 2, name: "Sesi 2", time: "12.00–14.00" },
  { id: 3, name: "Sesi 3", time: "14.30–16.30" },
  { id: 4, name: "Sesi 4", time: "17.00–19.00" },
  { id: 5, name: "Sesi 5 (Tambahan)", time: "15.00–17.00" },
];

function SeatAuditDialog({ open, onOpenChange, isSuper }) {
  const [sid, setSid] = useState(5);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async (s) => {
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/admin/sessions/${s}/seat-audit`);
      setData(data);
    } catch { toast.error("Gagal memuat audit kursi"); }
    setLoading(false);
  };

  useEffect(() => { if (open) load(sid); /* eslint-disable-next-line */ }, [open, sid]);

  const cleanup = async () => {
    if (!window.confirm("Lepas semua kunci kursi 'basi' (yang tidak punya order aktif)? Order yang masih aktif TIDAK akan tersentuh.")) return;
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/admin/sessions/${sid}/release-stale-locks`);
      toast.success(data.removed > 0 ? `${data.removed} kunci basi dilepas: ${data.labels.join(", ")}` : "Tidak ada kunci basi — semua kursi terkunci punya order aktif");
      await load(sid);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal melepas kunci"); }
    setBusy(false);
  };

  const c = data?.counts || {};
  const activeLocked = (c.verified || 0) + (c.pending || 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="seat-audit-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#7A241F] flex items-center gap-2">
            <Armchair className="h-5 w-5 text-[#B26A1E]" /> Audit Kursi — Cek Denah
          </DialogTitle>
          <DialogDescription className="text-sm text-[#7A6A5E]">
            Melihat kursi yang <b>terkunci</b> (tampil "terisi" di denah) beserta ordernya. Denah menandai terisi untuk SEMUA reservasi — termasuk yang <b>belum bayar</b>.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {SESSIONS_STATIC.map((s) => (
            <button key={s.id} onClick={() => setSid(s.id)} data-testid={`seat-audit-sess-${s.id}`}
              className={cn("px-3 py-1.5 rounded-full text-sm font-medium border transition-colors",
                sid === s.id ? "bg-[#7A241F] text-white border-[#7A241F]" : "bg-white text-[#7A241F] border-[#B26A1E]/40 hover:bg-[#F3E9DD]")}>
              {s.name}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#B26A1E]" /></div>
        ) : data ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3" data-testid="seat-audit-counts">
              <div className="rounded-xl border border-border bg-[#FAF7F2] p-3">
                <p className="text-[11px] text-[#7A6A5E] uppercase tracking-wide">Kapasitas</p>
                <p className="text-xl font-bold text-[#2C1E16]">{data.capacity}</p>
              </div>
              <div className="rounded-xl border border-[#9CA3AF]/40 bg-[#9CA3AF]/10 p-3">
                <p className="text-[11px] text-[#4B5563] uppercase tracking-wide">Terisi di denah</p>
                <p className="text-xl font-bold text-[#374151]">{c.in_layout || 0}</p>
              </div>
              <div className="rounded-xl border border-[#2F703E]/30 bg-[#2F703E]/[0.06] p-3">
                <p className="text-[11px] text-[#255E33] uppercase tracking-wide">Sudah bayar</p>
                <p className="text-xl font-bold text-[#255E33]">{c.verified || 0}</p>
              </div>
              <div className="rounded-xl border border-[#B26A1E]/40 bg-[#B26A1E]/[0.07] p-3">
                <p className="text-[11px] text-[#8A3A12] uppercase tracking-wide">Belum bayar</p>
                <p className="text-xl font-bold text-[#8A3A12]">{c.pending || 0}</p>
              </div>
            </div>

            {(c.orphan > 0 || c.invalid_label > 0) && (
              <div className="rounded-xl border border-[#EF4444]/40 bg-[#EF4444]/[0.06] p-3 mb-3 text-sm text-[#B91C1C]">
                {c.orphan > 0 && <p><b>{c.orphan} kunci basi</b> (tanpa order aktif) — kursi ini tampil terisi padahal tidak ada pembelinya. Klik "Lepas Kunci Basi" untuk mengosongkannya.</p>}
                {c.invalid_label > 0 && <p className="mt-1">{c.invalid_label} kunci memakai label kursi lama (di luar denah baru) — perbaiki lewat banner "Ubah Kursi".</p>}
              </div>
            )}

            <p className="text-[13px] text-[#7A6A5E] mb-1.5">
              Total {c.total || 0} kursi terkunci = <b className="text-[#255E33]">{c.verified || 0} sudah bayar</b> + <b className="text-[#8A3A12]">{c.pending || 0} belum bayar</b>{c.orphan ? <> + <b className="text-[#B91C1C]">{c.orphan} basi</b></> : null}.
            </p>

            <div className="rounded-xl border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F3E9DD] text-[#7A241F]">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Kursi</th>
                    <th className="text-left px-3 py-2 font-semibold">No Order</th>
                    <th className="text-left px-3 py-2 font-semibold">Nama</th>
                    <th className="text-left px-3 py-2 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody data-testid="seat-audit-rows">
                  {data.locks.map((l, i) => (
                    <tr key={`${l.seat}-${i}`} className={cn("border-t border-border", l.orphan && "bg-[#EF4444]/[0.05]")}>
                      <td className="px-3 py-2 font-bold text-[#2C1E16]">
                        {l.seat}{!l.in_layout && <span className="ml-1 text-[10px] text-[#B91C1C]">(label lama)</span>}
                      </td>
                      <td className="px-3 py-2 text-[#5B4636]">{l.order_no || "—"}</td>
                      <td className="px-3 py-2 text-[#5B4636] break-words">{l.name || <span className="text-[#B91C1C] font-semibold">TANPA ORDER (basi)</span>}</td>
                      <td className="px-3 py-2">
                        {l.orphan ? <span className="text-[#B91C1C] font-semibold">basi</span>
                          : l.status === "verified" ? <span className="text-[#255E33] font-semibold">sudah bayar</span>
                          : <span className="text-[#8A3A12] font-semibold">belum bayar</span>}
                      </td>
                    </tr>
                  ))}
                  {data.locks.length === 0 && (
                    <tr><td colSpan={4} className="px-3 py-6 text-center text-[#7A6A5E]">Tidak ada kursi terkunci di sesi ini.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => load(sid)} className="flex-1" data-testid="seat-audit-refresh">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Muat Ulang
          </Button>
          {isSuper && (
            <Button onClick={cleanup} disabled={busy || !data || (c.orphan || 0) === 0}
              data-testid="seat-audit-cleanup"
              className="flex-1 bg-[#B91C1C] hover:bg-[#991B1B] text-white">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />} Lepas Kunci Basi{data ? ` (${c.orphan || 0})` : ""}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InvalidSeatFixer({ order, available, seatMap, busy, onSave }) {
  const [choices, setChoices] = useState({});
  const [mapOpen, setMapOpen] = useState(false);
  const invalid = order.invalid_seats || [];
  const chosen = Object.values(choices).filter(Boolean);
  const allChosen = invalid.length > 0 && invalid.every((s) => choices[s]);
  const hasDup = new Set(chosen).size !== chosen.length;
  const setChoice = (seat, val) => setChoices((prev) => ({ ...prev, [seat]: val }));
  const short = order.id.slice(0, 8);
  const validSeats = (order.seats || []).filter((s) => !invalid.includes(s));

  const pickFromMap = (label) => {
    if (seatMap?.couples?.[label]) { toast.error("Kursi sweetbox (pink) tidak bisa dipilih sebagai pengganti"); return; }
    setChoices((prev) => {
      const found = Object.entries(prev).find(([, v]) => v === label);
      if (found) { const n = { ...prev }; delete n[found[0]]; return n; }
      const slot = invalid.find((s) => !prev[s]);
      if (!slot) { toast.message("Semua kursi bermasalah sudah punya pengganti"); return prev; }
      return { ...prev, [slot]: label };
    });
  };

  return (
    <div className="rounded-xl border border-[#B26A1E]/40 bg-white p-3.5" data-testid={`invalid-seat-row-${short}`}>
      <div className="flex flex-wrap items-baseline gap-x-2 mb-2.5">
        <p className="text-sm font-semibold text-[#2C1E16] break-words">#{order.order_no} · {order.name}</p>
        <p className="text-[12px] text-[#7A6A5E]">Semua kursi: {(order.seats || []).join(", ")}</p>
      </div>
      <div className="space-y-2">
        {invalid.map((seat) => {
          const opts = available.filter((a) => !chosen.includes(a) || choices[seat] === a);
          return (
            <div key={seat} className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#7A241F]">
                <span className="px-2 py-0.5 rounded-md bg-[#7A241F]/10">Kursi {seat}</span>
                <span className="text-[#7A6A5E]">diganti ke</span>
              </span>
              <select
                data-testid={`invalid-replace-${short}-${seat}`}
                value={choices[seat] || ""}
                onChange={(e) => setChoice(seat, e.target.value)}
                className="h-9 min-w-[150px] rounded-lg border border-[#B26A1E]/50 bg-[#FAF7F2] px-3 text-sm font-semibold text-[#2C1E16] focus:outline-none focus:ring-2 focus:ring-[#B26A1E]/40">
                <option value="">— pilih kursi kosong —</option>
                {opts.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          );
        })}
      </div>
      {hasDup && <p className="text-[12px] text-[#EF4444] mt-2 font-medium">Kursi pengganti tidak boleh sama.</p>}
      <div className="flex flex-wrap justify-end gap-2 mt-3">
        {seatMap && (
          <Button size="sm" variant="outline" onClick={() => setMapOpen(true)}
            data-testid={`invalid-seat-map-${short}`}
            className="border-[#B26A1E]/50 text-[#8A3A12] hover:bg-[#B26A1E]/[0.06] h-9 px-4">
            <Armchair className="h-4 w-4 mr-1.5" /> Pilih di Denah
          </Button>
        )}
        <Button size="sm" disabled={!allChosen || hasDup || busy}
          onClick={() => onSave(order, choices)}
          data-testid={`invalid-seat-save-${short}`}
          className="bg-[#2F703E] hover:bg-[#255E33] text-white h-9 px-4">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Simpan Perubahan
        </Button>
      </div>

      <Dialog open={mapOpen} onOpenChange={setMapOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid={`invalid-map-dialog-${short}`}>
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#7A241F] flex items-center gap-2">
              <Armchair className="h-5 w-5 text-[#B26A1E]" /> Pilih Kursi Pengganti — {order.name}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#7A6A5E]">
              #{order.order_no} · Sesi 5 (Tambahan). Klik kursi kosong di denah untuk mengganti kursi yang bermasalah.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-[#B26A1E]/30 bg-[#F3E9DD]/40 p-3 mb-3 text-sm space-y-1.5">
            {validSeats.length > 0 && (
              <p className="text-[13px] text-[#255E33]"><b>Kursi tetap (tidak berubah):</b> {validSeats.join(", ")}</p>
            )}
            <div className="flex flex-wrap gap-1.5 items-center">
              <span className="text-[13px] text-[#7A241F] font-semibold mr-1">Penggantian:</span>
              {invalid.map((s) => (
                <span key={s} className="px-2 py-1 rounded-md bg-white border border-[#B26A1E]/40 text-[13px]">
                  <b className="text-[#7A241F]">{s}</b>
                  <span className="text-[#7A6A5E]"> → </span>
                  {choices[s]
                    ? <b className="text-[#255E33]">{choices[s]}</b>
                    : <span className="text-[#9CA3AF]">belum dipilih</span>}
                </span>
              ))}
            </div>
          </div>
          {seatMap && (
            <SeatMap rows={seatMap.rows} selected={chosen} onToggle={pickFromMap} couples={seatMap.couples}
              allowDisability={true} aisleRow={seatMap.aisle_row} doorRow={seatMap.door_row} reverse={seatMap.reverse} />
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setChoices({})} className="flex-1"
              data-testid={`invalid-map-reset-${short}`}>Reset Pilihan</Button>
            <Button onClick={() => setMapOpen(false)} disabled={!allChosen || hasDup}
              data-testid={`invalid-map-done-${short}`}
              className="flex-1 bg-[#B26A1E] hover:bg-[#9A5A16] text-white">
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Selesai Pilih
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(!!getAdminUser());
  const [currentUser, setCurrentUser] = useState(getAdminUser());
  const isSuper = currentUser?.role === "superadmin";
  const isStaff = currentUser?.role === "superadmin" || currentUser?.role === "admin";
  const canDelete = isSuper || !!currentUser?.can_delete;
  const canDeleteRow = (o) => isSuper || (canDelete && o.status !== "verified");
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [event, setEvent] = useState(null);
  const [soldOutSched, setSoldOutSched] = useState("");
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [proofView, setProofView] = useState(null);
  const [proofImage, setProofImage] = useState(null);
  const [proofLoading, setProofLoading] = useState(false);
  const [dialogVerified, setDialogVerified] = useState(false);
  const [verifyAmount, setVerifyAmount] = useState("");
  const [printOrder, setPrintOrder] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [tab, setTab] = useState("payment");
  const [checkinQuery, setCheckinQuery] = useState("");
  const [checkinPopup, setCheckinPopup] = useState(null);
  const [sessionFilter, setSessionFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [filter, sessionFilter, searchQuery]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deletedOrders, setDeletedOrders] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [restoreBusyId, setRestoreBusyId] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [invalidOrders, setInvalidOrders] = useState([]);
  const [invalidAvail, setInvalidAvail] = useState([]);
  const [invalidMap, setInvalidMap] = useState(null);
  const [invalidBusyId, setInvalidBusyId] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);

  const loadInvalid = useCallback(async () => {
    if (!isStaff) return;
    try {
      const [inv, seats5] = await Promise.all([
        adminApi.get("/admin/orders/invalid-seats"),
        api.get("/sessions/5/seats"),
      ]);
      setInvalidOrders(inv.data || []);
      const avail = [];
      (seats5.data.rows || []).forEach((r) => r.blocks.forEach((b) => b.forEach((s) => {
        if (s.status === "available" && !s.couple && !s.disability) avail.push(s.label);
      })));
      avail.sort((a, b) => a.charCodeAt(0) - b.charCodeAt(0) || parseInt(a.slice(1)) - parseInt(b.slice(1)));
      setInvalidAvail(avail);
      setInvalidMap({
        rows: seats5.data.rows || [],
        couples: seats5.data.couples || {},
        door_row: seats5.data.door_row,
        aisle_row: seats5.data.aisle_row,
        reverse: seats5.data.reverse,
      });
    } catch { /* keep previous */ }
  }, [isStaff]);

  const saveInvalidFix = async (order, choices) => {
    const newSeats = (order.seats || []).map((s) => choices[s] || s);
    setInvalidBusyId(order.id);
    try {
      const { data } = await adminApi.put(`/admin/orders/${order.id}/seats`, { seats: newSeats });
      toast.success(`Kursi #${order.order_no} (${order.name}) diperbarui: ${data.seats.join(", ")}`);
      await loadInvalid();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengubah kursi");
    }
    setInvalidBusyId(null);
  };

  const load = useCallback(async () => {
    if (!isStaff) { setLoading(false); return; }
    setLoading(true);
    try {
      const [o, s, e] = await Promise.all([
        adminApi.get("/admin/orders"),
        adminApi.get("/admin/stats"),
        api.get("/event"),
      ]);
      setOrders(o.data); setStats(s.data); setEvent(e.data);
    } catch (err) {
      if (err?.response?.status === 401) { clearAdminSession(); setCurrentUser(null); setAuthed(false); }
      else toast.error("Gagal memuat data");
    }
    setLoading(false);
  }, [isStaff]);

  useEffect(() => { if (authed) loadInvalid(); }, [authed, loadInvalid]);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  const onLoggedIn = () => { setCurrentUser(getAdminUser()); setAuthed(true); };

  const act = async (id, action) => {
    setBusyId(id);
    try {
      await adminApi.post(`/admin/orders/${id}/${action}`);
      toast.success(action === "verify" ? "Pesanan diverifikasi" : action === "reject" ? "Pesanan ditolak" : "Check-in tersimpan");
      await load();
    } catch (err) { toast.error("Aksi gagal"); }
    setBusyId(null);
  };

  const toggleSessionOpen = async (sid, open, target = "public") => {
    try {
      await adminApi.post("/admin/sessions/toggle", { session_id: sid, open, target });
      const who = target === "walkin" ? "Panitia (lokasi)" : "Umum (online)";
      toast.success(open ? `Sesi ${sid} DIBUKA untuk ${who}` : `Sesi ${sid} DITUTUP untuk ${who}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah sesi"); }
  };

  const loadTrash = useCallback(async () => {
    setTrashLoading(true);
    try { const { data } = await adminApi.get("/admin/orders/deleted"); setDeletedOrders(data); }
    catch { toast.error("Gagal memuat kotak sampah"); }
    setTrashLoading(false);
  }, []);

  const restoreOrder = async (o) => {
    setRestoreBusyId(o.id);
    try {
      await adminApi.post(`/admin/orders/${o.id}/restore`);
      toast.success(`Pesanan #${o.order_no} dipulihkan`);
      await loadTrash();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memulihkan pesanan"); }
    setRestoreBusyId(null);
  };

  useEffect(() => { if (authed && isSuper && tab === "trash") loadTrash(); }, [authed, isSuper, tab, loadTrash]);

  const toggleComingSoon = async () => {
    const enable = !event?.coming_soon;
    try {
      await adminApi.post("/admin/coming-soon", { enabled: enable });
      toast.success(enable ? "Mode Coming Soon AKTIF — halaman depan menampilkan 'Tiket Segera Dibuka'" : "Penjualan tiket DIBUKA — pembeli sudah bisa memesan!");
      await load();
    } catch { toast.error("Gagal mengubah mode"); }
  };

  const toggleSoldOut = async () => {
    const enable = !event?.sold_out_manual;
    try {
      await adminApi.post("/admin/sold-out", { enabled: enable });
      toast.success(enable ? "Mode TIKET HABIS AKTIF — halaman depan menampilkan 'Tiket Habis Terjual + terima kasih'" : "Mode Tiket Habis dimatikan — penjualan kembali dibuka");
      await load();
    } catch { toast.error("Gagal mengubah mode"); }
  };

  const toggleOrdersClosed = async () => {
    const enable = !event?.orders_closed;
    if (!window.confirm(enable
      ? "Aktifkan mode TIKET HABIS TOTAL? Halaman depan menampilkan ucapan terima kasih + 'Sampai jumpa di CGV' (tanpa form & tanpa walk-in). Pesanan online tidak bisa dibuat lagi."
      : "Matikan mode ini & buka kembali pemesanan online?")) return;
    try {
      await adminApi.post("/admin/orders-closed", { enabled: enable });
      toast.success(enable ? "Mode TIKET HABIS TOTAL aktif — halaman depan menampilkan 'Sampai jumpa di CGV'" : "Pemesanan online DIBUKA kembali");
      await load();
    } catch { toast.error("Gagal mengubah mode"); }
  };

  const saveSoldOutSchedule = async (localValue) => {
    // localValue "2026-09-13T09:00" selalu dianggap WIB (UTC+7), lepas dari zona perangkat panitia
    const withSec = localValue && localValue.length === 16 ? `${localValue}:00` : localValue;
    const iso = localValue ? new Date(`${withSec}+07:00`).toISOString() : null;
    try {
      await adminApi.post("/admin/sold-out-schedule", { sold_out_at: iso });
      toast.success(iso ? "Jadwal Tiket Habis otomatis disimpan" : "Jadwal Tiket Habis otomatis dihapus");
      await load();
    } catch { toast.error("Gagal menyimpan jadwal"); }
  };

  useEffect(() => { setSoldOutSched(isoToLocalInput(event?.sold_out_at)); }, [event?.sold_out_at]);

  // ---- Ubah kursi pesanan website ----
  const [seatEdit, setSeatEdit] = useState(null);
  const [seatRows, setSeatRows] = useState([]);
  const [seatSelected, setSeatSelected] = useState([]);
  const [seatCouples, setSeatCouples] = useState({});
  const [seatMeta, setSeatMeta] = useState({ door_row: "C", aisle_row: "K", reverse: false });
  const [seatLoading, setSeatLoading] = useState(false);
  const [seatBusy, setSeatBusy] = useState(false);

  const openSeatEdit = async (o) => {
    setSeatEdit(o);
    setSeatSelected(o.seats || []);
    setSeatLoading(true);
    try {
      const sid = o.session_id ?? o.session?.id;
      const { data } = await api.get(`/sessions/${sid}/seats`);
      const cur = new Set(o.seats || []);
      const rows = (data.rows || []).map((row) => ({
        ...row,
        blocks: row.blocks.map((block) => block.map((s) =>
          cur.has(s.label) && (s.status === "booked" || s.status === "locked") ? { ...s, status: "available" } : s)),
      }));
      setSeatRows(rows);
      setSeatCouples(data.couples || {});
      setSeatMeta({ door_row: data.door_row, aisle_row: data.aisle_row, reverse: data.reverse });
    } catch { toast.error("Gagal memuat peta kursi"); }
    setSeatLoading(false);
  };

  const toggleSeatEdit = (label, partner) => {
    setSeatSelected((prev) => {
      const set = new Set(prev);
      const flip = (l) => { if (set.has(l)) set.delete(l); else set.add(l); };
      flip(label);
      if (partner) flip(partner);
      return Array.from(set);
    });
  };

  const saveSeatEdit = async () => {
    if (!seatEdit) return;
    if (seatSelected.length === 0) { toast.error("Pilih minimal 1 kursi"); return; }
    setSeatBusy(true);
    try {
      const { data } = await adminApi.put(`/admin/orders/${seatEdit.id}/seats`, { seats: seatSelected });
      toast.success(`Kursi #${seatEdit.order_no} diubah: ${data.seats.join(", ")}`);
      setOrders((prev) => prev.map((x) => x.id === seatEdit.id ? { ...x, seats: data.seats, qty: data.qty } : x));
      setSeatEdit(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengubah kursi");
    }
    setSeatBusy(false);
  };


  const logout = async () => {
    try { await adminApi.post("/admin/logout"); } catch (e) { console.error("logout:", e); }
    clearAdminSession(); setCurrentUser(null); setAuthed(false);
  };

  const [exportingCheckin, setExportingCheckin] = useState(false);
  const exportCheckin = async () => {
    setExportingCheckin(true);
    try {
      const res = await adminApi.get("/admin/checkin/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      a.href = url; a.download = `data_checkin_${ts}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Data check-in diunduh (backup Excel)");
    } catch { toast.error("Gagal export data check-in"); }
    setExportingCheckin(false);
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await adminApi.delete(`/admin/orders/${deleteTarget.id}`);
      toast.success(`Pesanan #${deleteTarget.order_no} dipindah ke Kotak Sampah`);
      setDeleteTarget(null);
      setSelectedIds((p) => p.filter((x) => x !== deleteTarget.id));
      await load();
    } catch { toast.error("Gagal menghapus pesanan"); }
    setDeleteBusy(false);
  };

  const sendAndMarkWA = async (o) => {
    sendWA(o);
    try { await adminApi.post(`/admin/orders/${o.id}/wa-sent`); await load(); }
    catch (e) { console.error("Gagal menandai WA terkirim:", e); }
  };

  const adminUpload = (o) => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setBusyId(o.id);
      try {
        const dataUrl = await compressImage(file);
        await api.post(`/orders/${o.id}/proof`, { proof_image: dataUrl });
        toast.success(`Bukti untuk ${o.name} berhasil diupload`);
        await load();
      } catch (err) { toast.error(err?.response?.data?.detail || "Gagal upload bukti"); }
      setBusyId(null);
    };
    input.click();
  };

  const openProof = async (o) => {
    setProofView(o); setProofImage(null); setProofLoading(true); setDialogVerified(false);
    setVerifyAmount(String(o.total_amount || ""));
    try {
      const { data } = await adminApi.get(`/admin/orders/${o.id}/proof-image`);
      setProofImage(data.proof_image);
    } catch { toast.error("Gagal memuat bukti"); }
    setProofLoading(false);
  };

  const verifyInDialog = async () => {
    if (!proofView) return;
    const amt = parseInt(verifyAmount, 10);
    if (!amt || amt <= 0) { toast.error("Isi nominal yang masuk"); return; }
    setBusyId(proofView.id);
    try {
      await adminApi.post(`/admin/orders/${proofView.id}/verify`, { amount: amt });
      toast.success(amt !== proofView.total_amount ? `Diverifikasi — nominal disesuaikan ke ${rupiah(amt)}` : "Pesanan diverifikasi");
      setDialogVerified(true);
      await load();
    } catch { toast.error("Gagal verifikasi"); }
    setBusyId(null);
  };

  const saveAmount = async () => {
    if (!proofView) return;
    const amt = parseInt(verifyAmount, 10);
    if (!amt || amt <= 0) { toast.error("Isi nominal yang benar"); return; }
    setBusyId(proofView.id);
    try {
      await adminApi.post(`/admin/orders/${proofView.id}/set-amount`, { amount: amt });
      toast.success(`Nominal diperbarui ke ${rupiah(amt)}`);
      setProofView({ ...proofView, total_amount: amt, amount_adjusted: true, amount_edited_once: true });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan nominal"); }
    setBusyId(null);
  };

  const doPrint = (o) => {
    setPrintOrder(o);
    setTimeout(() => window.print(), 250);
  };

  if (!authed) return <LoginView onLogin={onLoggedIn} />;

  // Petugas Check-in tidak boleh akses panel verifikasi
  if (!isStaff) {
    const isSeller = currentUser?.role === "seller";
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center">
        <div className="rounded-2xl border border-border bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <div className="h-12 w-12 rounded-full bg-[#2F703E]/10 flex items-center justify-center mb-4 mx-auto">
            {isSeller ? <Store className="h-6 w-6 text-[#B26A1E]" /> : <ScanLine className="h-6 w-6 text-[#2F703E]" />}
          </div>
          <h1 className="font-serif-display text-2xl text-[#7A241F]">Halo, {currentUser?.name}</h1>
          <p className="text-sm text-[#7A6A5E] mt-1 mb-5">
            {isSeller
              ? <>Akun Anda adalah <b>Petugas Penjual Tiket</b> — bisa jual tiket di tempat dan check-in peserta.</>
              : <>Akun Anda adalah <b>Petugas Check-in</b>, jadi hanya bisa mengakses halaman check-in peserta.</>}
          </p>
          <div className="flex flex-col items-center gap-2.5">
            {isSeller && (
              <a href="/walkin" data-testid="goto-walkin"
                className="inline-flex items-center gap-2 bg-[#B26A1E] hover:bg-[#8A3A12] text-white rounded-full px-6 py-3 text-sm font-medium">
                <Store className="h-4 w-4" /> Buka Halaman Jual di Tempat
              </a>
            )}
            <a href="/checkin" data-testid="goto-checkin"
              className={cn("inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium",
                isSeller ? "border border-[#2F703E]/40 text-[#255E33] hover:bg-[#2F703E]/10" : "bg-[#7A241F] hover:bg-[#5E1B17] text-white")}>
              <UserCheck className="h-4 w-4" /> Buka Halaman Check-in
            </a>
          </div>
          <button onClick={logout} data-testid="checkin-role-logout" className="block mx-auto mt-5 text-xs text-[#EF4444] underline">Keluar</button>
        </div>
      </div>
    );
  }

  const sq = searchQuery.trim().toLowerCase();
  const nsq = sq.replace(/[\s-]/g, "");
  // VIP (gratis) TIDAK masuk Verifikasi Pembayaran — kecuali ada bukti slip atau ada dana (berdana)
  const isPaymentRelevant = (o) => !(o.vip && !o.has_proof && !((o.total_amount || 0) > 0));
  const paymentOrders = orders.filter(isPaymentRelevant);
  const filtered = paymentOrders.filter((o) =>
    (filter === "all" || o.status === filter) &&
    (sessionFilter === "all" || o.session_id === sessionFilter) &&
    (sq === "" || o.name.toLowerCase().includes(sq) || o.phone.replace(/[\s-]/g, "").includes(nsq) || String(o.order_no || "").includes(searchQuery.trim()))
  );

  const selectableIds = filtered.filter((o) => o.status === "waiting_verification").map((o) => o.id);
  const STATUS_PRIORITY = { waiting_verification: 0, pending_payment: 1, rejected: 2, expired: 3, verified: 4 };
  const sortedFiltered = [...filtered].sort((a, b) => (STATUS_PRIORITY[a.status] ?? 9) - (STATUS_PRIORITY[b.status] ?? 9));
  const PER_PAGE = 20;
  const totalPages = Math.max(1, Math.ceil(sortedFiltered.length / PER_PAGE));
  const pageSafe = Math.min(page, totalPages);
  const paged = sortedFiltered.slice((pageSafe - 1) * PER_PAGE, pageSafe * PER_PAGE);
  const toggleSelect = (id) => setSelectedIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));
  const toggleSelectAll = () => setSelectedIds(allSelected ? [] : selectableIds);
  const bulkAct = async (action) => {
    if (selectedIds.length === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await adminApi.post("/admin/orders/bulk", { ids: selectedIds, action });
      toast.success(`${data.updated} pesanan ${action === "verify" ? "diverifikasi" : "ditolak"}`);
      setSelectedIds([]);
      await load();
    } catch { toast.error("Aksi massal gagal"); }
    setBulkBusy(false);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 no-print">
        <div>
          <h1 className="font-serif-display text-3xl text-[#7A241F]">Panel Admin</h1>
          <p className="text-sm text-[#7A6A5E] flex items-center gap-2">
            <span>Masuk sebagai <b className="text-[#2C1E16]">{currentUser?.name}</b></span>
            {currentUser?.role && (
              <span className={cn("text-[11px] px-2 py-0.5 rounded-full font-medium", (ROLE_BADGE[currentUser.role] || {}).c)}>
                {(ROLE_BADGE[currentUser.role] || {}).t}
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setTab("masterlist")} data-testid="btn-masterlist-pembelian"
            className={cn("shadow-sm text-base font-semibold border h-10 px-4",
              tab === "masterlist"
                ? "bg-[#E4C57E] text-[#5E1B17] border-[#B26A1E] hover:bg-[#DBB768]"
                : "bg-[#F6EEE1] text-[#7A241F] border-[#B26A1E]/50 hover:bg-[#EFE2CE]")}>
            <ClipboardList className="h-5 w-5 mr-1.5" /> Masterlist Pembelian
          </Button>
          <Button variant="outline" size="sm" onClick={load} data-testid="btn-refresh"><RefreshCw className="h-4 w-4 mr-1.5" /> Muat Ulang</Button>
          <Button variant="outline" size="sm" onClick={exportCheckin} disabled={exportingCheckin} data-testid="btn-export-checkin"
            className="border-[#2F703E]/40 text-[#255E33] hover:bg-[#2F703E]/[0.06]">
            {exportingCheckin ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Export Check-in
          </Button>
          <Button variant="outline" size="sm" onClick={() => setAuditOpen(true)} data-testid="btn-seat-audit"
            className="border-[#B26A1E]/50 text-[#8A3A12] hover:bg-[#B26A1E]/[0.06]">
            <Armchair className="h-4 w-4 mr-1.5" /> Audit Kursi
          </Button>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="btn-logout" className="text-[#EF4444]"><LogOut className="h-4 w-4 mr-1.5" /> Keluar</Button>
        </div>
      </div>

      <SeatAuditDialog open={auditOpen} onOpenChange={setAuditOpen} isSuper={isSuper} />

      {invalidOrders.length > 0 && (
        <div className="rounded-2xl border-2 border-[#B26A1E] bg-[#B26A1E]/[0.08] p-4 sm:p-5 mb-5 no-print" data-testid="invalid-seats-banner">
          <div className="flex items-start gap-3 mb-3">
            <span className="h-10 w-10 rounded-xl bg-[#B26A1E]/20 text-[#8A3A12] flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="font-serif-display text-lg text-[#7A241F] leading-tight">
                {invalidOrders.length} pesanan Sesi 5 dengan kursi tidak valid
              </p>
              <p className="text-sm text-[#7A6A5E] leading-snug mt-0.5">
                Denah Sesi 5 sudah diperbarui. Untuk tiap kursi yang bermasalah, cukup pilih kursi kosong penggantinya lalu klik "Simpan Perubahan". Peringatan ini hilang otomatis setelah semua diperbaiki.
              </p>
            </div>
          </div>
          <div className="space-y-2.5">
            {invalidOrders.map((o) => (
              <InvalidSeatFixer key={o.id} order={o} available={invalidAvail} seatMap={invalidMap}
                busy={invalidBusyId === o.id} onSave={saveInvalidFix} />
            ))}
          </div>
        </div>
      )}

      {isSuper && event && (
        <div className="rounded-2xl border border-border bg-white p-3 sm:p-4 mb-5 no-print" data-testid="front-controls">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#7A6A5E] mb-2.5 px-0.5">Kontrol Halaman Depan</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            {/* Coming Soon / Penjualan */}
            <div className={cn("flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 transition-colors",
              event.coming_soon ? "border-[#B26A1E]/50 bg-[#E8D8B6]/40" : "border-[#2F703E]/40 bg-[#2F703E]/[0.05]")}
              data-testid="coming-soon-toggle-card">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className={cn("h-8 w-8 rounded-lg flex items-center justify-center shrink-0",
                  event.coming_soon ? "bg-[#B26A1E]/15 text-[#8A3A12]" : "bg-[#2F703E]/15 text-[#255E33]")}>
                  {event.coming_soon ? <Clock className="h-4 w-4" /> : <Ticket className="h-4 w-4" />}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#2C1E16] leading-tight">Penjualan Tiket</p>
                  <p className={cn("text-[11px] font-semibold leading-tight", event.coming_soon ? "text-[#8A3A12]" : "text-[#255E33]")}
                    data-testid="coming-soon-status">{event.coming_soon ? "Coming Soon" : "Dibuka"}</p>
                </div>
              </div>
              <Switch checked={!event.coming_soon} onCheckedChange={toggleComingSoon} data-testid="coming-soon-switch" />
            </div>

            {/* Tiket Habis */}
            <div className={cn("flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 transition-colors",
              event.sold_out ? "border-[#7A241F]/50 bg-[#7A241F]/[0.06]" : "border-border bg-[#FAF7F2]")}
              data-testid="sold-out-toggle-card">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className={cn("h-8 w-8 rounded-lg flex items-center justify-center shrink-0",
                  event.sold_out ? "bg-[#7A241F]/15 text-[#7A241F]" : "bg-[#7A6A5E]/10 text-[#7A6A5E]")}>
                  <Ticket className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#2C1E16] leading-tight">Tiket Habis</p>
                  <p className={cn("text-[11px] font-semibold leading-tight", event.sold_out ? "text-[#7A241F]" : "text-[#7A6A5E]")}
                    data-testid="sold-out-status">{event.sold_out ? "Aktif" : "Nonaktif"}</p>
                </div>
              </div>
              <Switch checked={!!event.sold_out_manual} onCheckedChange={toggleSoldOut} data-testid="sold-out-switch" />
            </div>

            {/* Pemesanan Online / Walk-in */}
            <div className={cn("flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 transition-colors",
              event.orders_closed ? "border-[#B26A1E]/60 bg-[#B26A1E]/[0.07]" : "border-border bg-[#FAF7F2]")}
              data-testid="orders-closed-toggle-card">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className={cn("h-8 w-8 rounded-lg flex items-center justify-center shrink-0",
                  event.orders_closed ? "bg-[#B26A1E]/15 text-[#8A3A12]" : "bg-[#7A6A5E]/10 text-[#7A6A5E]")}>
                  <Store className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#2C1E16] leading-tight">Tiket Habis Total</p>
                  <p className={cn("text-[11px] font-semibold leading-tight", event.orders_closed ? "text-[#8A3A12]" : "text-[#7A6A5E]")}
                    data-testid="orders-closed-status">{event.orders_closed ? "Aktif · Sampai jumpa" : "Nonaktif"}</p>
                </div>
              </div>
              <Switch checked={!!event.orders_closed} onCheckedChange={toggleOrdersClosed} data-testid="orders-closed-switch" />
            </div>
          </div>
        </div>
      )}

      {isSuper && event && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6 no-print">
          <StatCard icon={Clock} label="Perlu Verifikasi" value={stats?.waiting_verification ?? 0} color="bg-[#7A241F]/10 text-[#7A241F]" />
          <StatCard icon={CheckCircle2} label="Terverifikasi" value={stats?.verified ?? 0} color="bg-[#2F703E]/10 text-[#2F703E]" />
          <StatCard icon={Ticket} label="Tiket Terjual" value={stats?.tickets_verified ?? 0} color="bg-[#B26A1E]/10 text-[#B26A1E]" />
          <StatCard icon={Wallet} label="Pendapatan" value={rupiah(stats?.revenue_verified ?? 0)} color="bg-[#2F703E]/10 text-[#2F703E]" />
        </div>
      )}

      {/* Tab switcher */}
      <div className="flex flex-wrap gap-2 mb-6 no-print">
        <button data-testid="admin-tab-payment" onClick={() => setTab("payment")}
          className={cn("px-4 py-2 rounded-full text-sm font-medium border transition-colors inline-flex items-center gap-1.5",
            tab === "payment" ? "bg-[#B26A1E] text-white border-[#B26A1E]" : "bg-white text-[#7A6A5E] border-border hover:border-[#B26A1E]/50")}>
          <Wallet className="h-4 w-4" /> Verifikasi Pembayaran
        </button>
        <button data-testid="admin-tab-bendahara" onClick={() => setTab("bendahara")}
          className={cn("px-4 py-2 rounded-full text-sm font-medium border transition-colors inline-flex items-center gap-1.5",
            tab === "bendahara" ? "bg-[#2F703E] text-white border-[#2F703E]" : "bg-white text-[#7A6A5E] border-border hover:border-[#2F703E]/50")}>
          <Banknote className="h-4 w-4" /> Bendahara
        </button>
        <button data-testid="admin-tab-vip" onClick={() => setTab("vip")}
          className={cn("px-4 py-2 rounded-full text-sm font-medium border transition-colors inline-flex items-center gap-1.5",
            tab === "vip" ? "bg-[#7A241F] text-white border-[#7A241F]" : "bg-white text-[#7A6A5E] border-border hover:border-[#B26A1E]/60")}>
          <Crown className="h-4 w-4" /> Tiket VIP
        </button>
        <button data-testid="admin-tab-manual" onClick={() => setTab("manual")}
          className={cn("px-4 py-2 rounded-full text-sm font-medium border transition-colors inline-flex items-center gap-1.5",
            tab === "manual" ? "bg-[#B26A1E] text-white border-[#B26A1E]" : "bg-white text-[#7A6A5E] border-border hover:border-[#B26A1E]/60")}>
          <ClipboardList className="h-4 w-4" /> Order Manual
        </button>
        <button data-testid="admin-tab-sessions" onClick={() => setTab("sessions")}
          className={cn("px-4 py-2 rounded-full text-sm font-medium border transition-colors inline-flex items-center gap-1.5",
            tab === "sessions" ? "bg-[#2F703E] text-white border-[#2F703E]" : "bg-white text-[#7A6A5E] border-border hover:border-[#2F703E]/50")}>
          <Users className="h-4 w-4" /> Buka/Tutup Sesi
        </button>
        <button data-testid="admin-tab-logs" onClick={() => setTab("logs")}
          className={cn("px-4 py-2 rounded-full text-sm font-medium border transition-colors inline-flex items-center gap-1.5",
            tab === "logs" ? "bg-[#7A241F] text-white border-[#7A241F]" : "bg-white text-[#7A6A5E] border-border hover:border-[#7A241F]/50")}>
          <History className="h-4 w-4" /> Log Aktivitas
        </button>
        {isStaff && (
          <button data-testid="admin-tab-users" onClick={() => setTab("users")}
            className={cn("px-4 py-2 rounded-full text-sm font-medium border transition-colors inline-flex items-center gap-1.5",
              tab === "users" ? "bg-[#7A241F] text-white border-[#7A241F]" : "bg-white text-[#7A6A5E] border-border hover:border-[#7A241F]/50")}>
            <Users className="h-4 w-4" /> Kelola User
          </button>
        )}
        {isSuper && (
          <button data-testid="admin-tab-setoran" onClick={() => setTab("setoran")}
            className={cn("px-4 py-2 rounded-full text-sm font-medium border transition-colors inline-flex items-center gap-1.5",
              tab === "setoran" ? "bg-[#2F703E] text-white border-[#2F703E]" : "bg-white text-[#7A6A5E] border-border hover:border-[#2F703E]/50")}>
            <Wallet className="h-4 w-4" /> Setoran Kas
          </button>
        )}
        {isSuper && (
          <button data-testid="admin-tab-trash" onClick={() => setTab("trash")}
            className={cn("px-4 py-2 rounded-full text-sm font-medium border transition-colors inline-flex items-center gap-1.5",
              tab === "trash" ? "bg-[#EF4444] text-white border-[#EF4444]" : "bg-white text-[#7A6A5E] border-border hover:border-[#EF4444]/50")}>
            <Trash2 className="h-4 w-4" /> Kotak Sampah
          </button>
        )}
        {isSuper && (
          <button data-testid="admin-tab-database" onClick={() => setTab("database")}
            className={cn("px-4 py-2 rounded-full text-sm font-medium border transition-colors inline-flex items-center gap-1.5",
              tab === "database" ? "bg-[#255E33] text-white border-[#255E33]" : "bg-white text-[#7A6A5E] border-border hover:border-[#255E33]/50")}>
            <Database className="h-4 w-4" /> Backup DB
          </button>
        )}
      </div>

      {tab === "payment" && (<>
      {stats && (
        <div className="mb-5 no-print">
          <button onClick={() => setShowSummary((v) => !v)} data-testid="toggle-summary"
            className="w-full flex items-center justify-between gap-3 rounded-xl border border-border bg-white px-4 py-3 hover:border-[#B26A1E]/50 transition-colors">
            <span className="flex items-center gap-2 text-sm font-semibold text-[#7A241F]">
              <Wallet className="h-4 w-4 text-[#B26A1E]" /> Ringkasan & Laporan Penjualan
              <span className="text-xs font-normal text-[#7A6A5E]">· {stats.verified || 0} terverifikasi · {rupiah(stats.revenue_verified || 0)}</span>
            </span>
            <ChevronDown className={cn("h-4 w-4 text-[#7A6A5E] transition-transform", showSummary && "rotate-180")} />
          </button>
          {showSummary && (
            <div className="mt-4">
              <SessionFunds stats={stats} />
              <SalesSummary stats={stats} />
              <DailySalesChart stats={stats} />
              <p className="text-[11px] text-[#7A6A5E] -mt-2 mb-2">Laporan keuangan lengkap (umum + panitia, bisa export Excel) ada di tab <b>Bendahara</b>.</p>
            </div>
          )}
        </div>
      )}
      {/* Filter per sesi */}
      <div className="flex flex-wrap items-center gap-2 mb-3 no-print">
        <span className="text-xs text-[#7A6A5E] mr-1">Filter sesi:</span>
        <button data-testid="session-filter-all" onClick={() => setSessionFilter("all")}
          className={cn("px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
            sessionFilter === "all" ? "bg-[#B26A1E] text-white border-[#B26A1E]" : "bg-white text-[#7A6A5E] border-border hover:border-[#B26A1E]/40")}>
          Semua Sesi
        </button>
        {[1, 2, 3, 4, 5].map((sid) => (
          <button key={sid} data-testid={`session-filter-${sid}`} onClick={() => setSessionFilter(sid)}
            className={cn("px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
              sessionFilter === sid ? "bg-[#B26A1E] text-white border-[#B26A1E]" : "bg-white text-[#7A6A5E] border-border hover:border-[#B26A1E]/40")}>
            {sid === 5 ? "Sesi 5" : `Sesi ${sid}`} ({paymentOrders.filter((o) => o.session_id === sid).length})
          </button>
        ))}
      </div>

      {/* Filters status */}
      <div className="flex flex-wrap gap-2 mb-4 no-print">
        {FILTERS.map((f) => (
          <button key={f.k} data-testid={`filter-${f.k}`} onClick={() => setFilter(f.k)}
            className={cn("px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
              filter === f.k ? "bg-[#7A241F] text-white border-[#7A241F]" : "bg-white text-[#7A6A5E] border-border hover:border-[#7A241F]/40")}>
            {f.t}
          </button>
        ))}
      </div>

      {/* Search + bulk actions */}
      <div className="flex flex-wrap items-center gap-3 mb-4 no-print">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A6A5E]" />
          <Input data-testid="orders-search" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cari nama, no HP, atau no order..." className="pl-9" />
        </div>
        {selectedIds.length > 0 && (
          <div className="flex items-center gap-2 rounded-full bg-[#7A241F]/5 border border-[#7A241F]/20 px-3 py-1.5">
            <span className="text-sm text-[#7A241F] font-medium" data-testid="bulk-count">{selectedIds.length} dipilih</span>
            <Button size="sm" onClick={() => bulkAct("verify")} disabled={bulkBusy} data-testid="bulk-verify"
              className="h-8 bg-[#2F703E] hover:bg-[#255E33]">
              {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />} Verifikasi
            </Button>
            <Button size="sm" variant="outline" onClick={() => bulkAct("reject")} disabled={bulkBusy} data-testid="bulk-reject"
              className="h-8 text-[#EF4444] border-[#EF4444]/40">
              <XCircle className="h-3.5 w-3.5 mr-1" /> Tolak
            </Button>
            <button onClick={() => setSelectedIds([])} className="text-xs text-[#7A6A5E] underline">bersihkan</button>
          </div>
        )}
      </div>

      {/* Orders table */}
      <div className="flex items-center justify-between mb-2 no-print">
        <h2 className="font-serif-display text-xl text-[#7A241F] flex items-center gap-2">
          <Wallet className="h-5 w-5 text-[#B26A1E]" /> Daftar Pesanan — Verifikasi Pembayaran
        </h2>
        <span className="text-xs text-[#7A6A5E]">{filtered.length} pesanan</span>
      </div>
      <p className="text-xs text-[#7A6A5E] mb-3 no-print">Klik <b>Lihat</b> pada kolom Bukti untuk melihat bukti transfer & memverifikasi.</p>
      <div className="rounded-2xl border border-border bg-white overflow-hidden no-print">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#B26A1E]" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-sm text-[#7A6A5E]">Tidak ada pesanan yang cocok.</div>
        ) : (
          <>
          {/* Mobile: card list */}
          <div className="md:hidden divide-y divide-border" data-testid="orders-cards">
            {paged.map((o) => {
              const dstat = orderProgress(o);
              return (
                <div key={o.id} className="p-4" data-testid={`order-card-${o.id.slice(0, 8)}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2.5 min-w-0">
                      {o.status === "waiting_verification" && (
                        <input type="checkbox" checked={selectedIds.includes(o.id)} onChange={() => toggleSelect(o.id)}
                          className="accent-[#7A241F] h-4 w-4 mt-1 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-[#2C1E16]">{o.name} <span className="font-mono text-[10px] text-[#7A6A5E]">#{o.order_no}</span></p>
                        <p className="text-xs text-[#7A6A5E]">{o.phone}</p>
                      </div>
                    </div>
                    <span className={cn("shrink-0 text-[11px] px-2 py-0.5 rounded-full font-medium", dstat.c)}>{dstat.t}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#7A6A5E]">
                    <span>{fmtTime(o.created_at)}</span>
                    <span className="text-[#2C1E16]">{o.session?.name} · {o.seats.join(", ")}</span>
                    <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full font-medium", channelInfo(o).c)} data-testid={`channel-${o.id.slice(0, 8)}`}>{channelInfo(o).t}</span>
                    <span className="font-semibold text-[#7A241F] text-sm ml-auto">{rupiah(o.total_amount)}</span>
                  </div>
                  {(o.walkin && o.sold_by) || o.verified_by ? (
                    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px]">
                      {o.walkin && o.sold_by && (
                        <span className="text-[#8A3A12]">Dijual: <b>{o.sold_by}</b>{o.payment_method === "cash" ? " (Cash)" : ""}</span>
                      )}
                      {o.verified_by && o.status === "verified" && (
                        <span className="text-[#255E33]">Diverifikasi: <b>{o.verified_by}</b>{o.updated_at ? ` · ${fmtTime(o.updated_at)}` : ""}</span>
                      )}
                    </div>
                  ) : null}
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {o.status === "waiting_verification" ? (
                      <Button size="sm" onClick={() => openProof(o)} className="h-8 bg-[#B26A1E] hover:bg-[#8A3A12] text-xs">
                        <Eye className="h-3.5 w-3.5 mr-1" /> Cek Bukti
                      </Button>
                    ) : o.status === "verified" ? (
                      <button onClick={() => o.has_proof && openProof(o)}
                        className="inline-flex flex-col items-start text-[#255E33] text-xs font-medium">
                        <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Payment OK</span>
                        {(o.verified_by || o.sold_by || o.created_by) && <span className="text-[10px] text-[#7A6A5E] font-normal">oleh {o.verified_by || o.sold_by || o.created_by}{o.updated_at ? ` · ${fmtTime(o.updated_at)}` : ""}</span>}
                      </button>
                    ) : (o.status === "pending_payment" || o.status === "expired") ? (
                      <Button size="sm" variant="outline" onClick={() => adminUpload(o)} disabled={busyId === o.id}
                        className="h-8 text-xs border-[#7A241F]/40 text-[#7A241F]">
                        {busyId === o.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <UploadCloud className="h-3.5 w-3.5 mr-1" />} Upload Bukti
                      </Button>
                    ) : null}
                    {o.status === "verified" ? (
                      <Button size="sm" onClick={() => sendAndMarkWA(o)}
                        className={cn("h-8 text-xs", o.wa_sent ? "bg-white text-[#255E33] border border-[#2F703E]/50 hover:bg-[#2F703E]/10" : "bg-[#2F703E] hover:bg-[#255E33]")}>
                        <MessageCircle className="h-3.5 w-3.5 mr-1" /> {o.wa_sent ? "Kirim Ulang" : "Kirim Pesan"}
                      </Button>
                    ) : (o.status === "pending_payment" || o.status === "expired") ? (
                      <Button size="sm" onClick={() => sendReminderWA(o)} className="h-8 text-xs bg-[#B26A1E] hover:bg-[#8A3A12]">
                        <MessageCircle className="h-3.5 w-3.5 mr-1" /> Ingatkan Upload
                      </Button>
                    ) : null}
                    <Button size="sm" variant="outline" onClick={() => openSeatEdit(o)} data-testid={`edit-seats-${o.id.slice(0, 8)}`}
                      className="h-8 text-xs border-[#B26A1E]/40 text-[#8A3A12] hover:bg-[#F3E9DD]/50">
                      <Armchair className="h-3.5 w-3.5 mr-1" /> Ubah Kursi
                    </Button>
                    {canDeleteRow(o) ? (
                      <button onClick={() => setDeleteTarget(o)} title="Hapus pesanan"
                        className="ml-auto inline-flex items-center justify-center h-8 w-8 rounded-lg text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    ) : (canDelete && o.status === "verified") ? (
                      <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-[#7A6A5E]" title="Terverifikasi — hanya Super Admin yang dapat menghapus"><Lock className="h-3 w-3" /> Super Admin</span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Desktop: table */}
          <div className="overflow-x-auto hidden md:block">
            <table className="w-full text-sm" data-testid="orders-table">
              <thead className="bg-muted/50 text-left text-xs text-[#7A6A5E]">
                <tr>
                  <th className="px-3 py-3 font-medium">
                    <input type="checkbox" data-testid="select-all" checked={allSelected} onChange={toggleSelectAll}
                      disabled={selectableIds.length === 0} className="accent-[#7A241F] h-4 w-4 align-middle" />
                  </th>
                  <th className="px-4 py-3 font-medium">Pemesan</th>
                  <th className="px-4 py-3 font-medium">Waktu Pesan</th>
                  <th className="px-4 py-3 font-medium">Sesi / Kursi</th>
                  <th className="px-4 py-3 font-medium">Total</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-center">Verifikasi</th>
                  <th className="px-4 py-3 font-medium text-center">Kirim Pesan</th>
                  {canDelete && <th className="px-4 py-3 font-medium text-center">Hapus</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {paged.map((o) => {
                  const dstat = orderProgress(o);
                  return (
                    <tr key={o.id} data-testid={`order-row-${o.id.slice(0, 8)}`} className="hover:bg-muted/30">
                      <td className="px-3 py-3">
                        {o.status === "waiting_verification" && (
                          <input type="checkbox" data-testid={`select-${o.id.slice(0, 8)}`}
                            checked={selectedIds.includes(o.id)} onChange={() => toggleSelect(o.id)}
                            className="accent-[#7A241F] h-4 w-4 align-middle" />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-[#2C1E16]">{o.name} <span className="font-mono text-[10px] text-[#7A6A5E]">#{o.order_no}</span></p>
                        <p className="text-xs text-[#7A6A5E]">{o.phone}</p>
                      </td>
                      <td className="px-4 py-3" data-testid={`order-time-${o.id.slice(0, 8)}`}>
                        <p className="text-xs text-[#7A6A5E] whitespace-nowrap">{fmtTime(o.created_at)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p>{o.session?.name}</p>
                        <p className="text-xs text-[#7A6A5E]">{o.seats.join(", ")}</p>
                        <button onClick={() => openSeatEdit(o)} data-testid={`edit-seats-desktop-${o.id.slice(0, 8)}`}
                          className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-[#8A3A12] bg-[#B26A1E]/15 hover:bg-[#B26A1E]/25 border border-[#B26A1E]/40 rounded-full px-2.5 py-1 transition-colors">
                          <Armchair className="h-3.5 w-3.5" /> Ubah Kursi
                        </button>
                        <span className={cn("block mt-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium w-fit", channelInfo(o).c)}
                          data-testid={`channel-${o.id.slice(0, 8)}`}>{channelInfo(o).t}</span>
                        {o.walkin && o.sold_by && (
                          <p className="text-[10px] text-[#8A3A12] mt-0.5" data-testid={`soldby-${o.id.slice(0, 8)}`}>
                            Dijual oleh <b>{o.sold_by}</b>{o.payment_method === "cash" ? " (Cash)" : ""}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 font-semibold text-[#7A241F]">{rupiah(o.total_amount)}</td>
                      <td className="px-4 py-3">
                        <span data-testid={`status-${o.id.slice(0, 8)}`} className={cn("text-[11px] px-2 py-0.5 rounded-full font-medium inline-block", dstat.c)}>{dstat.t}</span>
                      </td>
                      {/* Verifikasi */}
                      <td className="px-4 py-3 text-center">
                        {o.status === "waiting_verification" ? (
                          <Button size="sm" onClick={() => openProof(o)} data-testid={`verify-open-${o.id.slice(0, 8)}`}
                            className="h-8 bg-[#B26A1E] hover:bg-[#8A3A12] text-xs">
                            <Eye className="h-3.5 w-3.5 mr-1" /> Cek Bukti
                          </Button>
                        ) : o.status === "verified" ? (
                          <button onClick={() => o.has_proof && openProof(o)} data-testid={`verify-done-${o.id.slice(0, 8)}`}
                            className="inline-flex flex-col items-center text-[#255E33] text-xs font-medium">
                            <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Payment OK</span>
                            {(o.verified_by || o.sold_by || o.created_by) && <span className="text-[10px] text-[#7A6A5E] font-normal">oleh {o.verified_by || o.sold_by || o.created_by}{o.updated_at ? ` · ${fmtTime(o.updated_at)}` : ""}</span>}
                          </button>
                        ) : o.status === "pending_payment" ? (
                          <Button size="sm" variant="outline" onClick={() => adminUpload(o)} disabled={busyId === o.id}
                            data-testid={`admin-upload-${o.id.slice(0, 8)}`} className="h-8 text-xs border-[#7A241F]/40 text-[#7A241F]">
                            {busyId === o.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <UploadCloud className="h-3.5 w-3.5 mr-1" />} Upload Bukti
                          </Button>
                        ) : o.status === "expired" ? (
                          <Button size="sm" variant="outline" onClick={() => adminUpload(o)} disabled={busyId === o.id}
                            data-testid={`admin-upload-${o.id.slice(0, 8)}`} className="h-8 text-xs border-[#7A241F]/40 text-[#7A241F]">
                            {busyId === o.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <UploadCloud className="h-3.5 w-3.5 mr-1" />} Upload Bukti
                          </Button>
                        ) : (
                          <span className="text-xs text-[#7A6A5E]">—</span>
                        )}
                      </td>
                      {/* Kirim Pesan */}
                      <td className="px-4 py-3 text-center">
                        {o.status === "verified" ? (
                          <div className="flex items-center justify-center gap-1.5">
                            <Button size="sm" onClick={() => sendAndMarkWA(o)} data-testid={`wa-${o.id.slice(0, 8)}`}
                              className={cn("h-8 text-xs", o.wa_sent ? "bg-white text-[#255E33] border border-[#2F703E]/50 hover:bg-[#2F703E]/10" : "bg-[#2F703E] hover:bg-[#255E33]")}>
                              <MessageCircle className="h-3.5 w-3.5 mr-1" /> {o.wa_sent ? "Kirim Ulang" : "Kirim Pesan"}
                            </Button>
                          </div>
                        ) : (o.status === "pending_payment" || o.status === "expired") ? (
                          <Button size="sm" onClick={() => sendReminderWA(o)} data-testid={`remind-${o.id.slice(0, 8)}`}
                            className="h-8 text-xs bg-[#B26A1E] hover:bg-[#8A3A12]">
                            <MessageCircle className="h-3.5 w-3.5 mr-1" /> Ingatkan Upload
                          </Button>
                        ) : (
                          <span className="text-xs text-[#7A6A5E]">—</span>
                        )}
                      </td>
                      {/* Hapus */}
                      {canDelete && (
                        <td className="px-4 py-3 text-center">
                          {canDeleteRow(o) ? (
                            <button onClick={() => setDeleteTarget(o)} data-testid={`delete-open-${o.id.slice(0, 8)}`}
                              title="Hapus pesanan"
                              className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          ) : o.status === "verified" ? (
                            <span className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-[#7A6A5E]" title="Terverifikasi — hanya Super Admin yang dapat menghapus">
                              <Lock className="h-4 w-4" />
                            </span>
                          ) : (
                            <span className="text-xs text-[#7A6A5E]">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-border bg-muted/20" data-testid="orders-pagination">
              <span className="text-xs text-[#7A6A5E]">Halaman <b>{pageSafe}</b> / {totalPages} · {sortedFiltered.length} pesanan</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pageSafe <= 1}
                  data-testid="orders-prev-page"
                  className="h-8 px-3 rounded-lg border border-border text-sm font-medium text-[#7A241F] disabled:opacity-40 hover:bg-white">Sebelumnya</button>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={pageSafe >= totalPages}
                  data-testid="orders-next-page"
                  className="h-8 px-3 rounded-lg border border-border text-sm font-medium text-[#7A241F] disabled:opacity-40 hover:bg-white">Berikutnya</button>
              </div>
            </div>
          )}
          </>
        )}
      </div>
      </>)}

      {tab === "logs" && <LogsPanel />}

      {tab === "bendahara" && <BendaharaPanel isSuper={isSuper} onGoSetoran={() => setTab("setoran")} />}

      {tab === "vip" && <VIPPanel />}
      {tab === "manual" && <ManualPanel />}

      {tab === "sessions" && event && (
        <div className="rounded-2xl border border-border bg-white p-5 no-print" data-testid="session-control-card">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div>
              <h2 className="font-serif-display text-2xl text-[#2F703E] flex items-center gap-2"><Users className="h-5 w-5" /> Buka/Tutup Penjualan per Sesi</h2>
              <p className="text-xs text-[#7A6A5E]">Umum = pembeli online di web · Panitia = jual di lokasi (/walkin). {isSuper ? "" : "Hanya Super Admin yang dapat mengubah."}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {event.sessions.map((s) => {
              const isOpen = s.status === "open" || s.status === "full";
              const walkinOpen = !!s.walkin_open;
              return (
                <div key={s.id} data-testid={`admin-session-${s.id}`}
                  className={cn("rounded-xl border p-3",
                    (isOpen || walkinOpen) ? "border-[#2F703E]/40 bg-[#2F703E]/[0.05]" : "border-border bg-muted/20")}>
                  <p className="text-sm font-semibold text-[#2C1E16] leading-tight">{s.name}</p>
                  <p className="text-[11px] text-[#7A6A5E] mb-2">{s.time.replace(" WIB", "")} · {s.booked}/{s.capacity} terisi</p>
                  <div className="flex items-center justify-between gap-1 rounded-lg bg-white border border-border px-2 py-1.5">
                    <span className={cn("text-[11px] font-semibold", isOpen ? "text-[#255E33]" : "text-[#9CA3AF]")}>Umum · {isOpen ? "BUKA" : "TUTUP"}</span>
                    <Switch checked={isOpen} disabled={!isSuper} onCheckedChange={(v) => toggleSessionOpen(s.id, v, "public")}
                      data-testid={`session-open-switch-${s.id}`} />
                  </div>
                  <div className="flex items-center justify-between gap-1 mt-1.5 rounded-lg bg-white border border-border px-2 py-1.5">
                    <span className={cn("text-[11px] font-semibold", walkinOpen ? "text-[#8A3A12]" : "text-[#9CA3AF]")}>Panitia · {walkinOpen ? "BUKA" : "TUTUP"}</span>
                    <Switch checked={walkinOpen} disabled={!isSuper} onCheckedChange={(v) => toggleSessionOpen(s.id, v, "walkin")}
                      data-testid={`session-walkin-switch-${s.id}`} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "setoran" && isSuper && <SetoranPanel />}
      {tab === "masterlist" && <MasterlistPanel />}

      {tab === "users" && isStaff && <UsersPanel currentUser={currentUser} />}

      {tab === "trash" && isSuper && (
        <TrashPanel orders={deletedOrders} loading={trashLoading} onRefresh={loadTrash}
          onRestore={restoreOrder} restoreBusyId={restoreBusyId} />
      )}

      {tab === "database" && isSuper && <DatabasePanel onImported={() => window.location.reload()} />}

      {/* Proof viewer */}
      <Dialog open={!!proofView} onOpenChange={() => setProofView(null)}>
        <DialogContent data-testid="proof-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#7A241F]">
              {dialogVerified ? "Terverifikasi ✅" : "Verifikasi Pembayaran"}
            </DialogTitle>
          </DialogHeader>
          {proofView && (
            <div>
              <div className="text-sm mb-3 space-y-0.5">
                <p><b>{proofView.name}</b> <span className="font-mono text-xs text-[#7A6A5E]">#{proofView.order_no}</span></p>
                <p className="text-[#7A6A5E]">
                  {proofView.phone} · {proofView.session?.name} · Kursi {proofView.seats.join(", ")}
                  {isSuper && (
                    <button data-testid="btn-edit-phone" className="ml-2 text-[11px] text-[#B26A1E] underline"
                      onClick={async () => {
                        const np = window.prompt("Nomor HP baru (wajib diawali 08):", proofView.phone || "");
                        if (np === null) return;
                        try {
                          const { data } = await adminApi.put(`/admin/orders/${proofView.id}/phone`, { phone: np });
                          setProofView({ ...proofView, phone: data.phone });
                          setOrders((prev) => prev.map((x) => x.id === proofView.id ? { ...x, phone: data.phone } : x));
                          toast.success("No HP diperbarui");
                        } catch (e) { toast.error(e?.response?.data?.detail || "Gagal ubah No HP"); }
                      }}>Edit No HP</button>
                  )}
                </p>
                <p>Nominal: <b className="text-[#B26A1E]">{rupiah(proofView.total_amount)}</b> (kode unik {proofView.unique_code})</p>
              </div>

              {!dialogVerified && (
                proofLoading ? (
                  <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#B26A1E]" /></div>
                ) : proofImage ? (
                  <img src={proofImage} alt="Bukti" className="w-full rounded-lg border border-border max-h-[45vh] object-contain bg-muted/30" />
                ) : (
                  <p className="text-sm text-[#7A6A5E] py-4 text-center">Bukti tidak tersedia.</p>
                )
              )}

              {dialogVerified ? (
                <div className="mt-4">
                  <div className="rounded-lg bg-[#2F703E]/10 p-4 text-center mb-3">
                    <CheckCircle2 className="h-8 w-8 text-[#2F703E] mx-auto mb-1" />
                    <p className="text-sm text-[#255E33] font-medium">Pembayaran diverifikasi. Kirim tiket ke pembeli via WhatsApp.</p>
                  </div>
                  <Button onClick={() => { sendAndMarkWA(proofView); }} data-testid="dialog-send-wa"
                    className="w-full bg-[#2F703E] hover:bg-[#255E33]">
                    <MessageCircle className="h-4 w-4 mr-1.5" /> Kirim Tiket via WhatsApp
                  </Button>
                  <Button variant="ghost" onClick={() => setProofView(null)} className="w-full mt-1 text-[#7A6A5E]" data-testid="dialog-close">
                    Selesai
                  </Button>
                </div>
              ) : proofView.status === "waiting_verification" ? (
                <div className="mt-4">
                  <div className="rounded-lg bg-[#F3E9DD]/60 border border-[#B26A1E]/30 p-3 mb-3">
                    <label className="text-xs font-semibold text-[#7A241F] block mb-1">Nominal yang benar-benar masuk (sesuai slip TT)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#7A6A5E]">Rp</span>
                      <Input data-testid="verify-amount-input" inputMode="numeric"
                        value={verifyAmount ? Number(verifyAmount).toLocaleString("id-ID") : ""}
                        onChange={(e) => setVerifyAmount(e.target.value.replace(/[^0-9]/g, ""))}
                        className="pl-9 h-11 text-base font-semibold bg-white" />
                    </div>
                    <p className="text-[11px] text-[#7A6A5E] mt-1">
                      Diminta sistem: {rupiah(proofView.total_amount)} (kode unik {proofView.unique_code}).
                      Ubah kalau jumlah transfer beda — yang tercatat di rekap = nominal ini.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={verifyInDialog} disabled={busyId === proofView.id} data-testid="dialog-verify"
                      className="flex-1 bg-[#2F703E] hover:bg-[#255E33]">
                      {busyId === proofView.id ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Payment Masuk
                    </Button>
                    <Button variant="outline" onClick={() => { act(proofView.id, "reject"); setProofView(null); }}
                      data-testid="dialog-reject" className="flex-1 text-[#EF4444] border-[#EF4444]/40">
                      <XCircle className="h-4 w-4 mr-1.5" /> Tolak
                    </Button>
                  </div>
                </div>
              ) : proofView.status === "verified" ? (
                <div className="mt-4 space-y-2">
                  {(!proofView.amount_edited_once || isSuper) ? (
                    <div className="rounded-lg bg-[#F3E9DD]/60 border border-[#B26A1E]/30 p-3">
                      <label className="text-xs font-semibold text-[#7A241F] block mb-1">
                        Edit nominal (kalau tadi lupa/salah input)
                        {proofView.amount_edited_once && isSuper && <span className="ml-1 text-[#B26A1E]">· sudah pernah diedit</span>}
                      </label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#7A6A5E]">Rp</span>
                          <Input data-testid="edit-amount-input" inputMode="numeric"
                            value={verifyAmount ? Number(verifyAmount).toLocaleString("id-ID") : ""}
                            onChange={(e) => setVerifyAmount(e.target.value.replace(/[^0-9]/g, ""))}
                            className="pl-9 h-10 bg-white" />
                        </div>
                        <Button onClick={saveAmount} disabled={busyId === proofView.id} data-testid="edit-amount-save"
                          className="bg-[#B26A1E] hover:bg-[#9A5716]">Simpan</Button>
                      </div>
                      <p className="text-[11px] text-[#7A6A5E] mt-1">
                        Rekap Bendahara akan ikut nominal ini.
                        {!isSuper && " Nominal hanya bisa diedit sekali."}
                      </p>
                    </div>
                  ) : (
                    <div className="rounded-lg bg-[#F3E9DD]/40 border border-dashed border-[#B26A1E]/30 p-3" data-testid="edit-amount-locked">
                      <p className="text-xs text-[#7A6A5E]">
                        Nominal <b className="text-[#7A241F]">{rupiah(proofView.total_amount)}</b> sudah pernah diedit sekali.
                        Untuk mengubah lagi, minta bantuan <b>Super Admin</b>.
                      </p>
                    </div>
                  )}
                  <Button onClick={() => sendAndMarkWA(proofView)} data-testid="dialog-send-wa-2"
                    className="w-full bg-[#2F703E] hover:bg-[#255E33]">
                    <MessageCircle className="h-4 w-4 mr-1.5" /> Kirim Tiket via WhatsApp
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Check-in reminder popup */}
      <Dialog open={!!checkinPopup} onOpenChange={() => setCheckinPopup(null)}>
        <DialogContent data-testid="checkin-popup">
          <DialogHeader>
            <div className="h-11 w-11 rounded-full bg-[#2F703E]/15 flex items-center justify-center mb-2">
              <Ticket className="h-5 w-5 text-[#2F703E]" />
            </div>
            <DialogTitle className="font-serif-display text-2xl text-[#7A241F]">Peserta Sudah Datang</DialogTitle>
          </DialogHeader>
          {checkinPopup && (
            <div className="text-sm space-y-3">
              <p><b>{checkinPopup.name}</b> ({checkinPopup.phone}) — {checkinPopup.session?.name} · {checkinPopup.session?.time}</p>
              <p className="text-xs text-[#2F703E] font-medium">✓ Check-in tercatat: {fmtTime(new Date().toISOString())}</p>
              <div className="rounded-lg bg-[#B26A1E]/10 p-4">
                <p className="text-[#8A3A12] font-medium mb-2">Pastikan sudah serahkan tiket:</p>
                <p className="font-serif-display text-2xl text-[#7A241F] mb-2" data-testid="checkin-popup-session">{checkinPopup.session?.name?.toUpperCase()} · {checkinPopup.session?.time}</p>
                <p className="text-xs text-[#7A6A5E] mb-1">Nomor kursi <b className="text-[#8A3A12]">({checkinPopup.seats.length} tiket)</b>:</p>
                <div className="flex flex-wrap gap-2">
                  {checkinPopup.seats.map((s) => (
                    <span key={s} className="px-3 py-1.5 rounded-md bg-white text-[#8A3A12] font-bold text-base border border-[#B26A1E]/30">{s}</span>
                  ))}
                </div>
              </div>
              <p className="text-[#7A6A5E] text-xs">Jangan lupa memberikan tiket kursi di atas kepada peserta ini agar tidak terjadi double.</p>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button onClick={() => setCheckinPopup(null)} className="bg-[#7A241F] hover:bg-[#5E1B17] w-full" data-testid="checkin-popup-ok">
              Sudah Saya Berikan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ubah Kursi pesanan website */}
      <Dialog open={!!seatEdit} onOpenChange={() => !seatBusy && setSeatEdit(null)}>
        <DialogContent data-testid="seat-edit-dialog" className="max-w-3xl rounded-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#7A241F] flex items-center gap-2">
              <Armchair className="h-5 w-5 text-[#B26A1E]" /> Ubah Kursi
            </DialogTitle>
            {seatEdit && (
              <p className="text-sm text-[#7A6A5E]">
                <b className="text-[#2C1E16]">{seatEdit.name}</b> · #{seatEdit.order_no} · {seatEdit.session?.name} · {seatEdit.session?.time}
              </p>
            )}
          </DialogHeader>
          {seatLoading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#B26A1E]" /></div>
          ) : (
            <>
              <div className="rounded-xl border border-[#B26A1E]/30 bg-[#F3E9DD]/40 p-3 mb-3 text-sm">
                <p className="text-[#8A3A12] font-medium">Kursi dipilih ({seatSelected.length}):</p>
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="seat-edit-selected">
                  {seatSelected.length === 0 ? <span className="text-xs text-[#9CA3AF]">Belum ada kursi dipilih</span>
                    : seatSelected.map((s) => <span key={s} className="px-2.5 py-1 rounded-md bg-[#B26A1E]/15 text-[#8A3A12] text-sm font-bold">{s}</span>)}
                </div>
                <p className="text-[11px] text-[#7A6A5E] mt-2">Kursi lama otomatis dilepas & kursi baru dikunci saat disimpan. Klik kursi untuk pilih/batal.</p>
              </div>
              {seatRows.length > 0 && (
                <SeatMap rows={seatRows} selected={seatSelected} onToggle={toggleSeatEdit} couples={seatCouples} allowDisability={true}
                  aisleRow={seatMeta.aisle_row} doorRow={seatMeta.door_row} reverse={seatMeta.reverse} />
              )}
            </>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setSeatEdit(null)} disabled={seatBusy} className="flex-1" data-testid="seat-edit-cancel">Batal</Button>
            <Button onClick={saveSeatEdit} disabled={seatBusy || seatLoading || seatSelected.length === 0} data-testid="seat-edit-save"
              className="flex-1 bg-[#2F703E] hover:bg-[#255E33]">
              {seatBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Simpan Kursi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={() => !deleteBusy && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-dialog" className="max-w-sm rounded-2xl">
          <DialogHeader>
            <div className="h-11 w-11 rounded-full bg-[#EF4444]/15 flex items-center justify-center mb-2">
              <AlertTriangle className="h-5 w-5 text-[#EF4444]" />
            </div>
            <DialogTitle className="font-serif-display text-2xl text-[#7A241F]">Anda yakin hapus?</DialogTitle>
          </DialogHeader>
          {deleteTarget && (
            <div className="text-sm space-y-3">
              <p className="text-[#7A6A5E]">
                Pesanan berikut akan dipindah ke <b className="text-[#EF4444]">Kotak Sampah</b> dan kursinya dilepas.
                Super Admin masih bisa <b>memulihkannya</b> bila salah hapus.
              </p>
              <div className="rounded-lg border border-border bg-muted/30 p-3">
                <p className="font-semibold text-[#2C1E16]">{deleteTarget.name} <span className="font-mono text-xs text-[#7A6A5E]">#{deleteTarget.order_no}</span></p>
                <p className="text-xs text-[#7A6A5E]">{deleteTarget.phone} · {deleteTarget.session?.name} · {rupiah(deleteTarget.total_amount)}</p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleteBusy}
              data-testid="delete-cancel" className="flex-1">
              Batal
            </Button>
            <Button onClick={doDelete} disabled={deleteBusy} data-testid="delete-confirm"
              className="flex-1 bg-[#EF4444] hover:bg-[#DC2626]">
              {deleteBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Trash2 className="h-4 w-4 mr-1.5" />} Ya, Hapus
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Printable ticket */}
      {printOrder && (
        <div id="print-area" className="hidden print:block p-8">
          <div className="border-2 border-[#7A241F] rounded-xl p-6 max-w-md">
            <div className="flex items-center gap-3 border-b border-dashed border-gray-300 pb-3 mb-3">
              <img src={LOGOS.kbi} alt="KBI" className="h-10" />
              <img src={LOGOS.mbi} alt="MBI" className="h-10" />
            </div>
            <p className="text-xs text-gray-500">TIKET NONTON BERSAMA · Minggu, 13 September 2026 · CGV Grand Batam</p>
            <p className="font-serif-display text-xl text-[#7A241F] leading-tight mt-1">
              Y.A. MNS. Ashin Jinarakkhita: Jejak Langkah Sang Pelopor di Nusantara
            </p>
            <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
              <div><p className="text-gray-500 text-xs">Nama</p><p className="font-semibold">{printOrder.name}</p></div>
              <div><p className="text-gray-500 text-xs">No. HP</p><p className="font-semibold">{printOrder.phone}</p></div>
              <div><p className="text-gray-500 text-xs">Sesi</p><p className="font-semibold">{printOrder.session?.name} · {printOrder.session?.time}</p></div>
              <div><p className="text-gray-500 text-xs">Kursi</p><p className="font-semibold">{printOrder.seats.join(", ")}</p></div>
            </div>
            <div className="mt-4 pt-3 border-t border-dashed border-gray-300 flex justify-between items-center">
              <div><p className="text-gray-500 text-xs">No. Order</p><p className="font-mono font-bold">#{printOrder.order_no}</p></div>
              <div className="text-right"><p className="text-gray-500 text-xs">Total</p><p className="font-bold">{rupiah(printOrder.total_amount)}</p></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
